// SeaState console — no framework, direct DOM.
//
// Routes: "/" landing (public), "/console" map + forecast blocks, "/vessels"
// client settings, "/login" sign-in. Auth is a session cookie; the console and
// Vessels pages are gated — a logged-out visit is redirected to sign-in — and
// render the client's own vessels, operations and limits once authenticated.

import { createChart, type Series, type Chart } from "./chart/chart.js";
import { attachReadout } from "./chart/readout.js";
import { openChartModal } from "./chart/expand.js";
import { createTimeRuler, type TimeRuler } from "./chart/ruler.js";
import {
  getCursor,
  indexAtTime,
  subscribeCursor,
  setLabelZone,
  timeAtIndex,
  timeOfLabel,
} from "./time/cursor.js";
import {
  fmtClockHM,
  fmtDayAmPm,
  fmtDayDateHM,
  fmtDayHM,
  fmtEventStamp,
  getTimeMode,
  setTimeMode,
  subscribeTimeMode,
  type TimeMode,
} from "./ui/time.js";
import { adoptServerPrefs, enableServerSync } from "./ui/prefs.js";
import {
  convLen,
  convLenSeries,
  fmtDepth,
  fmtLimitLen,
  fmtLen,
  getUnitSystem,
  lenUnit,
  lenWord,
  setUnitSystem,
  subscribeUnits,
  type UnitSystem,
} from "./ui/units.js";
import { createSettingsControl, segmented } from "./ui/settings.js";
import { parseCoords } from "./ui/coords.js";
import {
  POSITION_FORMATS,
  fmtPosition,
  getPositionFormat,
  setPositionFormat,
  subscribePosition,
  type PositionFormat,
} from "./ui/position.js";
import {
  nowLine,
  refLine,
  limitLine,
  noGoSpans,
  dirArrows,
  nightBands,
  normalBand,
  shadeBands,
  cssVar,
  rgba,
} from "./chart/marks.js";
import { createMap, type SeaMap } from "./map/map.js";
import {
  setupBuoys,
  setupTropical,
  refreshTropical,
  createLayerControl,
  fetchJurisdiction,
  type BuoyPick,
} from "./map/overlays.js";
import { bucketObs, meanBias, type BuoyObs } from "./forecast/ndbc.js";
import { fetchObsFor, buoySource } from "./forecast/buoys/registry.js";
import { createWindControl, type WindControl } from "./map/windcontrol.js";
import { createMapDimmer, type MapDimmer } from "./map/dimmer.js";
import { createPointSocks, type PointSocks } from "./map/socks.js";
import { OPS_PANELS } from "./ui/flags.js";
import { createFactPanel, type FactPanel } from "./panel/factpanel.js";
import { seepFacts, seepNote } from "./panel/seep.js";
import { ebsFacts, EBS_NOTE } from "./panel/ebs.js";
import type { FieldLayer } from "./map/windfield.js";
import {
  soundSpeed,
  SOUND_SPEED_EQS,
  DEFAULT_EQ,
  isSoundSpeedEq,
  type SoundSpeedEq,
} from "./forecast/soundspeed.js";
// The profile figure moved out of this file when the printable report needed to
// draw it too — see the note at the top of svp/profile.ts.
import {
  svpDepthTicks,
  svpFiniteRow,
  svpFullDepthM,
  SVP_ZOOM_PRESETS,
  svpProfilesSvg,
  svpLegendKey,
  eqLabel,
} from "./svp/profile.js";
import {
  fetchArgo,
  argoAgeDays,
  argoStillRepresents,
  type ArgoProfile,
} from "./forecast/argo.js";
import { francoisGarrison, ABSORPTION_FREQS_KHZ, DEFAULT_PH } from "./ops/absorption.js";
import {
  setMapClickInterceptor,
  setVesselRange,
  clearVesselRange,
  setReachVeil,
  clearReachVeil,
  setDrifterData,
  tropicalAt,
  tropicalSummary,
  refreshOverlayColors,
  type TropicalContext,
} from "./map/overlays.js";
import {
  fetchDrifters,
  driftersStillRepresent,
  driftersNewestMs,
  nearestDrifter,
  bucketDrifterSpeed,
  type Drifter,
  type HeldDrifters,
} from "./forecast/drifters.js";
import { fetchPrimer, buildPrimerHtml, exceedancePct, type ClimFact } from "./forecast/primer.js";
import {
  fetchPointForecast,
  type PointForecast,
  type ModelSeries,
} from "./forecast/openmeteo.js";
import { solarSeries, sunLine, sunElevation, type Solar } from "./forecast/solar.js";
import {
  fallbackRegistry,
  effectiveLimit,
  type EffectiveLimit,
  type Registry,
  type Vessel,
  type Operation,
} from "./ops/registry.js";
import { workability, WORK_GRADES, workGrade } from "./ops/workability.js";
import { reportSystem, subscribeSystems, systemsSnapshot, systemsBadge } from "./ops/health.js";
import { buildBriefing } from "./ops/briefing.js";
import { getWindowHours, setWindowHours, subscribeWindow } from "./time/window.js";
import {
  me,
  logout,
  getRegistry,
  fetchSvp,
  type ApiRegistry,
  type User,
  type SvpResult,
  type SvpCell,
  type SvpClimatology,
} from "./data/api.js";
import { mountLogin } from "./pages/login.js";
import { mountSetPassword } from "./pages/setpassword.js";
import { mountThermalConsole } from "./consoles/thermal/console.js";
import { mountReport, unmountReport } from "./pages/report.js";
import { mountVessels } from "./pages/vessels.js";
import { mountLanding, mountAbout, mountCaseStudy } from "./pages/site.js";
import { mountMethods } from "./pages/methods.js";
import { mountGuide } from "./pages/guide.js";
import { mountUpdates } from "./pages/updates.js";
import { mountAdmin } from "./pages/admin/shell.js";
import { initHelp, infoButton } from "./help/popover.js";
import type { HelpKey } from "./help/types.js";
import { markEl } from "./ui/mark.js";
import { createRail } from "./ui/rail.js";
import { createMobileTabs, type MobileTabs } from "./ui/mobiletabs.js";
import { tropCardEl } from "./ui/stormcards.js";
import { stormChip, stormFact } from "./data/stormlist.js";
import { getFeedback, sendFeedback, deleteFeedback, type ApiFeedback } from "./data/api.js";
import {
  listSaved,
  removeWaypoint,
  renameWaypoint,
  saveWaypoint,
  touchWaypoint,
  type SavedWaypoint,
} from "./ops/savedwaypoints.js";
import { createConfBar } from "./chart/confbar.js";
import { createWorkRibbon, type WorkRibbonData } from "./chart/ribbon.js";
import { createConditionsTable, type ConditionsTable } from "./panel/table.js";
import { WAYPOINTS, type Waypoint, type FramedWaypoint } from "./ops/waypoints.js";
import { createSearchBox, type SearchBox } from "./map/searchbox.js";
import { createGrip, type Grip } from "./ui/grip.js";
import {
  fetchFieldMesh,
  boundsCover,
  meshCellDeg,
  strideForBounds,
  meshSeriesAt,
  FIELD_CELL_DEG,
  FIELD_DEFAULT_MAX_CELLS,
  type FieldBounds,
  type FieldKind,
  type FieldMesh,
} from "./forecast/fieldmesh.js";
import { createTransport } from "./chart/transport.js";
import {
  RUNGS,
  DEFAULT_RUNG,
  boundsFor,
  clampToBounds,
  rung,
  rungForSpan,
  type RungId,
} from "./ops/view.js";
import {
  drawnCellDeg,
  fitTheater,
  refreshTheaterColors,
  setTheaterFrame,
  setTheaterGrid,
} from "./map/theaterlayer.js";

const DEFAULT_POINT: [number, number] = [27.5, -94.0]; // [lat, lon]

/** Narrow prefs.home out of the untyped prefs blob: {lat, lon} plus an optional
 *  NDBC buoy id to open selected. Anything malformed is simply no home — a bad
 *  pref must degrade to the default view, never to NaN coordinates. */
function homeFromPrefs(prefs: unknown): { lat: number; lon: number; buoy?: string } | null {
  if (typeof prefs !== "object" || prefs === null) return null;
  const home = (prefs as Record<string, unknown>).home;
  if (typeof home !== "object" || home === null) return null;
  const h = home as Record<string, unknown>;
  const lat = typeof h.lat === "number" && Number.isFinite(h.lat) ? h.lat : null;
  const lon = typeof h.lon === "number" && Number.isFinite(h.lon) ? h.lon : null;
  if (lat === null || lon === null) return null;
  const buoy = typeof h.buoy === "string" && h.buoy !== "" ? h.buoy : undefined;
  return { lat, lon, buoy };
}
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** The console's one way of writing a position — decimal degrees unless the
 *  operator has chosen otherwise (ui/position.ts). Kept as a local name because
 *  it is called from ~40 places in this file. */
function fmtCoord(lat: number, lon: number): string {
  return fmtPosition(lat, lon);
}

/** One-clause Systems note for the drifter feed: an empty set is a fact about the
 *  ocean, not a fault; a populated one reports the count and how close the nearest
 *  track came (whether any is near enough to land dots on the current chart). */
function driftersNote(drifters: Drifter[]): string {
  if (drifters.length === 0) return "no drifters in range";
  const nearest = drifters.reduce((m, d) => Math.min(m, d.minDistanceNm), Infinity);
  const plural = drifters.length === 1 ? "" : "s";
  return `${drifters.length} drifter${plural} · nearest ${Math.round(nearest)} nm`;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}


// --- SVP deliverable: Les Bender's CSV export ---------------------------------
//
// Reproduces the data file in SVProfile_Output.m (checked into SVP_bender/), so
// SeaState *generates his deliverable* instead of him running the MATLAB per site.
// Honestly adapted to what the console knows: we have no site NAMES (the map gives a
// clicked lat/lon, not a MasterList_SVP row), so Site is the coordinate; and his
// AbsorptionCoefficients.m was missing from the drop, so the 2/4/8 kHz columns come
// from Francois & Garrison (ops/absorption.ts), the formula Cody approved (2026-07-21).
//
// Up to three profiles the format keeps apart: the MODELLED forecast (the SVP grid cell
// under the surveyor's chosen equation), the OBSERVED cast when a float is displayed, and
// the WOA23 CLIMATOLOGY when its block is present. The console's standing rule — observation
// and reference beside model, NEVER blended — carries into the file: each is written as a
// SEPARATE table block with its own metadata header, never merged into shared columns (their
// depth levels don't even line up; the climatology alone reaches 1500 m). One file, one click
// — not three simultaneous downloads (which a browser may block), and not one mixed table.


/** Metadata header lines + the column block for one profile. His `key: value` header
 *  lines use CRLF (Excel- and survey-software-safe); data rows follow the same. */
function svpCsvBlock(
  headerLines: string[],
  levels: { depth: number; t: number; s: number }[],
  eq: SoundSpeedEq,
): string {
  const NL = "\r\n";
  const out: string[] = headerLines.slice();
  // His exact sub-bottom bands (SVProfile_Output.m: f = 2, 4, 8 kHz).
  out.push(
    "Depth (m),Sound Velocity (m/s),2 kHz attenuation (dB/km),4 kHz attenuation (dB/km),8 kHz attenuation (dB/km)",
  );
  for (const lv of levels) {
    const sv = soundSpeed(eq, lv.t, lv.s, lv.depth);
    // Absorption computed from the SAME T/S/depth/SV this row prints, so the deliverable
    // is internally consistent (absorption.ts explains why the printed SV is fed as c
    // rather than F&G's own internal sound-speed formula).
    const a = ABSORPTION_FREQS_KHZ.map((f) =>
      francoisGarrison(lv.t, lv.s, lv.depth, DEFAULT_PH, f, sv).toFixed(4),
    );
    // Precision: depth 1 dp, SV 2 dp, attenuation 4 dp — read by humans and survey software.
    out.push(`${lv.depth.toFixed(1)},${sv.toFixed(2)},${a[0]},${a[1]},${a[2]}`);
  }
  return out.join(NL);
}

/** Sanitise a token for a filename — keep it recognisable, drop anything a filesystem
 *  or shell would choke on. */
const fnToken = (s: string): string =>
  s.replace(/[^A-Za-z0-9.+-]+/g, "-").replace(/^-+|-+$/g, "");

const coordToken = (lat: number, lon: number): string =>
  `${Math.abs(lat).toFixed(3)}${lat >= 0 ? "N" : "S"}_${Math.abs(lon).toFixed(3)}${lon >= 0 ? "E" : "W"}`;

/** Human-readable cell coordinate for a provenance line (e.g. "27.00°N, 91.50°W"). */
const fmtCellCoord = (lat: number, lon: number): string =>
  `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"}, ${Math.abs(lon).toFixed(2)}°${lon >= 0 ? "E" : "W"}`;

/** Build the deliverable's filename + full text from the CURRENTLY DISPLAYED profile.
 *  Mirrors his SVP_<site>_<model>_ naming, keyed on the coordinate since we have no site
 *  names. Returns null when the cell has too few usable levels to be a profile. */
function buildSvpCsv(
  result: SvpResult,
  eq: SoundSpeedEq,
  site: { lat: number; lon: number },
  obs: ArgoProfile | null,
  clim: SvpClimatology | null,
  siteDepthM: number | null,
): { filename: string; text: string } | null {
  const cell = result.cell;
  const rows = cell.p.filter(
    (r) => r.length >= 3 && Number.isFinite(r[0]) && Number.isFinite(r[1]) && Number.isFinite(r[2]),
  );
  if (rows.length < 2) return null;
  const modelDepth = rows[rows.length - 1][0];

  // Les read the first block's "Model depth: 763" against rows reaching 1100 m in a
  // LATER block and against the 828 m he knows the site to be, and called the file
  // inconsistent (2026-07-21). All three numbers were right; the file never said how
  // they relate. So the header now (a) carries the charted water depth — the one
  // number the surveyor trusts, (b) says outright that Model depth is the model
  // cell's deepest wet level, not the seabed, and (c) announces every block the file
  // contains, so three stacked tables cannot be read as one contradictory one.
  //
  // The block filters run BEFORE the manifest line so it announces the blocks the
  // file actually writes — a promised-but-absent block would be the same
  // file-contradicts-itself failure this header exists to end. And "Model depth"
  // stays a bare number (his toolchain may read it as one; SVProfile_Output.m
  // parity); the explanation is its own Note line.
  const obsRows =
    obs !== null
      ? obs.levels.filter(
          (l) => Number.isFinite(l.depthM) && Number.isFinite(l.tempC) && Number.isFinite(l.salPSU),
        )
      : [];
  const climRows =
    clim !== null
      ? clim.levels.filter(
          (l) => l.length >= 3 && Number.isFinite(l[0]) && Number.isFinite(l[1]) && Number.isFinite(l[2]),
        )
      : [];
  const blocks: string[] = [`MODELLED (${result.source})`];
  if (obs !== null && obsRows.length >= 2) blocks.push(`OBSERVED (ARGO float ${obs.platform})`);
  if (clim !== null && climRows.length >= 2) blocks.push("CLIMATOLOGY (WOA23)");
  const header = [
    "Project: SeaState export",
    `Profiles in this file: ${blocks.join("; ")} — each is a separate block with its own header and depth levels`,
    `Site: ${site.lat.toFixed(4)}, ${site.lon.toFixed(4)}`,
    `Site date: ${result.date}`,
    `Site latitude: ${site.lat.toFixed(6)}`,
    `Site longitude: ${site.lon.toFixed(6)}`,
    `Water depth at site (NCEI bathymetry): ${siteDepthM !== null && Number.isFinite(siteDepthM) && siteDepthM > 0 ? siteDepthM.toFixed(1) : "unavailable"}`,
    `Model: ${result.source} · ${eqLabel(eq)}`,
    `Model latitude: ${cell.lat.toFixed(6)}`,
    `Model longitude: ${cell.lon.toFixed(6)}`,
    `Model depth: ${modelDepth.toFixed(1)}`,
    "Note: Model depth is the deepest model level of the grid cell, not the seabed",
  ];
  const modelledLevels = rows.map((r) => ({ depth: r[0], t: r[1], s: r[2] }));
  let text = svpCsvBlock(header, modelledLevels, eq);

  // Observed cast, when one is displayed: its own block below the modelled one, its own
  // provenance (float id, cast date, distance) in the header — never a shared column.
  if (obs !== null && obsRows.length >= 2) {
    const castDate = new Date(obs.timeUtcMs).toISOString().slice(0, 10);
    const obsHeader = [
      "",
      `Observed profile (ARGO float ${obs.platform}) — beside the model, not blended`,
      `Float: ${obs.platform}`,
      `Cast date: ${castDate}`,
      `Float latitude: ${obs.lat.toFixed(6)}`,
      `Float longitude: ${obs.lon.toFixed(6)}`,
      `Distance from site: ${obs.distanceNm.toFixed(1)} nm`,
      `Model: Observed cast · ${eqLabel(eq)}`,
      `Model depth: ${obs.maxDepthM.toFixed(1)}`,
      "Note: Model depth is the deepest level of the cast",
    ];
    const obsLevels = obsRows.map((l) => ({ depth: l.depthM, t: l.tempC, s: l.salPSU }));
    text += "\r\n\r\n" + svpCsvBlock(obsHeader, obsLevels, eq);
  }

  // Climatology block, when one is displayed: its own header (WOA23 source, month, the
  // climatology grid cell — a coarser grid than the model, so its own coordinate) and
  // its full depth range (to ~1500 m, unclamped by the display window). Reference beside
  // model, never a shared column. SV + attenuation under the SAME equation and pH.
  if (clim !== null && climRows.length >= 2) {
    const climHeader = [
      "",
      "Climatology profile (WOA23 monthly average) — reference beside the model, not blended",
      `Source: ${clim.source}`,
      `Month: ${clim.month}${clim.monthName !== "" ? ` (${clim.monthName})` : ""}`,
      `Cell latitude: ${clim.cellLat.toFixed(6)}`,
      `Cell longitude: ${clim.cellLon.toFixed(6)}`,
      `Model: WOA23 ${clim.monthName !== "" ? `${clim.monthName} ` : ""}average · ${eqLabel(eq)}`,
      `Model depth: ${climRows[climRows.length - 1][0].toFixed(1)}`,
      "Note: Model depth is the deepest climatology level, not the seabed",
    ];
    const climLevels = climRows.map((l) => ({ depth: l[0], t: l[1], s: l[2] }));
    text += "\r\n\r\n" + svpCsvBlock(climHeader, climLevels, eq);
  }

  const fname = `SVP_${coordToken(site.lat, site.lon)}_${fnToken(result.source)}_${result.date}.csv`;
  return { filename: fname, text };
}

// --- SVP graphic export: "Download SVP (.png)" — his words verbatim (2026-07-21) ---
//
// The chart is an inline SVG styled entirely by CSS custom properties, so a naive
// serialisation would rasterise unstyled black-on-transparent: a blob-URL SVG can't
// see the page's stylesheet. Every presentation property is therefore copied from
// getComputedStyle onto a clone as inline style first — the PNG shows exactly what
// the panel shows, in the current bridge theme. Provenance (site, model, equation,
// the observed ARGO float, charted depth) and a curve key are composed around the
// chart as SVG text: this graphic gets pasted into survey reports, and a chart that
// leaves the console without its provenance is an orphan.

const SVP_PNG_PROPS = [
  "fill", "stroke", "stroke-width", "stroke-dasharray", "stroke-linecap",
  "stroke-linejoin", "opacity", "font-family", "font-size", "letter-spacing",
] as const;

/** Clone an SVG subtree with the live computed style of every node baked in as
 *  inline style, so it renders identically outside the page's stylesheet. */
function svpInlineStyles(src: SVGElement): SVGElement {
  const clone = src.cloneNode(true) as SVGElement;
  const from = [src, ...Array.from(src.querySelectorAll<SVGElement>("*"))];
  const to = [clone, ...Array.from(clone.querySelectorAll<SVGElement>("*"))];
  for (let i = 0; i < from.length && i < to.length; i++) {
    const cs = getComputedStyle(from[i]);
    let style = "";
    for (const prop of SVP_PNG_PROPS) {
      const v = cs.getPropertyValue(prop);
      if (v !== "") style += `${prop}:${v};`;
    }
    to[i].setAttribute("style", style);
  }
  return clone;
}

interface SvpPngMeta {
  title: string;
  headLines: string[];
  /** Curve key rows: each samples the LIVE chart element of that class, so the
   *  key's dash pattern and colour are the drawn line's, never a re-statement. */
  keyEntries: { cls: string; label: string }[];
  footLines: string[];
}

/** Compose chart + provenance into one SVG and rasterise to a PNG blob (3× scale).
 *  Returns null when the browser refuses any step; the caller simply does nothing —
 *  a failed export must never wedge the panel. */
async function buildSvpPng(chart: SVGSVGElement, meta: SvpPngMeta): Promise<Blob | null> {
  const vb = chart.viewBox.baseVal;
  const cw = vb.width;
  const ch = vb.height;
  const lineH = 11;
  const padX = 8;
  const headH = 8 + 14 + meta.headLines.length * lineH + 4;
  const keyH = meta.keyEntries.length > 0 ? meta.keyEntries.length * lineH + 4 : 0;
  const footH = meta.footLines.length * lineH + 10;
  const totalH = headH + ch + keyH + footH;

  // Colours and fonts from the live page, so the export matches the current theme.
  const ink = getComputedStyle(document.body).color;
  const firstText = chart.querySelector("text");
  const mono =
    firstText !== null
      ? getComputedStyle(firstText).fontFamily
      : "ui-monospace, Menlo, Consolas, monospace";
  let bg = "#ffffff";
  for (let host: HTMLElement | null = chart.parentElement; host !== null; host = host.parentElement) {
    const c = getComputedStyle(host).backgroundColor;
    if (c !== "" && c !== "transparent" && c !== "rgba(0, 0, 0, 0)") {
      bg = c;
      break;
    }
  }

  const NS = "http://www.w3.org/2000/svg";
  const out = document.createElementNS(NS, "svg");
  out.setAttribute("xmlns", NS);
  out.setAttribute("viewBox", `0 0 ${cw} ${totalH}`);
  out.setAttribute("width", String(cw));
  out.setAttribute("height", String(totalH));
  const bgRect = document.createElementNS(NS, "rect");
  bgRect.setAttribute("width", String(cw));
  bgRect.setAttribute("height", String(totalH));
  bgRect.setAttribute("style", `fill:${bg};`);
  out.appendChild(bgRect);

  const text = (x: number, y: number, s: string, size: number, bold: boolean): void => {
    const t = document.createElementNS(NS, "text");
    t.setAttribute("x", String(x));
    t.setAttribute("y", String(y));
    t.setAttribute(
      "style",
      `fill:${ink};font-family:${mono};font-size:${size}px;${bold ? "font-weight:600;" : ""}`,
    );
    t.textContent = s;
    out.appendChild(t);
  };

  text(padX, 18, meta.title, 10, true);
  let y = 18 + lineH;
  for (const line of meta.headLines) {
    text(padX, y, line, 7.5, false);
    y += lineH;
  }

  const inlined = svpInlineStyles(chart) as SVGSVGElement;
  inlined.setAttribute("x", "0");
  inlined.setAttribute("y", String(headH));
  inlined.setAttribute("width", String(cw));
  inlined.setAttribute("height", String(ch));
  out.appendChild(inlined);

  y = headH + ch + lineH;
  for (const k of meta.keyEntries) {
    const ref = chart.querySelector(`.${k.cls}`);
    if (ref !== null) {
      const cs = getComputedStyle(ref);
      const sample = document.createElementNS(NS, k.cls === "svp-band" ? "rect" : "line");
      if (k.cls === "svp-band") {
        sample.setAttribute("x", String(padX));
        sample.setAttribute("y", String(y - 6));
        sample.setAttribute("width", "22");
        sample.setAttribute("height", "7");
        // The band's in-plot opacity (0.13) vanishes at swatch size — same floor
        // the HTML legend applies (style.css .svp-lgkey .svp-band).
        const bandOp = Math.max(0.3, Number.parseFloat(cs.opacity) || 1);
        sample.setAttribute("style", `fill:${cs.fill};opacity:${bandOp};`);
      } else {
        sample.setAttribute("x1", String(padX));
        sample.setAttribute("y1", String(y - 3));
        sample.setAttribute("x2", String(padX + 22));
        sample.setAttribute("y2", String(y - 3));
        sample.setAttribute(
          "style",
          `stroke:${cs.stroke};stroke-width:${cs.strokeWidth};stroke-dasharray:${cs.strokeDasharray};opacity:${cs.opacity};fill:none;`,
        );
      }
      out.appendChild(sample);
    }
    text(padX + 28, y, k.label, 7.5, false);
    y += lineH;
  }

  y += 2;
  for (const line of meta.footLines) {
    text(padX, y, line, 7.5, false);
    y += lineH;
  }

  const xml = new XMLSerializer().serializeToString(out);
  const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("SVG rasterise failed"));
      img.src = url;
    });
    const SCALE = 3;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(cw * SCALE);
    canvas.height = Math.round(totalH * SCALE);
    const ctx = canvas.getContext("2d");
    if (ctx === null) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  } finally {
    URL.revokeObjectURL(url);
  }
}

// --- Surface-SV matcher: Les's model-selection rule as a UI affordance ---------
//
// His methodology (svp-bender-requirements.md): the ship measures surface sound
// velocity at the transducer, and he picks the source whose SURFACE value matches. We
// HIGHLIGHT that match across his trio — forecast (model), measured (float), usual
// (climatology); we never auto-select or hide a curve. The surveyor picks, the console
// informs. Each closest case tells a different story: an OBSERVED cast matching is the
// strongest validation (a real cast agreeing with the ship's own probe); a CLIMATOLOGY
// match beating the forecast is itself a warning that today's forecast may be off here.

interface SurfaceMatch {
  /** Chip HTML appended to the modelled sound-velocity legend entry ("" = no ship SV). */
  modelledChip: string;
  /** Chip HTML appended to the observed-float legend entry ("" = no float or no ship SV). */
  observedChip: string;
  /** Chip HTML appended to the climatology legend entry ("" = no climatology or no ship SV). */
  climatologyChip: string;
  /** One-line read under the legend; "" when no ship SV is set. */
  note: string;
}

const EMPTY_MATCH: SurfaceMatch = {
  modelledChip: "",
  observedChip: "",
  climatologyChip: "",
  note: "",
};

function surfaceMatch(
  eq: SoundSpeedEq,
  cell: SvpCell,
  obs: ArgoProfile | null,
  clim: SvpClimatology | null,
  shipSV: number | null,
): SurfaceMatch {
  // Cleared or malformed input → no comparison at all, never a NaN chip.
  if (shipSV === null || !Number.isFinite(shipSV)) return EMPTY_MATCH;
  const p0 = cell.p[0];
  if (p0 === undefined || p0.length < 3) return EMPTY_MATCH;

  const modelledDelta = soundSpeed(eq, p0[1], p0[2], p0[0]) - shipSV;
  let observedDelta: number | null = null;
  if (obs !== null && obs.levels.length > 0) {
    const l0 = obs.levels[0];
    observedDelta = soundSpeed(eq, l0.tempC, l0.salPSU, l0.depthM) - shipSV;
  }
  let climatologyDelta: number | null = null;
  if (clim !== null && clim.levels.length > 0) {
    const [d0, t0, s0] = clim.levels[0];
    climatologyDelta = soundSpeed(eq, t0, s0, d0) - shipSV;
  }

  // Winner = smallest |Δ| across the trio. Ties resolve by a fixed precedence: the
  // observed cast first (an in-situ match is the strongest claim), then the forecast,
  // and only then climatology — so the notable "climatology beats forecast" case must
  // win outright on the numbers, never by a tie-break, before we say the forecast is off.
  type Src = "model" | "obs" | "clim";
  const rank: Record<Src, number> = { obs: 0, model: 1, clim: 2 };
  const cands: { key: Src; delta: number }[] = [{ key: "model", delta: modelledDelta }];
  if (observedDelta !== null) cands.push({ key: "obs", delta: observedDelta });
  if (climatologyDelta !== null) cands.push({ key: "clim", delta: climatologyDelta });
  let winner = cands[0];
  for (const cd of cands) {
    const da = Math.abs(cd.delta);
    const wa = Math.abs(winner.delta);
    if (da < wa - 1e-9 || (Math.abs(da - wa) <= 1e-9 && rank[cd.key] < rank[winner.key])) {
      winner = cd;
    }
  }

  const fmtDelta = (d: number): string => `${d >= 0 ? "+" : "−"}${Math.abs(d).toFixed(1)}`;
  const chip = (d: number, isWinner: boolean): string =>
    isWinner
      ? `<span class="svp-dchip ok">closest match · Δ ${fmtDelta(d)} m/s</span>`
      : `<span class="svp-dchip muted">Δ ${fmtDelta(d)} m/s</span>`;

  const modelledChip = chip(modelledDelta, winner.key === "model");
  const observedChip = observedDelta !== null ? chip(observedDelta, winner.key === "obs") : "";
  const climatologyChip =
    climatologyDelta !== null ? chip(climatologyDelta, winner.key === "clim") : "";

  const lead = `Ship's surface SV ${shipSV.toFixed(1)} m/s: `;
  let note: string;
  if (winner.key === "obs") {
    note =
      lead +
      `the OBSERVED float cast is closest (Δ ${fmtDelta(observedDelta as number)} m/s) — ` +
      `an in-situ cast agreeing with the transducer is the strongest validation available.`;
  } else if (winner.key === "clim") {
    const name = clim !== null && clim.monthName !== "" ? clim.monthName : "monthly";
    note =
      lead +
      `closest to the WOA23 ${name} climatology (Δ ${fmtDelta(climatologyDelta as number)} m/s) — ` +
      `the transducer agrees with the long-term average more than with today's forecast, so the ` +
      `forecast may be off here.`;
  } else {
    note =
      lead +
      `the modelled forecast curve is closest (Δ ${fmtDelta(modelledDelta)} m/s). ` +
      `Highlight only — the surveyor picks the curve.`;
  }
  return { modelledChip, observedChip, climatologyChip, note };
}

interface SvpPanel {
  root: HTMLElement;
  /** Render a cell, or the no-coverage state (r = null with answered = true: the
   *  server said "no grid cell here", which after the 2026-07-21 global grid means
   *  land-adjacent or an enclosed sea the model omits — never silence; Les read the
   *  old silent blank as a broken panel). answered = false (transport failure)
   *  hides, as before. The site is the CLICKED coordinate (not the model grid
   *  point), carried for the CSV metadata. */
  render(r: SvpResult | null, site: { lat: number; lon: number } | null, answered: boolean): void;
  /** Set (or clear) the observed Argo overlay. Independent of `render`: the SVP
   *  grid and the float answer on their own timelines, so each updates the panel
   *  when it lands rather than one waiting on the other. */
  setObserved(obs: ArgoProfile | null): void;
  /** Set (or clear) the charted water depth (NCEI) for the current site. Its own
   *  timeline again — it rides the point-forecast fetch, which must never gate
   *  the SVP render. Extends the depth axis, draws the seabed line, and anchors
   *  the CSV/PNG provenance. */
  setSiteDepth(depthM: number | null): void;
  /** Hide + clear, on a new point load until the fetch answers. */
  reset(): void;
}

// Sound-speed structure as its own collapsible panel (survey acoustics) rather than
// buried in the location primer: the depth profile + a one-line read, sitting with
// the other graphs. The grid is global (2026-07-21); when a point still has no
// model cell (land-adjacent, enclosed seas) the panel says so rather than hiding.
function buildSvpPanel(parent: HTMLElement): SvpPanel {
  const b = el("section", "block");
  b.style.display = "none"; // shown once a covered point loads
  const bh = el("div", "bh");
  const t = el("span", "bt", "Sound Velocity Profile (SVP)");
  t.appendChild(infoButton("svp.overview"));
  bh.append(t, el("span", "bsub", "temperature · salinity · sound velocity vs depth"));
  b.appendChild(bh);

  // Sound-speed equation selector — recomputes the SV curve from the stored T/S/D.
  const ctl = el("div", "svp-ctl");
  const lab = el("label", "svp-ctl-lab", "Sound-speed equation");
  lab.setAttribute("for", "svp-eq");
  const sel = document.createElement("select");
  sel.className = "sel svp-eq";
  sel.id = "svp-eq";
  for (const e of SOUND_SPEED_EQS) {
    const o = document.createElement("option");
    o.value = e.key;
    o.textContent = e.label;
    sel.appendChild(o);
  }
  sel.value = DEFAULT_EQ;
  ctl.append(lab, sel);
  b.appendChild(ctl);

  // Depth-range zoom (Les, 2026-07-27): segmented presets clamping the shared
  // depth axis to an upper-ocean cut. Buttons are rebuilt per column by draw()
  // — which presets exist is a fact about the data, not the control.
  const zoomCtl = el("div", "svp-ctl");
  const zoomLab = el("span", "svp-ctl-lab", "Depth range");
  const zoomSeg = el("div", "rungseg svp-zoom");
  zoomCtl.append(zoomLab, zoomSeg);
  b.appendChild(zoomCtl);

  // Ship's surface SV (Task 2) and the deliverable download (Task 1) share one row.
  const actions = el("div", "svp-actions");
  const shipWrap = el("div", "svp-ship-ctl");
  const shipLab = el("label", "svp-ctl-lab", "Ship's surface SV (m/s)");
  shipLab.setAttribute("for", "svp-ship-sv");
  const shipInput = document.createElement("input");
  shipInput.type = "number";
  shipInput.className = "sel svp-ship";
  shipInput.id = "svp-ship-sv";
  shipInput.step = "0.1";
  shipInput.inputMode = "decimal";
  shipInput.placeholder = "optional · from transducer";
  shipWrap.append(shipLab, shipInput);
  const dlBtn = el("button", "btn svp-dl", "Download SVP (.csv)");
  dlBtn.type = "button";
  dlBtn.disabled = true; // enabled once a covered cell is rendered
  const pngBtn = el("button", "btn svp-dl", "Download SVP (.png)");
  pngBtn.type = "button";
  pngBtn.disabled = true;
  actions.append(shipWrap, dlBtn, pngBtn);
  b.appendChild(actions);

  const body = el("div", "svp-body");
  b.appendChild(body);
  const src = el("div", "bsrc source", "");
  b.appendChild(src);
  makeCollapsible(b, bh, "Sound Velocity Profile");
  parent.appendChild(b);

  const SHIP_SV_KEY = "seastate_svp_shipsv_v1";
  let lastResult: SvpResult | null = null;
  let lastCell: SvpCell | null = null;
  let lastObs: ArgoProfile | null = null;
  // Climatology (WOA23) arrives in the same /api/svp result as the cell, not a
  // separate fetch — held here so draw() can offer it as the third reference curve.
  let lastClim: SvpClimatology | null = null;
  let site: { lat: number; lon: number } | null = null;
  // Charted water depth (NCEI) for the current site — arrives on the primer's
  // timeline, after or before the SVP result, so it is state here rather than an
  // argument to render().
  let siteDepth: number | null = null;
  // True when the server answered "no grid cell here" (vs. transport failure or
  // not-yet-asked). Drives the visible no-coverage state instead of a silent blank.
  let noCoverage = false;
  let eq: SoundSpeedEq = DEFAULT_EQ;
  // Chosen depth-range cut; null = full column. Like `eq`, a view preference
  // that outlives site changes (reset() leaves it alone): a surveyor working the
  // upper 200 m keeps that frame from site to site, falling back to Full only
  // when a column no longer reaches the cut. Never touches the CSV/PNG exports.
  let zoomM: number | null = null;
  // Ship's surface SV persists for the SESSION only (no server): a transducer reading
  // is a property of this survey, not a console-wide default, and must not outlive the
  // tab. null = unset or malformed (which draws no comparison, never a NaN chip).
  let shipSV: number | null = null;
  {
    const saved = sessionStorage.getItem(SHIP_SV_KEY);
    if (saved !== null) {
      const v = Number.parseFloat(saved);
      if (Number.isFinite(v)) {
        shipSV = v;
        shipInput.value = saved;
      }
    }
  }

  const climLegendLabel = (): string =>
    lastClim !== null && lastClim.monthName !== ""
      ? `WOA23 · ${lastClim.monthName} average`
      : "WOA23 · monthly average";

  // Footer provenance, rebuilt whenever any of its inputs lands (the SVP result,
  // the charted depth). The climatology clause names its source and its own
  // (coarser) grid cell when the block is present.
  const renderSrc = (): void => {
    if (lastResult === null) {
      src.textContent = "";
      return;
    }
    const r = lastResult;
    const climProv =
      r.climatology !== null
        ? ` Reference climatology · ${r.climatology.source}` +
          `${r.climatology.monthName !== "" ? ` ${r.climatology.monthName} average` : ""}` +
          ` at cell ${fmtCellCoord(r.climatology.cellLat, r.climatology.cellLon)}.`
        : "";
    const depthProv =
      siteDepth !== null && Number.isFinite(siteDepth) && siteDepth > 0
        ? ` Charted water depth ~${Math.round(siteDepth)} m (NCEI).`
        : "";
    src.textContent =
      `Planning guide · Copernicus forecast ${r.date} — confirm with an on-site SV cast.` +
      depthProv +
      climProv;
  };

  // Sync the depth-range control against THIS column: presets the column
  // doesn't reach past are not offered, and a held cut the column no longer
  // reaches falls back to Full — state included, so a later deep site doesn't
  // resurrect a forgotten choice. Shallower than every preset, the row hides:
  // a control offering only "Full" is furniture. Buttons are rebuilt only when
  // the offered set changes; a mere selection re-toggles classes (rungseg
  // pattern), so the clicked button keeps focus.
  let zoomBtns = new Map<number | null, HTMLButtonElement>();
  const syncZoomCtl = (fullD: number): void => {
    const presets = SVP_ZOOM_PRESETS.filter((p) => p < fullD);
    if (zoomM !== null && !presets.includes(zoomM)) zoomM = null;
    zoomCtl.style.display = presets.length === 0 ? "none" : "";
    if (zoomBtns.size !== presets.length + 1 || presets.some((p) => !zoomBtns.has(p))) {
      zoomBtns = new Map();
      zoomSeg.replaceChildren();
      for (const val of [null, ...presets]) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "rungseg-btn";
        btn.textContent = val === null ? "Full" : `0–${val} m`;
        btn.title = val === null ? "Full water column" : `Upper ${val} m only`;
        btn.addEventListener("click", () => {
          if (zoomM === val) return;
          zoomM = val;
          draw();
        });
        zoomBtns.set(val, btn);
        zoomSeg.appendChild(btn);
      }
    }
    for (const [val, btn] of zoomBtns) btn.classList.toggle("on", val === zoomM);
  };

  const draw = (): void => {
    if (lastCell === null) return;
    const c = lastCell;
    const surface = soundSpeed(eq, c.p[0][1], c.p[0][2], c.p[0][0]);
    // sound-channel axis recomputed under the chosen equation
    let axisDepth = c.p[0][0];
    let axisMin = surface;
    for (const r of c.p) {
      if (r.length >= 4 && Number.isFinite(r[1]) && Number.isFinite(r[2])) {
        const v = soundSpeed(eq, r[1], r[2], r[0]);
        if (v < axisMin) {
          axisMin = v;
          axisDepth = r[0];
        }
      }
    }
    const bits: string[] = [`surface ${Math.round(surface)} m/s`];
    if (c.mld !== null) bits.push(`mixed layer to ${c.mld} m`);
    if (c.th !== null) bits.push(`thermocline ${c.th[0]}–${c.th[1]} m (−${c.th[2]} °C)`);
    bits.push(`sound-channel axis ~${Math.round(axisDepth)} m`);
    // Control first, chart second: syncZoomCtl may retire a cut this column
    // doesn't reach, and the chart must draw the choice that survived.
    syncZoomCtl(svpFullDepthM(c, lastObs, siteDepth));
    const chart = svpProfilesSvg(c, eq, lastObs, lastClim, siteDepth, zoomM);
    // Surface-SV match chips ride ON the relevant legend entries (sound velocity =
    // modelled, float = observed, WOA23 = climatology). Recomputed under the current eq,
    // so re-selecting the equation after typing a ship value re-evaluates the match
    // rather than going stale.
    const match = surfaceMatch(eq, c, lastObs, lastClim, shipSV);
    // Climatology legend text carries the served month ("WOA23 · July average").
    const climLabel = climLegendLabel();
    const legend =
      chart === ""
        ? ""
        : `<p class="svp-legend">` +
          `<span class="svp-lg svp-lg-temp">${svpLegendKey("svp-temp")}temperature</span>` +
          `<span class="svp-lg svp-lg-sal">${svpLegendKey("svp-sal")}salinity</span>` +
          `<span class="svp-lg svp-lg-sound">${svpLegendKey("svp-sound")}sound velocity</span>` + match.modelledChip +
          (c.th !== null ? `<span class="svp-lg svp-lg-band">${svpLegendKey("svp-band")}thermocline</span>` : "") +
          (c.mld !== null ? `<span class="svp-lg svp-lg-mld">${svpLegendKey("svp-mld")}mixed layer</span>` : "") +
          (lastObs !== null
            ? `<span class="svp-lg svp-lg-obs">${svpLegendKey("svp-obs")}ARGO float (observed)</span>` + match.observedChip
            : "") +
          (lastClim !== null
            ? `<span class="svp-lg svp-lg-clim">${svpLegendKey("svp-clim")}${escapeHtml(climLabel)}</span>` + match.climatologyChip
            : "") +
          `</p>`;
    const matchNote =
      chart !== "" && match.note !== "" ? `<p class="svp-match-note">${escapeHtml(match.note)}</p>` : "";
    // Provenance for the observed overlay, in the house "float · age · distance"
    // form. The reach note states the cast's own bottom — the axis now covers it
    // (the cast is one of the axis-floor inputs), so this is data provenance, not
    // a deeper-than-the-window warning.
    let obsSrc = "";
    if (lastObs !== null) {
      const days = Math.round(argoAgeDays(lastObs));
      const nm = Math.round(lastObs.distanceNm);
      // Stated in Les's own words (2026-07-21): "Observed. ARGO float <id>" — the
      // word ARGO and the float number, unmissable whenever an observation is drawn.
      obsSrc =
        `<p class="svp-obs-src"><strong>Observed · ARGO float ${escapeHtml(lastObs.platform)}</strong> · ` +
        `${days} d ago · ${nm} nm away · profile to ${Math.round(lastObs.maxDepthM)} m.</p>`;
    }
    body.innerHTML =
      `<p class="svp-note">${escapeHtml(bits.join("; "))}.</p>` + chart + legend + matchNote + obsSrc;
  };

  sel.addEventListener("change", () => {
    if (isSoundSpeedEq(sel.value)) {
      eq = sel.value;
      draw();
    }
  });

  // Ship's surface SV: parse on every keystroke, persist per-session, redraw the chips.
  // Empty or malformed clears the comparison (shipSV = null) rather than showing NaN.
  shipInput.addEventListener("input", () => {
    const raw = shipInput.value.trim();
    const v = raw === "" ? Number.NaN : Number.parseFloat(raw);
    shipSV = Number.isFinite(v) ? v : null;
    if (shipSV !== null) sessionStorage.setItem(SHIP_SV_KEY, String(shipSV));
    else sessionStorage.removeItem(SHIP_SV_KEY);
    draw();
  });

  // The no-coverage state: the server answered and there is no model cell here.
  // Shown ONLY when the NCEI charted depth says the click is at sea — a nearby
  // float is NOT enough to show it, because the Argo reuse radius (60 nm) can
  // reach a ways inland and the SVP endpoint answers 204 for land too; a LAND
  // click must never grow this note. The wording asserts no reason: the client
  // cannot tell "no wet cell near this point" from "no grid loaded yet", and a
  // confident wrong explanation is worse than the blank it replaces. The
  // observed ARGO cast, when one is in range, is still named — the observation
  // exists whether or not the model has a cell to sit beside it.
  const drawNoCoverage = (): void => {
    let obsSrc = "";
    if (lastObs !== null) {
      const days = Math.round(argoAgeDays(lastObs));
      const nm = Math.round(lastObs.distanceNm);
      obsSrc =
        `<p class="svp-obs-src"><strong>Observed · ARGO float ${escapeHtml(lastObs.platform)}</strong> · ` +
        `${days} d ago · ${nm} nm away · profile to ${Math.round(lastObs.maxDepthM)} m.</p>`;
    }
    body.innerHTML =
      `<p class="svp-note">No modelled SVP is available at this point.` +
      (lastObs !== null ? ` A real cast was made nearby:` : "") +
      `</p>` + obsSrc;
    src.textContent = "SVP model coverage · global ocean (Copernicus).";
  };

  // One dispatcher decides what the panel shows; every state setter funnels here.
  // The equation selector, depth-range control, ship's-SV input and download
  // buttons only make sense against a modelled profile, so they hide with the
  // chart in no-coverage mode.
  const paint = (): void => {
    const hasCell = lastCell !== null;
    ctl.style.display = hasCell ? "" : "none";
    actions.style.display = hasCell ? "" : "none";
    if (!hasCell) zoomCtl.style.display = "none"; // else per-column, via syncZoomCtl
    if (hasCell) {
      draw();
      renderSrc();
      b.style.display = "";
      return;
    }
    if (noCoverage && siteDepth !== null) {
      drawNoCoverage();
      b.style.display = "";
      return;
    }
    b.style.display = "none";
  };

  // Download the CURRENTLY DISPLAYED profile as Les's CSV (client-side Blob; no server).
  dlBtn.addEventListener("click", () => {
    if (lastResult === null || site === null) return;
    const csv = buildSvpCsv(lastResult, eq, site, lastObs, lastClim, siteDepth);
    if (csv === null) return;
    // UTF-8 BOM so Excel reads the file as UTF-8 — the equation labels carry an en-dash
    // (e.g. "Chen–Millero"), which would otherwise mojibake in a default-codepage open.
    const blob = new Blob(["﻿" + csv.text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = csv.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

  // "Download SVP (.png)" — the graphic beside the data file (Les, 2026-07-21).
  // Provenance and the curve key travel IN the image: the PNG gets pasted into
  // survey reports, where the panel's HTML legend and footer can't follow it.
  pngBtn.addEventListener("click", () => {
    void (async () => {
      if (lastResult === null || site === null) return;
      // The PNG keeps the FULL column whatever the on-screen depth-range cut —
      // the zoom is a reading aid, and a report graphic cropped by it would
      // misstate the water column (the rule the CSV already follows). The
      // full-depth chart is mounted hidden just long enough for style inlining:
      // getComputedStyle only resolves class-carried styles inside the document.
      let chartEl = body.querySelector<SVGSVGElement>("svg.svp-svg3");
      let scratch: HTMLElement | null = null;
      if (zoomM !== null && lastCell !== null) {
        scratch = el("div", "svp-png-scratch");
        scratch.innerHTML = svpProfilesSvg(lastCell, eq, lastObs, lastClim, siteDepth, null);
        body.appendChild(scratch);
        chartEl = scratch.querySelector<SVGSVGElement>("svg.svp-svg3") ?? chartEl;
      }
      try {
        if (chartEl === null) return;
        const r = lastResult;
        // One fact per line: the frame is 360 SVG units wide and SVG text does not
        // wrap, so a combined source+equation+cell line would rasterise clipped.
        const headLines: string[] = [
          `Model: ${r.source}`,
          `Forecast ${r.date} · ${eqLabel(eq)} · model cell ${fmtCellCoord(r.cell.lat, r.cell.lon)}`,
        ];
        if (siteDepth !== null && Number.isFinite(siteDepth) && siteDepth > 0) {
          headLines.push(`Charted water depth ~${Math.round(siteDepth)} m (NCEI)`);
        }
        const keyEntries: { cls: string; label: string }[] = [
          { cls: "svp-sound", label: "modelled forecast (solid; per-panel colours)" },
        ];
        if (lastObs !== null) {
          keyEntries.push({ cls: "svp-obs", label: `observed · ARGO float ${lastObs.platform}` });
        }
        if (lastClim !== null) keyEntries.push({ cls: "svp-clim", label: climLegendLabel() });
        if (lastCell !== null && lastCell.mld !== null) {
          keyEntries.push({ cls: "svp-mld", label: "mixed-layer depth" });
        }
        if (lastCell !== null && lastCell.th !== null) {
          keyEntries.push({ cls: "svp-band", label: "thermocline band" });
        }
        const footLines: string[] = [];
        if (lastObs !== null) {
          const castDate = new Date(lastObs.timeUtcMs).toISOString().slice(0, 10);
          // Two lines for the same clipping reason as the header.
          footLines.push(
            `Observed · ARGO float ${lastObs.platform} · cast ${castDate}`,
            `${Math.round(lastObs.distanceNm)} nm from site · profile to ${Math.round(lastObs.maxDepthM)} m`,
          );
        }
        footLines.push("Planning guide — confirm with an on-site SV cast.");
        const blob = await buildSvpPng(chartEl, {
          title: `Sound Velocity Profile — ${fmtCellCoord(site.lat, site.lon)} — ${r.date}`,
          headLines,
          keyEntries,
          footLines,
        });
        if (blob === null) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `SVP_${coordToken(site.lat, site.lon)}_${fnToken(r.source)}_${r.date}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } finally {
        if (scratch !== null) scratch.remove();
      }
    })().catch(() => undefined); // a failed export does nothing; it must never wedge the panel
  });

  return {
    root: b,
    reset(): void {
      b.style.display = "none";
      lastResult = null;
      lastCell = null;
      lastObs = null;
      lastClim = null;
      site = null;
      siteDepth = null;
      noCoverage = false;
      dlBtn.disabled = true;
      pngBtn.disabled = true;
      body.replaceChildren();
      src.textContent = "";
    },
    render(r: SvpResult | null, s: { lat: number; lon: number } | null, answered: boolean): void {
      lastResult = r;
      lastCell = r !== null ? r.cell : null;
      lastClim = r !== null ? r.climatology : null;
      site = s;
      noCoverage = answered && r === null;
      dlBtn.disabled = r === null;
      pngBtn.disabled = r === null;
      paint();
    },
    setObserved(obs: ArgoProfile | null): void {
      lastObs = obs;
      // A repaint, whatever the mode: beside the model when there is a cell, named
      // in the no-coverage state when there is not — the observation exists either way.
      paint();
    },
    setSiteDepth(depthM: number | null): void {
      const d = depthM !== null && Number.isFinite(depthM) && depthM > 0 ? depthM : null;
      if (d === siteDepth) return;
      siteDepth = d;
      paint();
    },
  };
}

// A tropical callout for the location primer / window briefing: the NHC context
// that today only lives in a transient map hover. Empty string when clear.
function tropicalHtml(t: TropicalContext | null): string {
  if (t === null) return "";
  const bits: string[] = [];
  if (t.storm !== null) {
    bits.push(
      `<p class="trop-note crit">⚠ Inside the NHC forecast cone of ` +
        `<strong>${escapeHtml(t.storm.name)}</strong> — see the track on the map.</p>`,
    );
  }
  if (t.dev !== null) {
    const basin = t.dev.basin !== "" ? ` (${escapeHtml(t.dev.basin)})` : "";
    const risk = t.dev.risk7day !== "" ? ` · ${escapeHtml(t.dev.risk7day)} risk` : "";
    const p7 = t.dev.prob7day !== "" ? escapeHtml(t.dev.prob7day) : "—";
    const p2 = t.dev.prob2day !== "" ? escapeHtml(t.dev.prob2day) : "—";
    bits.push(
      `<p class="trop-note warn">⚠ NHC tropical development area${basin} — ` +
        `${p7} chance of formation in 7 days${risk}; ${p2} in 48 h.</p>`,
    );
  }
  return bits.join("");
}

// Convert the API registry to the ops Registry (identical shape; squall
// sensitivity narrowed to its union).
function toRegistry(api: ApiRegistry): Registry {
  const narrow = (s: unknown): "low" | "med" | "high" =>
    s === "low" || s === "high" ? s : "med";
  const defaults: Registry["defaults"] = {};
  for (const [kind, d] of Object.entries(api.defaults)) {
    defaults[kind] = {
      label: d.label,
      wave_m: d.wave_m,
      wind_kn: d.wind_kn,
      task_hours: d.task_hours,
      squall_sensitivity: narrow(d.squall_sensitivity),
      dp_station: d.dp_station === true,
    };
  }
  return {
    vessels: api.vessels,
    operations: api.operations,
    defaults,
    limits: api.limits,
  };
}

// --- app state ----------------------------------------------------------------

interface AppState {
  user: User | null;
  registry: Registry;
}

const state: AppState = { user: null, registry: fallbackRegistry() };

// --- display theme: ECDIS-style Day / Dusk / Night, plus Auto (by local sun) ----
//
// The palette lives in CSS custom properties (Day = :root, Dusk/Night override by
// data-theme). Charts read those vars at draw time, so a theme change repaints the
// console; the raster basemap is dimmed via MapLibre paint properties. Auto picks
// the table from the sun's elevation at the current point (or the device location
// when granted), re-checked every few minutes.

type ThemeChoice = "day" | "dusk" | "night" | "auto";
type ResolvedTheme = "day" | "dusk" | "night";
const THEME_KEY = "seastate_theme_v1";

let themeChoice: ThemeChoice = "day";
let autoAnchor: [number, number] = DEFAULT_POINT; // where Auto reads the sun
let autoTimer: number | null = null;
// Set by the console so a theme change can repaint its canvases; null off-console.
let activeThemeRepaint: (() => void) | null = null;
// The map's brightness dimmer, built with the map and null everywhere else (the
// report and manual pages have no map to dim).
let mapDimmer: MapDimmer | null = null;
// The socks live as long as the map they are pinned to: createMap tears its
// container down and rebuilds it on a theater change, which takes the attached
// marker with it, so these are rebuilt in the same breath.
let pointSocks: PointSocks | null = null;
// Same pattern for the clock: a Local/UTC flip re-renders the console's TEXTUAL
// time surfaces (window chips, briefing prose, freshness line, feedback stamps).
// Canvas surfaces (charts, ruler, transport) hold their own subscriptions.
let activeClockRepaint: (() => void) | null = null;
// And for units: a metric/imperial flip converts numbers on every surface —
// chart axes, meters, prose, the seabed chip — so the console reloads its
// current point, the same simple whole-repaint a theme change uses.
let activeUnitsRepaint: (() => void) | null = null;
// And for typed coordinates: the console points this at the same path a map
// click takes, so the header's coordinate entry works wherever the console is up.
let coordEntry: ((lat: number, lon: number) => void) | null = null;
/** The map's search field. Held so a re-render disposes the old one rather than
 *  leaving it attached to a destroyed map container. */
let activeSearch: SearchBox | null = null;
// The console's most recent picked point, so the Report page can prefill its
// position field with the place the operator was just looking at. Survives
// route changes (module-level, set on every console load()); null before any
// point has been picked this session.
let lastPickedPoint: { lat: number; lon: number } | null = null;
// And the Workability panel's current vessel × operation, mirrored on every
// selection change for the same reason: the Report page's "From current view"
// snapshot must name the pair the console is actually judging.
let lastWorkSelection: { vesselId: number; opId: number } | null = null;

function resolvedTheme(): ResolvedTheme {
  if (themeChoice !== "auto") return themeChoice;
  const elev = sunElevation(Date.now(), autoAnchor[0], autoAnchor[1]);
  if (elev > 6) return "day"; // sun well up
  if (elev < -6) return "night"; // past civil twilight — dark
  return "dusk"; // sun near the horizon
}

// Per-client branding: logo pair (day / inverse for the dark bridge themes) and
// an optional Day-theme tint. The tint touches UI chrome only — Dusk/Night are
// physiological palettes and stay untouched, and the semantic data colours
// (--good/--warn/--crit, the chart model series) are never rebranded.
interface ClientBrand {
  /** Both optional. A client we have no artwork for still gets its NAME beside
   *  our mark — see buildHeader. Without this the header showed our mark and our
   *  wordmark and nothing of theirs, which on a bespoke console reads as our
   *  logo printed twice. A registered client with no logo file is the normal
   *  state at the start of an engagement, not an error. */
  logoDay?: string;
  logoNight?: string;
  /** Day-theme overrides for --navy / --accent. Accent shows up in chrome AND
   *  in the chart cursor + the "Map · GFS-Wave" sample trace, so any accent
   *  chosen here must stay distinguishable from the model-series blues. */
  navy?: string;
  accent?: string;
}

const CLIENT_BRANDS: Record<string, ClientBrand> = {
  // Key must equal users.client verbatim — seed.go's clientName, not the
  // colloquial short form (that mismatch shipped once and ate the logo).
  "TDI-Brooks International": {
    logoDay: "/assets/tdi-brooks.png",
    logoNight: "/assets/tdi-brooks-night.png",
    navy: "#213d75", // sampled from TDI-Brooks collateral
    accent: "#1d97bf", // the deeper of their cyans — the bright one washes out on white
  },
  // Wikimedia Commons, "Pacific Gas and Electric Company (logo).svg": public
  // domain (simple geometric shapes and text, below the threshold of
  // originality) and marked "Restrictions: trademarked", which is the expected
  // status and the legitimate case — identifying PG&E on a console built FOR
  // PG&E is nominative use, the same footing as the TDI-Brooks mark above.
  //
  // ONE FILE FOR BOTH THEMES, on purpose. The mark is a filled blue block with
  // white letterforms, so it carries its own contrast on any ground; TDI-Brooks
  // needs a second file because theirs is dark artwork that disappears at night.
  // Recolouring a registered trademark to suit our dark theme would be a worse
  // answer than reusing it, and the stylesheet already dims the badge.
  //
  // NO --navy / --accent OVERRIDES, and that is a decision rather than an
  // omission. Their palette is #1689C5 and #FDB813: the blue is far too bright
  // to sit behind white chrome where TDI-Brooks' #213d75 does, and the yellow
  // collides with the amber this console already spends on the DECISION BOUND.
  // On a margin product a brand colour must never land on top of a safety
  // colour, so their identity here is the mark and the name, not the palette.
  "Pacific Gas and Electric": {
    logoDay: "/assets/pge.png",
    logoNight: "/assets/pge.png",
  },
};

function clientBrand(): ClientBrand | undefined {
  return state.user !== null ? CLIENT_BRANDS[state.user.client] : undefined;
}

/** Inline overrides on :root would outrank the Dusk/Night stylesheet values, so
 *  they are set only while the resolved theme is Day and cleared otherwise. */
function applyClientBrand(t: ResolvedTheme): void {
  const root = document.documentElement.style;
  const b = clientBrand();
  if (t === "day" && b?.navy !== undefined) root.setProperty("--navy", b.navy);
  else root.removeProperty("--navy");
  if (t === "day" && b?.accent !== undefined) root.setProperty("--accent", b.accent);
  else root.removeProperty("--accent");
}

function applyTheme(): void {
  const t = resolvedTheme();
  document.documentElement.setAttribute("data-theme", t);
  applyClientBrand(t);
  applyMapTheme(t);
  // The dimmer is per-theme: it carries a separate stored brightness for Dusk
  // and for Night, and hides itself on Day (a sunlit bridge wants every candela
  // the panel has). Told last, so it re-applies its stored value ON TOP of the
  // basemap dimming applyMapTheme just set.
  mapDimmer?.setTheme(t);
  activeThemeRepaint?.();
}

// Dim the raster basemap to match the table. Day = full; Dusk/Night progressively
// darker and desaturated. No-op until the map style is loaded (re-applied on ready).
function applyMapTheme(t: ResolvedTheme): void {
  const mm = activeMap;
  if (mm === null) return;
  const m = mm.raw();
  // NOT gated on isStyleLoaded(): that predicate also goes false while raster
  // tiles are in flight, so a theme change during ordinary panning would silently
  // do nothing and leave a bright basemap on a darkened bridge. getLayer() is the
  // narrow question we actually mean — can this layer take a paint property.
  const cfg =
    t === "night"
      ? { bright: 0.24, sat: -0.7, contrast: 0.08 }
      : t === "dusk"
        ? { bright: 0.52, sat: -0.35, contrast: 0.04 }
        : { bright: 1, sat: 0, contrast: 0 };
  // Dim the basemap and any raster jurisdiction/lease overlays (ov-*).
  try {
    const dimIds = ["ocean", "labels"];
    for (const layer of m.getStyle().layers ?? []) {
      if (layer.type === "raster" && layer.id.startsWith("ov-")) dimIds.push(layer.id);
    }
    for (const id of dimIds) {
      if (!m.getLayer(id)) continue;
      m.setPaintProperty(id, "raster-brightness-max", cfg.bright);
      m.setPaintProperty(id, "raster-saturation", cfg.sat);
      m.setPaintProperty(id, "raster-contrast", cfg.contrast);
    }
  } catch {
    // A style swap between the getLayer check and the set; the next theme change
    // (or the onReady re-apply) puts it right. Never take the console down for it.
  }
  // Re-theme the vector overlays whose colours come from CSS vars (buoys, tropical,
  // and the framed frame + cell graticule).
  refreshOverlayColors(m);
  refreshTheaterColors(m);
}

function startAuto(): void {
  // A one-shot device-location read refines the anchor when the OS/user allows it;
  // otherwise Auto keeps using the current map point.
  if (typeof navigator !== "undefined" && navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        autoAnchor = [pos.coords.latitude, pos.coords.longitude];
        if (themeChoice === "auto") applyTheme();
      },
      () => {
        /* denied or unavailable — keep the map-point anchor */
      },
      { maximumAge: 3_600_000, timeout: 8000 },
    );
  }
  if (autoTimer === null) {
    autoTimer = window.setInterval(() => {
      if (themeChoice === "auto") applyTheme();
    }, 5 * 60_000);
  }
}

function stopAuto(): void {
  if (autoTimer !== null) {
    window.clearInterval(autoTimer);
    autoTimer = null;
  }
}

function setTheme(choice: ThemeChoice): void {
  themeChoice = choice;
  try {
    window.localStorage.setItem(THEME_KEY, choice);
  } catch {
    /* storage blocked — theme still applies for the session */
  }
  if (choice === "auto") startAuto();
  else stopAuto();
  applyTheme();
  document.querySelectorAll<HTMLElement>(".themeseg-btn").forEach((b) => {
    b.classList.toggle("on", b.dataset["theme"] === choice);
  });
}

function initTheme(): void {
  let saved: string | null = null;
  try {
    saved = window.localStorage.getItem(THEME_KEY);
  } catch {
    /* storage blocked */
  }
  themeChoice =
    saved === "dusk" || saved === "night" || saved === "auto" ? saved : "day";
  if (themeChoice === "auto") startAuto();
  document.documentElement.setAttribute("data-theme", resolvedTheme());
}

function buildThemeControl(): HTMLElement {
  const seg = el("div", "themeseg");
  seg.title = "Display for the bridge lighting — Auto follows local sunrise/sunset";
  const opts: [ThemeChoice, string][] = [
    ["day", "Day"],
    ["dusk", "Dusk"],
    ["night", "Night"],
    ["auto", "Auto"],
  ];
  for (const [val, label] of opts) {
    const b = el("button", "themeseg-btn", label);
    b.type = "button";
    b.dataset["theme"] = val;
    if (val === themeChoice) b.classList.add("on");
    b.addEventListener("click", () => setTheme(val));
    seg.appendChild(b);
  }
  return seg;
}

async function refreshAuth(): Promise<void> {
  const res = await me();
  state.user = res.ok ? res.data : null;
  if (state.user !== null) {
    // The server's prefs win over localStorage on sign-in (they follow the
    // account), and from here on preference writes mirror up to users.prefs.
    adoptServerPrefs(state.user.prefs);
    enableServerSync(true);
    await refreshRegistry();
  } else {
    enableServerSync(false);
    state.registry = fallbackRegistry();
  }
}

/**
 * Re-read the fleet, operations and limits into `state`.
 *
 * `state.registry` used to be written ONLY by refreshAuth, whose three call sites
 * are all auth lifecycle events — boot, login, sign-out. Navigation never went
 * near it, because render() rebuilds the console from whatever is already in
 * `state`. So an operator could set their limits on the Vessels page, click
 * Console, and be told their limits were still seeded defaults: the Vessels page
 * keeps its own local copy, the save genuinely succeeded, and the console simply
 * never asked again. Only F5 cleared it.
 *
 * Split out of refreshAuth so entering the console does not also re-hit /api/me.
 * Returns whether anything actually changed, so a caller can avoid a pointless
 * re-render on the common case where nothing was edited.
 */
async function refreshRegistry(): Promise<boolean> {
  const reg = await getRegistry();
  if (!reg.ok) return false; // leave the last-known-good registry in place
  const next = toRegistry(reg.data);
  const changed = JSON.stringify(next) !== JSON.stringify(state.registry);
  state.registry = next;
  return changed;
}

// --- router ---------------------------------------------------------------------

type Route =
  | "landing"
  | "about"
  | "case-study"
  | "console"
  | "vessels"
  | "report"
  | "login"
  | "guide"
  | "methods"
  | "updates"
  | "set-password"
  | "admin";

function currentRoute(): Route {
  const p = window.location.pathname;
  if (p.startsWith("/console")) return "console";
  if (p.startsWith("/vessels")) return "vessels";
  if (p.startsWith("/report")) return "report";
  if (p.startsWith("/login")) return "login";
  if (p.startsWith("/about")) return "about";
  if (p.startsWith("/case-study")) return "case-study";
  if (p.startsWith("/guide")) return "guide";
  if (p.startsWith("/methods")) return "methods";
  if (p.startsWith("/updates")) return "updates";
  if (p.startsWith("/set-password")) return "set-password";
  if (p.startsWith("/admin")) return "admin";
  return "landing";
}

function navigate(path: string): void {
  window.history.pushState(null, "", path);
  render();
}

// --- header ---------------------------------------------------------------------

/** The settings panel's "Change…" button and the small form it reveals. */
function buildPasswordControl(): HTMLElement {
  const wrap = el("div", "pwctl");
  const open = el("button", "pwbtn", "Change…");
  open.type = "button";
  const form = document.createElement("form");
  form.className = "pwform";
  form.hidden = true;
  const field = (label: string, autocomplete: string): HTMLInputElement => {
    const i = document.createElement("input");
    i.type = "password";
    i.required = true;
    i.setAttribute("autocomplete", autocomplete);
    i.placeholder = label;
    i.setAttribute("aria-label", label);
    return i;
  };
  const cur = field("Current password", "current-password");
  const next = field("New password (12+ characters)", "new-password");
  const again = field("New password again", "new-password");
  const save = el("button", "pwsave", "Save");
  save.type = "submit";
  const msg = el("span", "pwmsg");
  form.append(cur, next, again, save, msg);
  open.addEventListener("click", () => {
    form.hidden = !form.hidden;
    if (!form.hidden) cur.focus();
  });
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    msg.textContent = "";
    msg.className = "pwmsg";
    if (next.value !== again.value) {
      msg.textContent = "The two new passwords differ.";
      msg.classList.add("bad");
      return;
    }
    if ([...next.value].length < 12) {
      msg.textContent = "At least 12 characters.";
      msg.classList.add("bad");
      return;
    }
    save.disabled = true;
    void (async () => {
      try {
        const res = await fetch("/api/password", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ current: cur.value, new: next.value }),
        });
        const payload: unknown = await res.json().catch(() => null);
        if (!res.ok) {
          const err =
            typeof payload === "object" && payload !== null && typeof (payload as { error?: unknown }).error === "string"
              ? (payload as { error: string }).error
              : "";
          msg.textContent = err !== "" ? err : `Could not change it (HTTP ${res.status}).`;
          msg.classList.add("bad");
          return;
        }
        form.reset();
        msg.textContent = "Changed.";
      } catch {
        msg.textContent = "Could not reach the server.";
        msg.classList.add("bad");
      } finally {
        save.disabled = false;
      }
    })();
  });
  wrap.append(open, form);
  return wrap;
}

interface Header {
  coord: HTMLElement;
  juris: HTMLElement;
  seabed: HTMLElement;
}

/**
 * The phone header's hamburger.
 *
 * WHY A DRAWER AND NOT A SCROLLING ROW. The nav was a horizontal scroller below
 * 1100px: six links, of which two or three fit, and the rest reachable only by
 * a sideways swipe on a bar nobody expects to scroll. Links you cannot see are
 * links that do not exist, and on the phone the header's whole first line was
 * being spent on them. Behind a button the same six are a full-width list, and
 * the line goes back to what it should carry — whose console this is.
 *
 * The button only exists below 880px (display: none above), so a desktop never
 * sees it and the horizontal nav is untouched.
 *
 * CLOSING. Navigating rebuilds the header, so a tapped link closes the drawer by
 * construction. What is left is the two ways out that lead nowhere — a tap
 * outside and Escape — and both are on `document`, which is a leak if left
 * behind: this function runs on every render. The controller is aborted at the
 * top of the next build, so exactly one header's worth of listeners is ever
 * live.
 */
let burgerAbort: AbortController | null = null;

const BURGER_BARS =
  '<path d="M3 6h16M3 12h16M3 18h16" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>';
const BURGER_CLOSE =
  '<path d="M5 5l12 12M17 5L5 17" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>';

function buildBurger(head: HTMLElement, nav: HTMLElement): HTMLButtonElement {
  burgerAbort?.abort();
  const ac = new AbortController();
  burgerAbort = ac;

  const btn = el("button", "navburger");
  btn.type = "button";
  btn.setAttribute("aria-label", "Menu");
  btn.setAttribute("aria-expanded", "false");
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 22 22");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = BURGER_BARS;
  btn.appendChild(svg);

  const setOpen = (open: boolean): void => {
    head.dataset.menu = open ? "open" : "closed";
    btn.setAttribute("aria-expanded", String(open));
    svg.innerHTML = open ? BURGER_CLOSE : BURGER_BARS;
  };
  setOpen(false);

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    setOpen(head.dataset.menu !== "open");
  });
  // Tapping a link normally closes the drawer by rebuilding the header — except
  // when the link is the route you are already on, which re-renders nothing and
  // would leave the drawer sitting open over the page you asked to see.
  nav.addEventListener("click", (e) => {
    if (e.target instanceof Element && e.target.closest("a") !== null) setOpen(false);
  });
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target;
      if (head.dataset.menu !== "open") return;
      if (t instanceof Node && nav.contains(t)) return;
      setOpen(false);
    },
    { signal: ac.signal },
  );
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Escape" && head.dataset.menu === "open") {
        setOpen(false);
        btn.focus();
      }
    },
    { signal: ac.signal },
  );
  return btn;
}

/** The thermal console's own documentation, served per client (server/thermal.go). */
const THERMAL_PAPER = "/api/thermal/paper";
/** Routes that exist only for the fleet product. */
const FLEET_ONLY_ROUTES: readonly Route[] = ["guide", "methods", "updates", "report", "vessels"];

function buildHeader(root: HTMLElement, route: Route): Header {
  const head = el("header", "apphead");
  const brand = el("a", "brand");
  brand.href = "/";
  brand.appendChild(markEl(18));
  brand.appendChild(document.createTextNode("SeaState"));
  brand.addEventListener("click", (e) => {
    e.preventDefault();
    navigate("/");
  });
  head.appendChild(brand);

  // The client's own mark beside ours: this is a bespoke console and the header
  // says whose. Both theme variants ship in the DOM; CSS picks per bridge theme.
  const b = clientBrand();
  if (b !== undefined && state.user !== null) {
    const badge = el("span", "clientbadge");
    if (b.logoDay !== undefined && b.logoNight !== undefined) {
      for (const [cls, src] of [
        ["lg-day", b.logoDay],
        ["lg-night", b.logoNight],
      ] as const) {
        const img = document.createElement("img");
        img.className = cls;
        img.src = src;
        img.alt = state.user.client;
        badge.appendChild(img);
      }
    } else {
      // Wordmark fallback. Pointing <img> at artwork we do not have would put a
      // broken-image glyph in the header of a client demo, which is worse than
      // the plain name — and the name is the part that actually says whose
      // console this is.
      badge.classList.add("clientbadge-text");
      badge.textContent = state.user.client;
    }
    head.appendChild(badge);
  }
  // A principal looking at a client that is not their own is told so on every
  // route (server/tenant.go: "the condition the console must announce"), and
  // the chip is the way back: Admin › Console. Client accounts never carry the
  // flag, so they never see this.
  if (state.user?.viewing === true) {
    const chip = el("a", "viewingchip", `Viewing ${state.user.client}`);
    chip.href = "/admin#console";
    chip.title = `You are a SeaState principal looking at ${state.user.client}'s console. Switch in Admin › Console.`;
    chip.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/admin#console");
    });
    head.appendChild(chip);
  }

  const nav = document.createElement("nav");
  const link = (label: string, path: string, on: boolean): HTMLAnchorElement => {
    const a = el("a", on ? "on" : undefined, label);
    a.href = path;
    a.addEventListener("click", (e) => {
      e.preventDefault();
      navigate(path);
    });
    return a;
  };
  /**
   * Guide, Methods and Updates behind one Docs entry.
   *
   * They are siblings, not a hierarchy — how to use it, how far to trust it,
   * what changed — so none of them belongs underneath another. Updates in
   * particular must not sit inside Guide: somebody checking whether a number
   * changed meaning should not have to enter a tutorial to find out.
   *
   * On the console header this takes the nav from 341px to 266px — six items to
   * four — which is worth having on a bar that also carries the position, the
   * jurisdiction, the theme switch and the account.
   *
   * It does NOT buy anything on a phone, and the comment here said otherwise
   * until it was measured. The phone nav is a horizontal scroller, so six links
   * never wrapped it; the 117px-to-86px header saving came from flex-basis: 0,
   * not from the link count. Below 880px the group flattens back to its three
   * links (see the stylesheet) because a hover menu hanging off a scroller
   * would open off-screen and be untappable.
   *
   * Hover OR focus opens it, and the top item is a real link to /guide, so a
   * tap still goes somewhere sensible on a touch screen where there is no
   * hover — a menu that only opens on hover is a dead control on a phone.
   */
  // WHICH SURFACES BELONG TO WHICH CLIENT.
  //
  // Report, Vessels and the Guide are the FLEET console's. A thermal client has
  // no vessels, does not run workability, and the Guide is 25 references deep in
  // vessel and workability language. They were appended unconditionally, so the
  // PG&E account was offered four fleet surfaces in its own navigation.
  //
  // No data crossed — every endpoint behind them scopes on effectiveClient(),
  // and /api/registry verifiably returns an empty registry for demo@pge.com. But
  // "one client seeing another client's information" is not only about rows in a
  // database; a navigation full of another account's features is the same
  // failure wearing a smaller consequence, and on a sales demo it is the one the
  // client actually notices.
  //
  // DOCS ARE PER CONSOLE (Cody, 2026-10-06: "Docs should be different per
  // client"). Methods & Limitations and Updates were kept for the thermal
  // console as "client-neutral"; they are not — Methods opens with the fleet
  // product's not-for-navigation statement and Updates is the fleet changelog.
  // The thermal console's documentation is its own paper, which carries the
  // method, the verification and the limitations in one place.
  const isFleet = state.user?.console !== "thermal";
  const DOCS: readonly (readonly [string, string, Route, string])[] = isFleet
    ? [
        ["Guide", "/guide", "guide", "Getting a workability read, step by step"],
        ["Methods", "/methods", "methods", "How each number is made, and how far to trust it"],
        ["Updates", "/updates", "updates", "What has changed in the console"],
      ]
    : [];
  // The client's own paper, for consoles that have one. The endpoint behind it
  // re-checks the client per request (server/thermal.go), so this decides
  // rendering only.
  const docsExtra: (readonly [string, string, string])[] = isFleet
    ? []
    : [["Methods & limitations", THERMAL_PAPER, "The methods note — method, verification, accuracy and limitations"]];
  const docsOpen = DOCS.some(([, , r]) => route === r);
  const docs = el("div", "navgroup");
  // The group's top link has to land on a page this client actually has: the
  // guide for the fleet, the paper for a thermal console.
  const docsTop = isFleet ? link("Docs", "/guide", docsOpen) : el("a", undefined, "Docs");
  if (!isFleet) {
    docsTop.href = THERMAL_PAPER;
    docsTop.target = "_blank";
    docsTop.rel = "noopener";
  }
  docsTop.classList.add("navgroup-top");
  docsTop.setAttribute("aria-haspopup", "true");
  const docsMenu = el("div", "navmenu");
  for (const [label, path, blurb] of docsExtra) {
    const a = el("a", "navmenu-item");
    a.textContent = label;
    a.href = path;
    a.target = "_blank";
    a.rel = "noopener";
    a.appendChild(el("span", "navmenu-blurb", blurb));
    docsMenu.appendChild(a);
  }
  for (const [label, path, r, blurb] of DOCS) {
    const a = link(label, path, route === r);
    a.classList.add("navmenu-item");
    a.appendChild(el("span", "navmenu-blurb", blurb));
    docsMenu.appendChild(a);
  }
  docs.append(docsTop, docsMenu);

  nav.append(link("Console", "/console", route === "console"));
  if (isFleet) {
    nav.append(
      link("Report", "/report", route === "report"),
      link("Vessels", "/vessels", route === "vessels"),
    );
  }
  nav.append(docs);
  // Admin, for the two principals. Gated on `staff` and NOT on role === "admin",
  // which is what this line said until the client's own operator account — seeded
  // 'admin' since day one — turned out to satisfy it.
  //
  // Rendering is ALL this decides. Every admin endpoint re-reads the column per
  // request, so anyone who forges this into their DOM gets a page frame and
  // nothing to put in it. Last in the nav because it is ours, not the client's,
  // and it should not sit among the things they came here to use.
  if (state.user?.staff) {
    nav.appendChild(link("Admin", "/admin", route === "admin"));
  }
  head.appendChild(nav);
  head.appendChild(buildBurger(head, nav));

  const cb = el("div", "coordbox");
  const coord = el("span");
  const juris = el("span");
  const seabed = el("span");
  // Ethical anchor: always one glance away, on every route. The fleet's links
  // to the Methods page, which opens with the full not-for-navigation statement;
  // a thermal console's says what it is — decision support — and links to the
  // limitations section of its own paper. "Not for navigation" on a power
  // plant's intake forecast is the wrong warning.
  const disc = el("a", "disclaimer", isFleet ? "Not for navigation" : "Decision support only");
  if (isFleet) {
    disc.href = "/methods";
    disc.title = "Decision support only — see Methods & Limitations";
    disc.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/methods");
    });
  } else {
    disc.href = `${THERMAL_PAPER}#limitations`;
    disc.target = "_blank";
    disc.rel = "noopener";
    disc.title = "Decision support only — see the methods note's limitations";
  }

  // Settings: one gear, one compact panel of rows. The clock toggle is the
  // first row; later preferences (units, …) join it here rather than adding
  // more header chrome.
  const settings = createSettingsControl();
  settings.addRow(
    "Clock",
    segmented<TimeMode>(
      [
        ["local", "Local"],
        ["utc", "UTC"],
      ],
      getTimeMode,
      setTimeMode,
      "Times as the site's wall clock (the console's default), or UTC everywhere",
    ),
  );
  settings.addRow(
    "Units",
    segmented<UnitSystem>(
      [
        ["metric", "Metric"],
        ["imperial", "Imperial"],
      ],
      getUnitSystem,
      setUnitSystem,
      "Waves and depths in metres (the console's default) or feet — wind stays in knots, the marine convention",
    ),
  );

  // Positions. Survey deliverables do not agree on how a position is spelled —
  // their Ghana reporting uses degrees and decimal minutes, their Guyana
  // reporting UTM easting/northing — and whoever is transcribing into the
  // client's convention should not be doing the arithmetic by hand. Every
  // option describes the SAME position on WGS 84; see ui/position.ts for why no
  // datum is offered here.
  settings.addRow(
    "Positions",
    segmented<PositionFormat>(
      POSITION_FORMATS.map(([id, label]) => [id, label] as const),
      getPositionFormat,
      setPositionFormat,
      POSITION_FORMATS.map(([, , hint]) => hint).join("\n"),
    ),
  );

  // Password: every account can replace its own (server/password.go). It lives
  // here, in the person's own settings, not in the admin area — it is theirs,
  // not a principal's instrument. The two principals' temporary passwords of
  // 2026-10-06 are the first thing it is for.
  settings.addRow("Password", buildPasswordControl());

  // The coordinate readout doubles as coordinate ENTRY on the console: click,
  // type a position (decimal, hemisphere letters, or DMS), Enter — and the
  // console picks that point exactly as a map click would.
  const coordWrap = el("span", "coordedit");
  const coordInput = document.createElement("input");
  coordInput.type = "text";
  coordInput.className = "coordinput";
  coordInput.placeholder = "27.5, -94.2 · 27°30'N 94°12'W";
  coordInput.spellcheck = false;
  coordInput.hidden = true;
  const coordErr = el("span", "cerr");
  coordWrap.append(coord, coordInput, coordErr);
  if (route === "console") {
    coord.classList.add("editable");
    coord.title = "Click to type a position — decimal, N/S E/W, or degrees-minutes";
    const closeEditor = (): void => {
      coordInput.hidden = true;
      coordInput.classList.remove("err");
      coordErr.textContent = "";
      coord.hidden = false;
    };
    coord.addEventListener("click", () => {
      coord.hidden = true;
      coordInput.hidden = false;
      coordInput.value = "";
      coordInput.focus();
    });
    coordInput.addEventListener("input", () => {
      coordInput.classList.remove("err");
      coordErr.textContent = "";
    });
    coordInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeEditor();
        return;
      }
      if (e.key !== "Enter") return;
      const parsed = parseCoords(coordInput.value);
      if (parsed === null) {
        // A quiet inline refusal: the typed text stays put to be corrected.
        coordInput.classList.add("err");
        coordErr.textContent = "can't read that";
        return;
      }
      closeEditor();
      coordEntry?.(parsed.lat, parsed.lon);
    });
    coordInput.addEventListener("blur", closeEditor);
  }

  cb.append(disc, buildThemeControl(), settings.root, coordWrap, juris, seabed);

  if (state.user !== null) {
    const initials = state.user.name
      .split(/\s+/)
      .map((w) => w[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase();
    const av = el("div", "avatar", initials);
    av.title = `${state.user.name} — sign out`;
    av.style.cursor = "pointer";
    av.addEventListener("click", () => {
      void logout().then(() => {
        void refreshAuth().then(render);
      });
    });
    cb.appendChild(av);
  } else {
    const login = el("a", "loginlink", "Client login");
    login.href = "/login";
    login.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/login");
    });
    cb.appendChild(login);
  }
  head.appendChild(cb);
  root.appendChild(head);
  return { coord, juris, seabed };
}

// --- chart plumbing ---------------------------------------------------------------

function tooltipTitle(times: string[]): (i: number) => string {
  // Through the shared clock (ui/time.ts): labels parse in the site's zone and
  // format in whichever clock the console is set to, labelled UTC when it is.
  return (i) => {
    const label = times[i] ?? "";
    const t = timeOfLabel(label);
    return Number.isFinite(t) ? fmtDayDateHM(t) : label;
  };
}

function hasData(s: ModelSeries): boolean {
  return s.data.some((v) => v !== null && Number.isFinite(v));
}

function anyData(a: (number | null)[] | null | undefined): boolean {
  return a != null && a.some((v) => v !== null && Number.isFinite(v));
}

function envelope(
  models: ModelSeries[],
  n: number,
): { lo: (number | null)[]; hi: (number | null)[] } {
  const lo: (number | null)[] = [];
  const hi: (number | null)[] = [];
  for (let i = 0; i < n; i++) {
    const vals: number[] = [];
    for (const m of models) {
      const v = m.data[i];
      if (v !== null && Number.isFinite(v)) vals.push(v);
    }
    // A spread needs at least two models to measure: a lone reporting model is
    // a line, not a band, so the hour carries no envelope rather than a
    // zero-width one pinned to a single run.
    if (vals.length < 2) {
      lo.push(null);
      hi.push(null);
    } else {
      lo.push(Math.min(...vals));
      hi.push(Math.max(...vals));
    }
  }
  return { lo, hi };
}

function squallWash(fc: PointForecast) {
  const levels = fc.squall?.level ?? [];
  return shadeBands(levels, (lvl) =>
    lvl >= 3
      ? rgba(cssVar("--crit", "#b23b3b"), 0.16)
      : lvl === 2
        ? rgba(cssVar("--warn", "#b9791a"), 0.22)
        : lvl === 1
          ? rgba(cssVar("--warn", "#b9791a"), 0.1)
          : null,
  );
}

// seaCharacterLevels classifies each hour of the swell/wind-sea partition:
//   2 = crossed seas — swell and wind sea from directions > 60° apart while both
//       are running (> 0.3 m): the confused, rolly hazard;
//   1 = wind sea dominates — choppier, locally driven;
//   0 = swell-dominated or calm.
// Ported from the v1 read that this console had dropped.
function seaCharacterLevels(fc: PointForecast): number[] {
  const p = fc.partition;
  if (p === null) return [];
  const at = (a: (number | null)[] | null, i: number): number | null => (a ? a[i] ?? null : null);
  return fc.times.map((_, i) => {
    const sH = at(p.swellH, i) ?? 0;
    const wH = at(p.windSeaH, i) ?? 0;
    const sD = at(p.swellDir, i);
    const wD = at(p.windSeaDir, i);
    const crossed =
      sD !== null &&
      wD !== null &&
      sH > 0.3 &&
      wH > 0.3 &&
      Math.abs(((sD - wD + 540) % 360) - 180) > 60;
    if (crossed) return 2;
    if (wH > sH && wH > 0.3) return 1;
    return 0;
  });
}

// --- console blocks ------------------------------------------------------------------

interface Block {
  root: HTMLElement;
  wrap: HTMLDivElement;
  src: HTMLDivElement;
  sub: HTMLSpanElement;
  chart: Chart | null;
  /** Track blocks only: the live value at the playhead, shown in the header.
   *  Null for ordinary blocks, which have no meter. */
  meter: HTMLSpanElement | null;
  /** The values-at-cursor strip, between the header and the plot. Text that
   *  used to be drawn INSIDE the canvas at the pointer, where it covered the
   *  traces and — once observations pushed the row count past the canvas
   *  height — silently lost its last rows. See chart/readout.ts. */
  readout: HTMLDivElement;
  /** The block's title, for the modal's header. */
  title: string;
  /** The builder mountChart was last given, replayed by the modal to raise a
   *  second chart on the same data without asking anything upstream to rebuild. */
  build: ((c: HTMLCanvasElement) => Chart | null) | null;
  /** Drops the readout's subscription when the chart under it is replaced. */
  readoutUnsub: (() => void) | null;
}

// Collapsible blocks — screen space is scarce on a bridge laptop. The chevron
// header folds a block to its title; the set of folded blocks is remembered.
const COLLAPSE_KEY = "seastate_collapsed_v1";

function collapsedSet(): Set<string> {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(COLLAPSE_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

function makeCollapsible(block: HTMLElement, bh: HTMLElement, key: string): void {
  // A PLATE, NOT A GLYPH. This was a bare "›" at 15px sitting at the end of the
  // header, and the whole header has been clickable the entire time — the
  // control was there, it just did not look like one, so nobody used it. The
  // grey plate is what says "press me"; the chevron on it only says which way.
  // Same reasoning, and deliberately the same visual language, as the splitter
  // grips in ui/grip.ts, one size down.
  const chev = el("span", "chev");
  chev.setAttribute("aria-hidden", "true"); // the state is on the header itself
  const cs = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  cs.setAttribute("viewBox", "0 0 12 12");
  cs.innerHTML =
    '<path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>';
  chev.appendChild(cs);
  bh.appendChild(chev);
  bh.classList.add("clickable");
  // A header that collapses on click but cannot be reached from a keyboard is a
  // control half the bridge does not have — the same argument makeExpandable
  // makes for the plots. aria-expanded is what tells a screen reader the panel
  // is closed rather than empty.
  bh.setAttribute("role", "button");
  bh.tabIndex = 0;
  if (collapsedSet().has(key)) block.classList.add("collapsed");
  const toggle = (): void => {
    block.classList.toggle("collapsed");
    const s = collapsedSet();
    const isCollapsed = block.classList.contains("collapsed");
    if (isCollapsed) s.add(key);
    else s.delete(key);
    bh.setAttribute("aria-expanded", isCollapsed ? "false" : "true");
    try {
      window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...s]));
    } catch {
      /* storage blocked — collapse still works for the session */
    }
  };
  bh.setAttribute("aria-expanded", block.classList.contains("collapsed") ? "false" : "true");
  bh.addEventListener("click", toggle);
  bh.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  });
}

function chartBlock(parent: HTMLElement, title: string, sub: string, help?: HelpKey): Block {
  const b = el("section", "block");
  const bh = el("div", "bh");
  const subEl = el("span", "bsub", sub);
  const bt = el("span", "bt", title);
  // The ⓘ stops propagation, so opening help never toggles the panel collapse.
  if (help !== undefined) bt.appendChild(infoButton(help));
  bh.append(bt, subEl);
  b.appendChild(bh);
  // Title, then the numbers, then the picture, then the provenance. The readout
  // sits ABOVE the plot for the reason Les gave for wanting it moved: read the
  // value, then look at the shape, without one hiding the other.
  const readout = el("div", "creadout");
  readout.hidden = true;
  b.appendChild(readout);
  const wrap = el("div", "chart-wrap");
  b.appendChild(wrap);
  const src = el("div", "bsrc source");
  b.appendChild(src);
  makeCollapsible(b, bh, title);
  parent.appendChild(b);
  const block: Block = {
    root: b, wrap, src, sub: subEl, chart: null, meter: null,
    readout, title, build: null, readoutUnsub: null,
  };
  makeExpandable(block);
  return block;
}

/** A track in the stack: header column on the left carrying the name, the live
 *  value at the playhead and the limit, with the plot beside it.
 *
 *  Same Block shape as chartBlock so every existing caller keeps working — the
 *  difference is layout and the meter. Reading a track is "what shape, and is it
 *  over the line"; the precise number comes from the header, which is why these
 *  can be a third the height of a full chart card. */
function trackBlock(
  parent: HTMLElement,
  title: string,
  sub: string,
  help?: HelpKey,
  /** Give this track the taller plot. For the two that stack several models, a
   *  spread band, a limit and the map field's trace — see .block.track.tall. */
  tall = false,
  /** Key the collapsed state is remembered under. Defaults to the title, which is
   *  right until two blocks share a title — the Workability track and the Site
   *  panel that still holds its controls both answer to "Workability", and one
   *  key would have collapsed both at once. */
  collapseKey = title,
): Block {
  const b = el("section", `block track${tall ? " tall" : ""}`);
  const hdr = el("div", "trackhdr");
  const bh = el("div", "bh");
  const bt = el("span", "bt", title);
  if (help !== undefined) bt.appendChild(infoButton(help));
  bh.appendChild(bt);
  const meter = el("span", "tval", "—");
  const subEl = el("span", "bsub", sub);
  hdr.append(bh, meter, subEl);
  // The plot column, so the readout can sit above the canvas without breaking
  // the two-column grid the header alignment depends on. The header column's
  // meter answers "what is the one number"; this answers "and what were the
  // four models, the buoy and the map field each saying at that hour".
  const plotcol = el("div", "plotcol");
  const readout = el("div", "creadout");
  readout.hidden = true;
  const wrap = el("div", "chart-wrap");
  plotcol.append(readout, wrap);
  const src = el("div", "bsrc source");
  b.append(hdr, plotcol, src);
  makeCollapsible(b, bh, collapseKey);
  parent.appendChild(b);
  const block: Block = {
    root: b, wrap, src, sub: subEl, chart: null, meter,
    readout, title, build: null, readoutUnsub: null,
  };
  makeExpandable(block);
  return block;
}

/** Clicking a plot opens it alone and large (chart/expand.ts).
 *
 *  Wired to the plot, not the whole block: the header already means "collapse",
 *  and one element that collapses on click and enlarges on the same click is a
 *  coin toss. A button role and Enter/Space because a chart you can only reach
 *  with a mouse is a chart half the bridge cannot reach at all. */
function makeExpandable(b: Block): void {
  const open = (): void => {
    if (b.build === null) return; // no chart mounted (a note, or no data)
    openChartModal({
      title: b.title,
      sub: b.sub.textContent ?? "",
      source: b.src.textContent ?? "",
      build: b.build,
      returnFocusTo: b.wrap,
    });
  };
  b.wrap.addEventListener("click", open);
  b.wrap.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  });
}

/** Show or hide the "this opens" affordance. A pointer cursor and a tab stop on
 *  a slot that currently holds the words "No wave data at this point" is an
 *  offer we cannot keep, so the affordance follows the chart, not the block. */
function setExpandable(b: Block, on: boolean): void {
  b.wrap.classList.toggle("expandable", on);
  if (on) {
    b.wrap.tabIndex = 0;
    b.wrap.setAttribute("role", "button");
    b.wrap.setAttribute("aria-label", `Enlarge ${b.title}`);
    return;
  }
  b.wrap.removeAttribute("tabindex");
  b.wrap.removeAttribute("role");
  b.wrap.removeAttribute("aria-label");
}

/** Drive a track's header meter from the shared playhead. Returns an unsubscribe.
 *  `limit` is read per update so changing the operation recolours the meter
 *  without re-registering. */
function bindMeter(
  b: Block,
  times: string[],
  data: (number | null)[],
  unit: string,
  digits: number,
  limit: () => number | null,
): () => void {
  const paint = (): void => {
    const m = b.meter;
    if (m === null) return;
    const i = indexAtTime(times, getCursor());
    const v = i === null ? null : data[i];
    if (v == null || !Number.isFinite(v)) {
      m.textContent = "—";
      m.classList.remove("over");
      return;
    }
    m.textContent = `${v.toFixed(digits)} ${unit}`;
    const lim = limit();
    m.classList.toggle("over", lim !== null && v > lim);
  };
  paint();
  return subscribeCursor(paint);
}

function sourceLine(models: ModelSeries[], extra?: string): string {
  const names = models.filter(hasData).map((m) => m.label);
  return "Source: " + names.join(" · ") + (extra !== undefined ? " · " + extra : "");
}

function resetBlock(b: Block, note: string): void {
  b.readoutUnsub?.();
  b.readoutUnsub = null;
  b.build = null;
  b.chart?.destroy();
  b.chart = null;
  b.readout.replaceChildren();
  b.readout.hidden = true;
  setExpandable(b, false);
  b.wrap.replaceChildren(el("p", "note", note));
  b.src.textContent = "";
}

function mountChart(b: Block, build: (c: HTMLCanvasElement) => Chart | null): void {
  b.readoutUnsub?.();
  b.readoutUnsub = null;
  b.chart?.destroy();
  const c = document.createElement("canvas");
  b.wrap.replaceChildren(c);
  b.chart = build(c);
  // Kept so the modal can raise the same chart again in a bigger canvas without
  // asking anything upstream to refetch or recompute.
  b.build = b.chart !== null ? build : null;
  setExpandable(b, b.chart !== null);
  if (b.chart !== null) b.readoutUnsub = attachReadout(b.chart, b.readout);
  else {
    b.readout.replaceChildren();
    b.readout.hidden = true;
  }
}

// --- workability panel ----------------------------------------------------------------

interface WorkPanel {
  root: HTMLElement;
  render(fc: PointForecast | null): void;
  /** The workability the panel is currently showing, or null when it is showing
   *  a reason instead (no forecast, no vessel, no limits).
   *
   *  The ribbon lives in the track stack and the windows and warnings live here,
   *  but there must only ever be ONE computation behind them: two calls to
   *  `workability()` for the same hour is two go/no-go answers that can drift
   *  apart, which is the worst failure this panel has available to it. So the
   *  panel computes, and everything else reads this. */
  hourly(): WorkRibbonData | null;
  /** Programmatically arm a vessel — the Fleet roster's click. Runs the exact
   *  flow the dropdown runs, so a roster pick and a dropdown pick are
   *  indistinguishable downstream. */
  selectVessel(id: number): void;
  /** The current effective wind limit (kn) for the selected vessel+operation,
   *  or null if none is set — drives the wind chart's limit line and axis scale. */
  windLimitKn(): number | null;
  /** The current effective wave limit (m) — anchors the wave reach-field ramp. */
  waveLimitM(): number | null;
  /** The whole effective limit for the armed vessel+operation, or null when one
   *  of them is unset (or the operation is bespoke and has none yet). The
   *  conditions table colours every row against this one object, so the row
   *  that turns red is the parameter actually binding — which needs the squall
   *  sensitivity and the task length too, not just the two thresholds above. */
  effective(): EffectiveLimit | null;
  /** True when the limits in force came from the preset heuristic rather than a
   *  client limits row. The whole colour ramp hangs off them, so the rail carries
   *  this as a badge — it is a safety fact, not a settings detail. */
  limitsProvisional(): boolean;
  /** The operation the limits are currently drawn from, or null when none is
   *  selected. Read by the feedback panel so a report carries what the operator
   *  was actually looking at. */
  selectedOperation(): string | null;
}

function fmtWindow(times: string[], start: number, end: number, nowIdx: number): string {
  const fmt = (i: number): string => {
    const t = timeOfLabel(times[i] ?? "");
    return Number.isFinite(t) ? fmtDayAmPm(t) : times[i] ?? "";
  };
  const len = end - start;
  if (start <= nowIdx) return `Open now — ${end - nowIdx} h remaining`;
  return `${fmt(start)} → ${fmt(Math.max(start, end - 1))} · ${len} h`;
}

function buildWorkPanel(
  parent: HTMLElement,
  onVessel?: (v: Vessel | null) => void,
  onChange?: () => void,
): WorkPanel {
  const b = el("section", "block");
  const bh = el("div", "bh");
  const wt = el("span", "bt", "Workability");
  wt.appendChild(infoButton("wk.verdict"));
  // The subtitle names what is left in this panel. The hour-by-hour chance is on
  // the Workability track at the head of the stack, not here.
  bh.append(wt, el("span", "bsub", "limits, and the windows that meet them"));
  b.appendChild(bh);

  const row = el("div", "fieldrow");
  const vSel = document.createElement("select");
  vSel.className = "sel";
  const oSel = document.createElement("select");
  oSel.className = "sel";
  row.append(vSel, oSel);
  b.appendChild(row);

  // What is left here after the ribbon moved to the track stack: the controls
  // that choose what is being asked (vessel, operation), the windows that answer
  // it in words, and the provenance of the limits. All of that is settings and
  // prose, not a time series — the hour-by-hour verdict rides the shared axis
  // with the inputs it is computed from.
  const windows = el("div", "windows");
  const warn = el("div", "warnrow");
  const note = el("p", "note");
  b.append(windows, warn, note);
  const src = el("div", "bsrc source",
    "Method: fraction of GEFS members ≤ wind limit × fraction of wave models ≤ wave limit, capped in squall hours. Limits are provisional until set in Vessels.");
  b.appendChild(src);
  makeCollapsible(b, bh, "Workability");
  parent.appendChild(b);

  let lastFc: PointForecast | null = null;
  // The last computed verdict, published through hourly() for the ribbon track
  // and its meter. Cleared on every path through paint() that does not compute
  // one, so a stale ribbon can never outlive the panel's own reading.
  let lastHours: WorkRibbonData | null = null;

  // The effective wind limit for the current vessel+operation, kept current
  // independently of the forecast so the wind chart can read it the moment a
  // point loads (before the first workability is computed).
  let currentWindLimit: number | null = null;
  let currentWaveLimit: number | null = null;
  let currentProvisional = false;
  let currentEffective: EffectiveLimit | null = null;
  function recomputeLimit(): void {
    const v = selVessel();
    const op = selOp();
    if (v === null || op === null) {
      currentWindLimit = null;
      currentWaveLimit = null;
      currentProvisional = false;
      currentEffective = null;
      return;
    }
    const lim = effectiveLimit(v, op, state.registry);
    currentWindLimit = lim === null ? null : lim.wind_kn;
    currentWaveLimit = lim === null ? null : lim.wave_m;
    currentProvisional = lim !== null && lim.isDefault;
    currentEffective = lim;
    // Mirror the pair for the Report page's console snapshot — this function
    // already runs on every selection change and on panel construction.
    lastWorkSelection = { vesselId: v.id, opId: op.id };
  }

  function fillVessels(): void {
    vSel.replaceChildren();
    const opt0 = document.createElement("option");
    opt0.value = "";
    opt0.textContent = "— vessel —";
    vSel.appendChild(opt0);
    for (const v of state.registry.vessels) {
      const o = document.createElement("option");
      o.value = String(v.id);
      o.textContent = v.name;
      vSel.appendChild(o);
    }
  }

  function fillOps(v: Vessel | null): void {
    oSel.replaceChildren();
    const opt0 = document.createElement("option");
    opt0.value = "";
    opt0.textContent = "— operation —";
    oSel.appendChild(opt0);
    const ops = state.registry.operations.filter(
      (op) => v === null || v.operation_ids.includes(op.id),
    );
    for (const op of ops) {
      const o = document.createElement("option");
      o.value = String(op.id);
      o.textContent = op.label;
      oSel.appendChild(o);
    }
  }

  function selVessel(): Vessel | null {
    const id = Number(vSel.value);
    return state.registry.vessels.find((v) => v.id === id) ?? null;
  }
  function selOp(): Operation | null {
    const id = Number(oSel.value);
    return state.registry.operations.find((o) => o.id === id) ?? null;
  }

  function paint(): void {
    const fc = lastFc;
    lastHours = null;
    windows.replaceChildren();
    warn.textContent = "";
    note.textContent = "";
    const v = selVessel();
    const op = selOp();
    if (fc === null) {
      note.textContent = "Waiting for forecast…";
      return;
    }
    if (v === null || op === null) {
      note.textContent = "Pick a vessel and an operation.";
      return;
    }
    const lim = effectiveLimit(v, op, state.registry);
    if (lim === null) {
      note.textContent = "This bespoke operation has no limits yet — set them in Vessels.";
      return;
    }
    const res = workability(fc, {
      wave_m: lim.wave_m,
      wind_kn: lim.wind_kn,
      squall: lim.squall,
      taskHours: lim.task_hours,
    });
    lastHours = { p: res.p, times: fc.times, nowIndex: fc.nowIndex };
    if (res.windows.length === 0) {
      windows.appendChild(
        el("span", "chip warn", `no ${lim.task_hours} h window in the next 7 days`),
      );
    } else {
      for (const w of res.windows.slice(0, 3)) {
        windows.appendChild(el("span", "chip ok", fmtWindow(fc.times, w.start, w.end, fc.nowIndex)));
      }
    }
    if (lim.isDefault) {
      // Attach the ⓘ so an operator can learn exactly what a seeded default means
      // (and why the warning is there) right where the warning appears.
      warn.replaceChildren(
        document.createTextNode("⚠ Using the seeded default limit — set your own in Vessels to clear this."),
        infoButton("wk.default"),
      );
    } else {
      warn.textContent = `Limits: waves ≤ ${fmtLimitLen(lim.wave_m)} · wind ≤ ${lim.wind_kn} kn · task ≥ ${lim.task_hours} h (yours)`;
    }
  }

  fillVessels();
  // default to the first vessel + its first operation so the headline panel
  // always shows a read (the picker refines it, it doesn't gate it)
  const first = state.registry.vessels[0];
  if (first !== undefined) {
    vSel.value = String(first.id);
    fillOps(first);
    const firstOp = state.registry.operations.find((op) => first.operation_ids.includes(op.id));
    if (firstOp !== undefined) oSel.value = String(firstOp.id);
  } else {
    fillOps(null);
  }
  recomputeLimit();
  onVessel?.(selVessel());
  // One flow for "the vessel changed", shared by the dropdown and by
  // programmatic selection (the Fleet roster) — two paths that diverge here
  // would arm different halves of the console.
  const vesselChanged = (): void => {
    fillOps(selVessel());
    onVessel?.(selVessel());
    recomputeLimit();
    paint();
    onChange?.();
  };
  vSel.addEventListener("change", vesselChanged);
  oSel.addEventListener("change", () => {
    recomputeLimit();
    paint();
    onChange?.();
  });

  return {
    root: b,
    render(fc: PointForecast | null): void {
      lastFc = fc;
      recomputeLimit();
      paint();
    },
    selectVessel(id: number): void {
      if (Number(vSel.value) === id) return;
      vSel.value = String(id);
      // A programmatic .value set fires no change event; run the flow directly.
      vesselChanged();
    },
    hourly(): WorkRibbonData | null {
      return lastHours;
    },
    windLimitKn(): number | null {
      return currentWindLimit;
    },
    waveLimitM(): number | null {
      return currentWaveLimit;
    },
    effective(): EffectiveLimit | null {
      return currentEffective;
    },
    limitsProvisional(): boolean {
      return currentProvisional;
    },
    selectedOperation(): string | null {
      return selOp()?.label ?? null;
    },
  };
}

// --- briefing panel ----------------------------------------------------------------------

interface BriefPanel {
  root: HTMLElement;
  render(fc: PointForecast | null, solar: Solar | null, tropHtml?: string): void;
  /** The briefing narrates THE console's vessel — the one Workability selected,
   *  the one the map ramp, the range rings and the reach veil are all keyed to.
   *  It used to carry its own "— vessel —" dropdown, which meant picking the
   *  boat twice and, worse, allowed the prose to describe a different hull than
   *  every other surface was coloured for. One vessel selection, one console. */
  setVessel(v: Vessel | null): void;
}

/** The window's start as an hour a person can say out loud, not "+27 h". */
function fmtWindowStart(fc: PointForecast, startHours: number): string {
  const label = fc.times[fc.nowIndex + startHours];
  if (label === undefined) return `+${startHours} h`;
  const t = timeOfLabel(label);
  return Number.isFinite(t) ? fmtDayHM(t) : label;
}

function buildBriefPanel(parent: HTMLElement): BriefPanel {
  const b = el("section", "block");
  const bh = el("div", "bh");
  const brt = el("span", "bt", "Window briefing");
  brt.appendChild(infoButton("wk.briefing"));
  bh.append(brt, el("span", "bsub", "plain-language read of a time span"));
  b.appendChild(bh);

  const sliders = el("div", "sliders");
  // No Start control. The playhead IS the start — the ruler places it, and this
  // panel reads it. Two separate ways to say "when" is what made the briefing and
  // the timeline disagree: scrub to Thursday and the panel still answered for now.
  const startLab = el("label", "sld startread");
  const startOut = el("output", undefined, "now");
  startLab.append(document.createTextNode("Start "), startOut);
  const lenLab = el("label", "sld");
  const lenOut = el("output", undefined, "12 h");
  const len = document.createElement("input");
  len.type = "range";
  len.min = "6";
  len.max = "72";
  len.step = "3";
  len.value = "12";
  lenLab.append(document.createTextNode("Length "), lenOut, len);
  sliders.append(startLab, lenLab);
  b.appendChild(sliders);

  const out = el("div", "brief");
  b.appendChild(out);
  makeCollapsible(b, bh, "Window briefing");
  parent.appendChild(b);

  let lastFc: PointForecast | null = null;
  let lastSolar: Solar | null = null;
  let lastTrop = ""; // tropical callout for the current point, if any
  let vessel: Vessel | null = null; // follows the Workability selection via setVessel

  // What the last painted briefing was FOR. The prose is a function of the
  // window (start hour, length) and the hull — not of the cursor's exact
  // millisecond — so a scrub that stays inside the same hour must not rebuild it.
  let painted: string | null = null;

  function paint(): void {
    const fc = lastFc;
    if (fc === null) {
      out.replaceChildren(el("p", "note", "Waiting for forecast…"));
      painted = null;
      return;
    }
    // Hours from now to the playhead. Null cursor, or a cursor in the past, both
    // mean "from now" — a briefing about a window that already closed is not a
    // briefing, and the forecast has no skill to offer behind us anyway.
    const cur = getCursor();
    const sh = cur === null ? 0 : Math.max(0, Math.round((cur - Date.now()) / 3_600_000));
    // buildBriefing walks every model series across the window and formats dates
    // through Intl; out.innerHTML then reparses the whole subtree. That is far too
    // much to run per pointermove — dragging the ruler fires them faster than the
    // hour ever changes, and playback drives ten a second. The window is quantised
    // to whole hours, so the overwhelming majority of those calls would rebuild the
    // panel into byte-identical HTML. Skip them.
    const key = `${sh}|${len.value}|${vessel?.id ?? ""}`;
    if (key === painted) return;
    painted = key;
    startOut.textContent = sh === 0 ? "now" : fmtWindowStart(fc, sh);
    lenOut.textContent = `${len.value} h`;
    out.innerHTML =
      lastTrop +
      buildBriefing({
        fc,
        solar: lastSolar,
        vessel,
        startHours: sh,
        lengthHours: Number(len.value),
      });
  }

  /** Repaint on the next frame. `force` is for the inputs the key cannot see —
   *  a new forecast, solar or tropical callout for the same window. */
  let briefRaf = 0;
  function repaint(force = false): void {
    if (force) painted = null;
    if (briefRaf !== 0) return;
    briefRaf = requestAnimationFrame(() => {
      briefRaf = 0;
      paint();
    });
  }

  len.addEventListener("input", () => {
    setWindowHours(Number(len.value)); // so the ruler can draw the same span
    repaint();
  });
  // Scrubbing moves the window's start, so the briefing follows the playhead.
  const unsubBriefCursor = subscribeCursor(() => repaint());
  setWindowHours(Number(len.value));

  void unsubBriefCursor; // panel lives for the console's lifetime

  return {
    root: b,
    render(fc: PointForecast | null, solar: Solar | null, tropHtml = ""): void {
      lastFc = fc;
      lastSolar = solar;
      lastTrop = tropHtml;
      repaint(true); // new forecast/solar/tropical: the key alone cannot see these
    },
    setVessel(v: Vessel | null): void {
      if (v?.id === vessel?.id) return;
      vessel = v;
      repaint(true);
    },
  };
}

// --- console page ---------------------------------------------------------------------------

let activeMap: SeaMap | null = null;

// The sidebar width lives in the --col2-w CSS var on .appbody; the gutter drags
// it. Persisted so a bridge laptop keeps its layout across sessions.
const COL2_WIDTH_KEY = "seastate_col2_w_v1";

// Column floors. The drag clamps and reclampColumns all defend MAP_MIN_W,
// which must match the minmax() floor on the map track in style.css.
const COL2_MIN_W = 320;
const WSPANEL_MIN_W = 240; // narrower and the fleet chips / systems detail lines wrap to mush
const MAP_MIN_W = 280;
const GUTTER_W = 7; // each splitter track in the .appbody template

function savedWidth(key: string): number | null {
  try {
    const v = Number(window.localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null; // storage blocked — resizing still works for the session
  }
}

/** The workspace panel's width as a grid column — 0 when it is closed
 *  ([hidden]) or floating as a drawer (≤1180px, position: absolute), because
 *  then it occupies no track and the map's space doesn't pay for it. */
function wsTrackWidth(body: HTMLElement): number {
  const panel = body.querySelector(".wspanel");
  if (!(panel instanceof HTMLElement) || panel.hidden) return 0;
  if (window.getComputedStyle(panel).position === "absolute") return 0;
  return panel.getBoundingClientRect().width;
}

function clampCol2Width(body: HTMLElement, w: number): number {
  const wsW = wsTrackWidth(body);
  const gutters = (wsW > 0 ? 2 : 1) * GUTTER_W;
  const max = window.innerWidth - (rail0Width(body) ?? 74) - wsW - gutters - MAP_MIN_W;
  return Math.max(COL2_MIN_W, Math.min(max, w));
}

/** One layout pass over both column vars: re-derive each from its saved
 *  preference (or the CSS default), jointly clamped so the map track keeps
 *  MAP_MIN_W — the sidebar gives up width first, the workspace panel second.
 *  Runs at mount, on panel toggle and on window resize, so a layout saved on a
 *  wide monitor and restored onto a half-width window degrades by narrowing
 *  columns, never by collapsing the map. */
function reclampColumns(body: HTMLElement): void {
  // A folded forecast column has a width of zero by decision, not by squeeze.
  if (body.classList.contains("col2closed")) return;
  const gutter = body.querySelector(".gutter:not(.lgutter)");
  // Single-column stack (≤880px): the columns aren't tracks at all.
  if (!(gutter instanceof HTMLElement) || window.getComputedStyle(gutter).display === "none") return;
  const vw = window.innerWidth;
  const railW = rail0Width(body) ?? 74;
  const wsInFlow = wsTrackWidth(body) > 0;
  const savedCol2 = savedWidth(COL2_WIDTH_KEY);
  const savedWs = savedWidth(WSPANEL_WIDTH_KEY);
  // Preferred widths; the fallbacks mirror the CSS defaults for --col2-w
  // (clamp(380px, 34vw, 560px)) and --wspanel-w (300px).
  let col2 = Math.max(COL2_MIN_W, savedCol2 ?? Math.min(560, Math.max(380, vw * 0.34)));
  let ws = wsInFlow ? Math.max(WSPANEL_MIN_W, Math.min(savedWs ?? 300, Math.floor(vw * 0.4))) : 0;
  let over = railW + ws + (wsInFlow ? 2 : 1) * GUTTER_W + MAP_MIN_W + col2 - vw;
  const squeezed = over > 0;
  if (over > 0) {
    const give = Math.min(over, col2 - COL2_MIN_W);
    col2 -= give;
    over -= give;
  }
  if (over > 0 && wsInFlow) ws = Math.max(WSPANEL_MIN_W, ws - over);
  // Pin a pixel width only where something demands it — a saved preference or
  // an active squeeze. Otherwise clear it, so the CSS defaults stay responsive.
  if (savedCol2 !== null || squeezed) body.style.setProperty("--col2-w", `${Math.round(col2)}px`);
  else body.style.removeProperty("--col2-w");
  if (wsInFlow) {
    if (savedWs !== null || squeezed) body.style.setProperty("--wspanel-w", `${Math.round(ws)}px`);
    else body.style.removeProperty("--wspanel-w");
  }
}

/** Whether the forecast column is folded away. */
const COL2_CLOSED_KEY = "seastate_col2_closed_v1";

/* The grip itself lives in ui/grip.ts — the conditions table folds with the
   same control, and two sizes of the same idea is how the small chevrons got
   unfindable in the first place. What stays here is what folding MEANS for a
   column: which track gives up its width, and what has to be told the box
   changed. */

/** Fold or unfold the forecast column. */
function setCol2Closed(body: HTMLElement, closed: boolean): void {
  body.classList.toggle("col2closed", closed);
  // An inline width beats the class rule, and reclampColumns writes one, so the
  // collapsed state is asserted here rather than left to the stylesheet to win.
  if (closed) body.style.setProperty("--col2-w", "0px");
  else {
    body.style.removeProperty("--col2-w");
    reclampColumns(body);
  }
  try {
    if (closed) window.localStorage.setItem(COL2_CLOSED_KEY, "1");
    else window.localStorage.removeItem(COL2_CLOSED_KEY);
  } catch {
    /* storage blocked — the fold still holds for this session */
  }
  activeMap?.raw().resize();
}

function col2ClosedPref(): boolean {
  try {
    return window.localStorage.getItem(COL2_CLOSED_KEY) === "1";
  } catch {
    return false;
  }
}

// setupGutter wires the draggable splitter: pointer drag resizes the sidebar (and
// resizes the map so its WebGL canvas keeps up); double-click resets to default.
// The initial restore of a saved width happens in reclampColumns, not here.
function setupGutter(body: HTMLElement, gutter: HTMLElement): void {
  let lastW = 0;
  gutter.addEventListener("pointerdown", (e: PointerEvent) => {
    e.preventDefault();
    gutter.classList.add("dragging");
    gutter.setPointerCapture(e.pointerId);
  });
  gutter.addEventListener("pointermove", (e: PointerEvent) => {
    if (!gutter.hasPointerCapture(e.pointerId)) return;
    // Folded away, the splitter is only a handle for the grip. Dragging a column
    // that is not there would set a width the moment it comes back, silently.
    if (body.classList.contains("col2closed")) return;
    const rect = body.getBoundingClientRect();
    lastW = clampCol2Width(body, rect.right - e.clientX);
    body.style.setProperty("--col2-w", `${lastW}px`);
    activeMap?.raw().resize();
  });
  const end = (e: PointerEvent): void => {
    if (!gutter.hasPointerCapture(e.pointerId)) return;
    gutter.releasePointerCapture(e.pointerId);
    gutter.classList.remove("dragging");
    activeMap?.raw().resize();
    if (lastW > 0) {
      try {
        window.localStorage.setItem(COL2_WIDTH_KEY, String(Math.round(lastW)));
      } catch {
        /* storage blocked */
      }
    }
  };
  gutter.addEventListener("pointerup", end);
  gutter.addEventListener("pointercancel", end);
  gutter.addEventListener("dblclick", () => {
    if (body.classList.contains("col2closed")) return;
    body.style.removeProperty("--col2-w");
    try {
      window.localStorage.removeItem(COL2_WIDTH_KEY);
    } catch {
      /* storage blocked */
    }
    activeMap?.raw().resize();
  });

  const startFolded = col2ClosedPref();
  const grip = createGrip({
    hideLabel: "Hide the forecast column",
    showLabel: "Show the forecast column",
    folded: startFolded,
    onToggle: (folded) => setCol2Closed(body, folded),
  });
  if (startFolded) setCol2Closed(body, true);
  gutter.appendChild(grip.root);
}

// The workspace panel's width, same contract as the right column: persisted,
// clamped, double-click to reset. Width is measured from the panel's LEFT edge
// (the rail's right edge) to the pointer, because the panel grows rightward.
const WSPANEL_WIDTH_KEY = "seastate_wspanel_w_v1";

function clampWsWidth(body: HTMLElement, w: number): number {
  const col = body.querySelector(".col2");
  const colW = col instanceof HTMLElement ? col.getBoundingClientRect().width : COL2_MIN_W;
  const max = Math.min(
    Math.floor(window.innerWidth * 0.4), // the map stays the protagonist
    window.innerWidth - (rail0Width(body) ?? 74) - 2 * GUTTER_W - MAP_MIN_W - colW,
  );
  return Math.max(WSPANEL_MIN_W, Math.min(max, w));
}

// Initial restore of a saved width happens in reclampColumns, not here.
function setupLeftGutter(body: HTMLElement, gutter: HTMLElement, onClose: () => void): void {
  let lastW = 0;
  gutter.addEventListener("pointerdown", (e: PointerEvent) => {
    e.preventDefault();
    gutter.classList.add("dragging");
    gutter.setPointerCapture(e.pointerId);
  });
  gutter.addEventListener("pointermove", (e: PointerEvent) => {
    if (!gutter.hasPointerCapture(e.pointerId)) return;
    // Pointer x minus the gutter's own left edge is the panel-width DELTA from
    // its current width; anchor on the panel's left edge instead so the panel
    // tracks the pointer absolutely, like the right gutter does.
    const railW = body.getBoundingClientRect().left + (rail0Width(body) ?? 74);
    lastW = clampWsWidth(body, e.clientX - railW);
    body.style.setProperty("--wspanel-w", `${lastW}px`);
    activeMap?.raw().resize();
  });
  const end = (e: PointerEvent): void => {
    if (!gutter.hasPointerCapture(e.pointerId)) return;
    gutter.releasePointerCapture(e.pointerId);
    gutter.classList.remove("dragging");
    activeMap?.raw().resize();
    if (lastW > 0) {
      try {
        window.localStorage.setItem(WSPANEL_WIDTH_KEY, String(Math.round(lastW)));
      } catch {
        /* storage blocked */
      }
    }
  };
  gutter.addEventListener("pointerup", end);
  gutter.addEventListener("pointercancel", end);
  gutter.addEventListener("dblclick", () => {
    body.style.removeProperty("--wspanel-w");
    try {
      window.localStorage.removeItem(WSPANEL_WIDTH_KEY);
    } catch {
      /* storage blocked */
    }
    activeMap?.raw().resize();
  });

  // Close only. This grip goes with the panel when it folds, and that is fine:
  // the rail beside it is a column of labelled buttons that reopens whichever
  // workspace you were in. Two doors back would be one more than the layout can
  // spare on the map's left edge.
  const grip: Grip = createGrip({
    hideLabel: "Hide this panel",
    showLabel: "Hide this panel",
    onToggle: () => {
      onClose();
      // This grip only ever closes, so it must not remember having been used:
      // the panel comes back through the rail, taking its splitter and this
      // control with it, and a grip that returned still labelled "show" would
      // be describing a state it is no longer in.
      grip.setFolded(false);
    },
  });
  grip.root.classList.add("gutgrip-left");
  gutter.appendChild(grip.root);
}

/** The rail's rendered width — read live rather than assumed, so a future rail
 *  width change cannot silently skew the drag anchor. */
function rail0Width(body: HTMLElement): number | null {
  const rail = body.querySelector(".rail");
  return rail instanceof HTMLElement ? rail.getBoundingClientRect().width : null;
}


function renderConsole(root: HTMLElement, header: Header): void {
  const body = el("div", "appbody");
  // Declared here, assigned after the body is built: the rail's onSelect closes
  // over it and the rail is constructed first.
  let mtabs: MobileTabs | null = null;
  const mapContainer = el("div", "map");
  const gutter = el("div", "gutter");
  gutter.title = "Drag to resize · double-click to reset";
  const lgutter = el("div", "gutter lgutter");
  lgutter.title = "Drag to resize · double-click to reset";
  const col = el("aside", "col2");

  // The workspace rail. Everything that is *not* a forecast track now lives in an
  // exclusive workspace beside it, which is what empties the column: the stack
  // reads as one instrument because the primer, briefing, SVP and validation
  // panels no longer sit between the charts.
  //
  // Theme and account are deliberately NOT in the rail even though the design
  // sketch grouped them there — this app already carries both in the header, and
  // a second copy of a control is worse than a control in the wrong place.
  // Methods is an action rather than a workspace for the same reason: the
  // /methods page and the per-track ⓘ popovers already hold that prose.
  const rail = createRail({
    items: [
      { id: "waypoints", label: "Waypoints", icon: "theater", title: "Waypoints" },
      { id: "site", label: "Site", icon: "site" },
      { id: "svp", label: "SVP", icon: "svp", title: "Sound Velocity Profile" },
      ...(OPS_PANELS
        ? [
            { id: "seep", label: "Seep", icon: "seep" as const, title: "Seep hunting — station context" },
            { id: "ebs", label: "EBS", icon: "ebs" as const, title: "Environmental Baseline Survey — cast context" },
          ]
        : []),
      { id: "fleet", label: "Fleet", icon: "fleet" },
      { id: "obs", label: "Observations", icon: "observations", title: "Observations" },
      { id: "tropical", label: "Tropical", icon: "tropical" },
      { id: "systems", label: "Systems", icon: "systems", title: "Data-source health" },
      { id: "feedback", label: "Feedback", icon: "feedback", title: "Tell us what is wrong or missing" },
      {
        id: "methods",
        label: "Methods",
        icon: "methods",
        kind: "action",
        bottom: true,
        title: "Methods & limitations",
        onAction: () => navigate("/methods"),
      },
    ],
    initial: "site",
    // On a phone the rail and its panel live in the Panels tab, but a workspace
    // can also be opened from elsewhere — a storm card, a buoy. Switching tabs
    // with it means that never lands on a panel behind another tab, which would
    // read as a dead tap. Safe despite mtabs being built further down: onSelect
    // is documented as not firing for the initial selection, so this cannot run
    // during construction.
    onSelect: () => {
      if (window.matchMedia("(max-width: 880px)").matches) mtabs?.show("panels");
    },
    // Closing the panel gives its 300 px back to the map. MapLibre sizes its
    // canvas from the container, so it has to be told the box changed.
    onPanelToggle: (open) => {
      body.classList.toggle("wsclosed", !open);
      reclampColumns(body); // the panel's track appeared or vanished — re-share the width
      activeMap?.raw().resize();
    },
  });
  body.append(rail.rail, rail.panel, lgutter, mapContainer, gutter, col);
  // The conditions table takes the shell's SECOND grid row, spanning the map,
  // its splitter and the track column. It is the one surface here that is a
  // table rather than an instrument — every cell prints its number — so it sits
  // across the bottom of the whole content area rather than inside a column.
  // Appended last so it is last in the tab order too; the grid area, not the
  // DOM order, is what puts it under the map. Hidden until a point is picked;
  // folding it is remembered per user, and the map is told its box changed
  // because MapLibre sizes its canvas from the container.
  const conditions: ConditionsTable = createConditionsTable(body);
  new ResizeObserver(() => activeMap?.raw().resize()).observe(mapContainer);
  root.appendChild(body);
  // The phone's bottom tab bar. Built on every console render and appended
  // AFTER the body, so it is the last flex child of #app and cannot overlap the
  // surface above it; the stylesheet hides it outright above 880px, where the
  // four surfaces fit side by side and there is nothing to switch between.
  mtabs = createMobileTabs(body);
  root.appendChild(mtabs.root);
  // Marks the shell as tab-driven so the stylesheet can give it a definite
  // height. Set here rather than in CSS because it must be true only when the
  // bar was really built — the stacked fallback must not be clipped by a height
  // it never asked for.
  root.classList.add("mtabbed");
  setupGutter(body, gutter);
  setupLeftGutter(body, lgutter, () => rail.setPanelOpen(false));
  reclampColumns(body); // restore saved column widths, jointly clamped to this viewport
  // Re-clamp when the window itself resizes: shrinking gives up column width,
  // never the map. Self-removing — a route change replaces the whole body, and
  // a listener holding a detached body would re-layout a console that is gone.
  let reclampRaf = 0;
  const onWindowResize = (): void => {
    if (!body.isConnected) {
      window.removeEventListener("resize", onWindowResize);
      return;
    }
    if (reclampRaf !== 0) return;
    reclampRaf = window.requestAnimationFrame(() => {
      reclampRaf = 0;
      reclampColumns(body);
      activeMap?.raw().resize();
    });
  };
  window.addEventListener("resize", onWindowResize);

  const wsTheater = rail.workspace("waypoints");
  const wsSite = rail.workspace("site");
  const wsSvp = rail.workspace("svp");
  // rail.workspace() throws for an id with no item, so these follow the flag.
  const wsSeep = OPS_PANELS ? rail.workspace("seep") : null;
  const wsEbs = OPS_PANELS ? rail.workspace("ebs") : null;
  const wsFleet = rail.workspace("fleet");
  const wsObs = rail.workspace("obs");
  const wsTropical = rail.workspace("tropical");
  const wsSystems = rail.workspace("systems");
  const wsFeedback = rail.workspace("feedback");
  // Claimed up front so the layer list heads the Observations workspace, even
  // though the control itself can only be built once the map style is ready.
  const layersHost = el("div", "wslayers");
  wsObs.appendChild(layersHost);

  // Freshness stamp: when this view's forecast was retrieved. Open-Meteo doesn't
  // expose the model run cycle, so we stamp the retrieval time — the honest signal
  // of how stale a left-open console has become. Reset on each point load.
  const freshness = el("div", "freshness");
  freshness.hidden = true;
  col.appendChild(freshness);

  // land notice — shown instead of the marine panels when the point is dry
  const landNote = el("section", "block landnote");
  landNote.appendChild(el("p", "note", ""));
  landNote.style.display = "none";
  col.appendChild(landNote);

  // Workability leads — it's the decision the console exists to make. Vessel
  // selection also drives the vessel's operational range rings around the current
  // point. It no longer touches the ports layer: that layer shows the World Port
  // Index record and grades nothing, so there is no vessel in it to set.
  let selectedVessel: Vessel | null = null;
  /** Forwards the console's vessel to the briefing panel. A late-bound hook, not
   *  a direct call: the work panel fires its onVessel during construction, before
   *  the briefing block exists — the same TDZ trap the rail's initial select once
   *  hit. Bound to brief.setVessel the moment the panel is built. */
  let briefVessel: (v: Vessel | null) => void = () => {};
  let mapReady = false;
  let rangeOn = true; // "Operational range" overlay toggle
  let curLat: number | null = null;
  let curLon: number | null = null;
  // What the textual panels were last rendered FROM, held so a clock flip
  // (Local/UTC) can re-render them in place without refetching anything.
  let lastFcHeld: PointForecast | null = null;
  let lastSolarHeld: Solar | null = null;
  let lastTropHeld = "";
  // When this view's forecast was retrieved (epoch ms), or null when the last
  // load failed — the freshness line re-formats from this on a clock flip.
  let freshAt: number | null = null;
  let repaintFeedback: () => void = () => {};
  // The confirmed operating area. Declared up here with the rest of the point
  // state because syncWindField (assigned during the work panel's construction)
  // reads it, and a `let` further down would be in its temporal dead zone.
  /** The area currently framed — a preset, a saved one, or one just saved. Null
   *  means "no area framed", which is a perfectly good state: the map is free and
   *  the field is painted regardless. */
  let framed: FramedWaypoint | null = null;
  let rungId: RungId = DEFAULT_RUNG;
  let showCells = false;
  // THE SEAM, left deliberately empty. A vessel's draft used to be pushed into the
  // ports layer here (`setPortVessel`) to recolour every harbour green/amber/red
  // against it. That verdict is withdrawn — see the header of map/overlays.ts and
  // docs/PORTS.md — so vessel selection and the ports layer are now unconnected.
  // If a fit is ever reinstated, this is where the selection would be forwarded
  // from, and the layer side of the seam is documented above PORT_LAYERS.
  //
  // Range rings: concentric 1/2/3-day reaches at the vessel's cruise speed,
  // centered on the clicked point. Drawn only once the map style is ready and a
  // vessel with a known cruise speed is selected; otherwise cleared.
  const updateRange = (): void => {
    const mm = activeMap;
    if (mm === null || !mapReady) return;
    const cruise = selectedVessel?.cruise_kn ?? 0;
    if (rangeOn && selectedVessel !== null && cruise > 0 && curLat !== null && curLon !== null) {
      setVesselRange(mm.raw(), curLat, curLon, cruise);
    } else {
      clearVesselRange(mm.raw());
    }
    updateReachVeil();
  };
  /**
   * The reach veil: full-colour field inside the vessel's 3-day steaming reach,
   * washed out beyond it. Keyed to the SAME cruise x 72 h the outermost range
   * ring draws, so the ring is the veil's labelled edge and the two can never
   * disagree about where "yours" ends.
   *
   * Deliberately NOT gated on rangeOn: the rings are an overlay you can switch
   * off, but the veil is part of how the field reads — with rings hidden the
   * feathered wash still says "this far", just without the line. It IS gated on
   * the field being on (a bare basemap must not sit dimmed for a layer that is
   * gone) and on a vessel with a known cruise speed — with no vessel there is
   * no "yours", and the field paints undimmed, which is the honest default.
   */
  const updateReachVeil = (): void => {
    const mm = activeMap;
    if (mm === null || !mapReady) return;
    const cruise = selectedVessel?.cruise_kn ?? 0;
    const fieldOn = (windControl?.kind() ?? null) !== null;
    if (fieldOn && cruise > 0 && curLat !== null && curLon !== null) {
      setReachVeil(mm.raw(), curLat, curLon, cruise * 72);
    } else {
      clearReachVeil(mm.raw());
    }
  };
  // Repaints the wind chart in place (limit line + axis) when the operation
  // changes. Assigned each time a point loads; null until the first load.
  // Meter subscriptions, torn down and rebuilt on each point load so a stale
  // track never keeps painting a previous location's numbers.
  let meterUnsubs: (() => void)[] = [];
  const clearMeters = (): void => {
    for (const off of meterUnsubs) off();
    meterUnsubs = [];
  };
  let redrawWind: (() => void) | null = null;
  let redrawWave: (() => void) | null = null;
  // This month's seasonal normal at the current point, for the band behind the
  // wind and wave tracks. Held rather than awaited: it comes from the primer's
  // ERA5 fetch (six archive requests), and the charts must not wait on it — they
  // draw from the forecast alone and repaint through redrawWind/redrawWave when
  // it lands, exactly as the Surface-current track does for drifters. Cleared on
  // every load so a new point can never be judged against the last one's normal.
  let curClim: ClimFact | null = null;
  // Repaints the Surface-current track. Hoisted like redrawWind because the drifter
  // fetch is slow (a 15-25 s OSMC scan) and must not block the chart: the chart draws
  // first from the model alone, and this repaints it with the observed drifter dots
  // the moment they land.
  let redrawCurrent: (() => void) | null = null;
  // Drifters near the CURRENT site, for the current-track dots. Refreshed per load by
  // the drifter fetch/reuse below; read by redrawCurrent.
  let curDrifters: Drifter[] = [];
  // Repaints the workability track — the ribbon and its header meter — from the
  // work panel's own computation. A reassignable placeholder because the panel is
  // built before the stack it feeds, and its onVessel callback fires during that
  // construction.
  let redrawWorkTrack: () => void = () => {};
  // Re-reads the work panel's limit provenance and repaints the Fleet badge.
  // Assigned when the Fleet workspace is built, which is after the work panel
  // exists — hence the no-op placeholder rather than a null check at every call.
  let limitBadgeSync: () => void = () => {};
  /** Re-lists NHC systems and re-keys the tropical rail badge to the current
   *  site. Assigned where the Tropical workspace is built; called when the NHC
   *  fetch lands and whenever the site moves. */
  let syncTropical: () => void = () => {};
  // Operational-reach wind field: fed the point + vessel + wind limit; it owns the
  // fetch, the limit-anchored colouring and the hourly scrubber. `syncWindField` is a
  // reassignable placeholder so the work panel's initial onVessel call (which fires
  // during construction, before `work` exists) is a safe no-op.
  let windControl: WindControl | null = null;
  let syncWindField: () => void = () => {};
  const work = buildWorkPanel(
    wsSite,
    (v) => {
      selectedVessel = v;
      updateRange();
      syncWindField();
      limitBadgeSync();
      briefVessel(v); // one boat everywhere: the briefing narrates this vessel too
    },
    () => {
      redrawWind?.();
      redrawWave?.();
      redrawWorkTrack();
      syncWindField();
      limitBadgeSync();
    },
  );
  syncWindField = (): void => {
    // The map paints the WINDOW mesh — the viewport plus a margin, at the finest
    // grid the cell budget affords there. Repainting only, no fetching —
    // ensureWindowMesh owns that.
    windControl?.update(activeMap?.raw() ?? null, {
      mesh: windowMesh,
      windLimitKn: work.windLimitKn(),
      waveLimitM: work.waveLimitM(),
    });
  };
  // Every chart in the column shares this left margin so the shared playhead
  // lands at the same screen x on all of them. Measured per chart, "10 m",
  // "1200" and "0.5 kn" would each set a different plot origin and the crosshair
  // would step sideways as your eye moved down the stack. Wide enough for the
  // widest label the column actually produces (visibility in metres).
  const STACK_GUTTER = 46;

/**
 * The width:height of a framed's geographic extent — a property of the THEATER,
 * not of whoever's browser window is open.
 *
 * It is tempting to widen the box to the viewport so the framed exactly fills the
 * map. That would make the fetched extent depend on window shape, and the whole
 * value of quantising a framed is that two people working the same block send the
 * same request and share the same cached tiles. A dragged window divider would
 * silently fragment that cache.
 *
 * So the box is fixed and the viewport simply looks at it: fitTheater sizes the
 * view to the box, and any leftover map around it is honest margin — the frame is
 * drawn, so the operating area's edge is never in doubt. Landscape-ish because
 * every rung is wider than it is tall on screen.
 */
const THEATER_ASPECT = 1.5;

/** The rung whose span is closest to `nm`. The rungs are view presets now, not a
 *  cage, so an area's preferred width just picks the nearest one. */
function rungForSpan(nm: number): RungId {
  let best: RungId = DEFAULT_RUNG;
  let bestGap = Infinity;
  for (const r of RUNGS) {
    const gap = Math.abs(r.spanNm - nm);
    if (gap < bestGap) {
      bestGap = gap;
      best = r.id;
    }
  }
  return best;
}

  // The track stack: every forecast chart, contiguous, under ONE time ruler.
  // Contiguity is the point — the briefing, primer and validation panels used to
  // sit between the charts, which is why each needed its own date strip. With
  // them moved below, the stack reads as one instrument and the ruler heads it.
  const stack = el("div", "trackstack");
  // Transport heads the stack: it drives the shared cursor, which every track and
  // the map field already follow. It is deliberately NOT in the rail — the rail is
  // space and mode, the stack's own bar is time.
  const transport = createTransport(stack);
  const rulerRow = el("div", "rulerrow");
  rulerRow.appendChild(el("span", "stubnm", "Timeline"));
  const rulerCanvas = document.createElement("canvas");
  rulerRow.appendChild(rulerCanvas);
  stack.appendChild(rulerRow);
  col.appendChild(stack);
  let ruler: TimeRuler | null = null;
  // The confidence strip rides directly under the ruler, above the first track:
  // it describes the x axis, so it belongs to the axis, not to any one series.
  const confRow = el("div", "confrow");
  confRow.appendChild(el("span", "stubnm", "Confidence"));
  stack.appendChild(confRow);
  const confBar = createConfBar(confRow, { gutterLeft: STACK_GUTTER });
  /** Point the shared ruler at a forecast's hours. */
  const setRulerTimes = (times: string[] | null): void => {
    confBar.update(times);
    transport.update(times);
    if (times === null || times.length === 0) {
      rulerRow.style.display = "none";
      return;
    }
    rulerRow.style.display = "";
    if (ruler === null) ruler = createTimeRuler(rulerCanvas, { xLabels: times, gutterLeft: STACK_GUTTER });
    else ruler.update(times);
  };
  setRulerTimes(null);

  // Workability heads the tracks, directly under the confidence strip. Everything
  // below it — wind, waves, period, light — is an INPUT to it, so putting the
  // verdict first means "why is Thursday no-go?" is answered by reading straight
  // down one column instead of across two unaligned time axes.
  const workTrack = trackBlock(
    stack,
    "Workability",
    "chance limits are met",
    "wk.verdict",
    false,
    "Workability track",
  );
  workTrack.root.classList.add("ribbontrack");
  const workCanvas = document.createElement("canvas");
  workTrack.wrap.replaceChildren(workCanvas);
  const workRibbon = createWorkRibbon(workCanvas, { gutterLeft: STACK_GUTTER });
  // The key travels with the ribbon. Colours stranded from their legend are worse
  // than either alone, and the legend is generated from the same band table the
  // ribbon paints from, so a threshold can only be changed in one place.
  const workKey = el("div", "ribbon-key");
  workKey.innerHTML = WORK_GRADES.map((g, i) => {
    // The bottom band is described by the band above it: "No-go < 30%", not
    // "No-go ≥ 0%", which is true but says nothing.
    const bound =
      i === WORK_GRADES.length - 1
        ? `&lt; ${Math.round(WORK_GRADES[i - 1].min * 100)}%`
        : `≥ ${Math.round(g.min * 100)}%`;
    return `<span><i style="background:var(${g.varName})"></i>${g.label} ${bound}</span>`;
  }).join("");
  workTrack.src.appendChild(workKey);
  // The meter deliberately does NOT use bindMeter: that helper reddens a value
  // that EXCEEDS its limit, and workability is alarming when it is LOW. It also
  // reads the panel's stored result rather than any series of its own, so the
  // number in the header is by construction the same number under the ribbon.
  // The verdict describes the WHOLE work window, graded on its worst hour.
  //
  // It used to grade the single hour under the playhead, which was defensible when
  // the playhead was just a hover position. It stopped being defensible once the
  // window became a real span you place and size: a header reading "Go" over a
  // ribbon with red inside the bookends is answering a question nobody asked. A
  // twelve-hour job that starts in clear water and hits a no-go hour at hour seven
  // is not a Go — the crew is already on deck when it breaks. The worst moment is
  // the binding constraint, so the worst moment is the verdict.
  //
  // Naming the hour is what makes it actionable, and it is also what keeps the
  // number honest: "62% · Marginal · worst Thu 04:00" is self-evidently about that
  // one hour. A bare window-wide percentage would invite reading it as "62% chance
  // the job completes", which is a joint probability over correlated hours that
  // this console does not compute and could not defend.
  //
  // Hours with no data are UNKNOWN, never clear: a window running past the
  // forecast's end cannot report Go. The header stays short (it is a narrow
  // column); the caveat rides in the hover title, the same division the squall
  // flag below uses.
  const paintWorkMeter = (): void => {
    const m = workTrack.meter;
    if (m === null) return;
    for (const g of WORK_GRADES) m.classList.remove(g.cls);
    m.removeAttribute("title");
    const hours = work.hourly();
    const cur = getCursor();
    const start = hours === null ? null : indexAtTime(hours.times, cur);
    if (hours === null || start === null || cur === null) {
      m.textContent = "—";
      return;
    }

    // The window's closing edge, found by INSTANT rather than by adding an index
    // offset — the same way the ruler draws its span, so the two cannot disagree
    // about where the window ends. A null means it runs off the end of the series.
    const span = Math.max(1, getWindowHours());
    const endT = cur + span * 3_600_000;
    const endIdx = indexAtTime(hours.times, endT);
    const last = hours.times.length - 1;
    const stop = Math.min(endIdx ?? last, last);
    const truncated = endIdx === null;

    let worst: number | null = null;
    let worstIdx = -1;
    let missing = 0;
    for (let i = start; i <= stop; i++) {
      const p = hours.p[i];
      if (p == null || !Number.isFinite(p)) {
        missing++;
        continue;
      }
      if (worst === null || p < worst) {
        worst = p;
        worstIdx = i;
      }
    }

    if (worst === null) {
      m.textContent = "—";
      m.title = "No workability data in this window.";
      return;
    }

    const grade = workGrade(worst);
    const t = worstIdx >= 0 ? timeAtIndex(hours.times, worstIdx) : null;
    let when = "";
    if (t !== null) {
      // The shared clock, like the ruler chip and the transport readout — the
      // hour named here has to be the hour the operator can point at on the axis.
      when = ` · worst ${fmtDayHM(t)}`;
    }
    m.textContent = `${Math.round(worst * 100)}%${when} · ${grade.label}`;
    m.classList.add(grade.cls);

    const spanTxt = `${Math.round(span)} h window from the playhead`;
    const gaps =
      truncated || missing > 0
        ? ` Part of it has no forecast (${truncated ? "the window runs past the end of the series" : `${missing} h missing`}), so the verdict covers only the hours with data.`
        : "";
    m.title =
      `Worst hour in the ${spanTxt}: ${Math.round(worst * 100)}% — ${grade.label}.` +
      ` The whole window is at least this good.${gaps}`;
  };
  redrawWorkTrack = (): void => {
    workRibbon.update(work.hourly());
    paintWorkMeter();
    // The table's cell colours are keyed to the armed vessel+operation, so a
    // selection change repaints them — but only the selection is swapped. The
    // weather did not move, so the columns, the day headings and the scroll
    // position must not either, and the workability row carries the panel's own
    // numbers rather than a second run of workability().
    conditions.refreshSelection({
      vessel: selectedVessel,
      limit: work.effective(),
      opLabel: work.selectedOperation(),
      workP: work.hourly()?.p ?? null,
    });
    pointSocks?.setLimit(work.effective());
  };

  const wind = trackBlock(stack, "Wind speed", "knots · 7-day", "fc.wind", true);
  // Squall flag: ONE ellipsized line in the track header — "⚡ Squalls likely ·
  // Wed 20:00" — with the full reasoning (CAPE fuel / convective rain / gust
  // spread) in its hover title and, per hour, in the chart tooltip. It used to be
  // a whole sentence inserted as a loose grid child, which auto-placed into the
  // 150 px header column and wrapped into six cramped lines beside the chart.
  // The header names the hazard and points at it; the words live under hover.
  const windSquall = el("span", "trackflag");
  (wind.root.querySelector(".trackhdr") ?? wind.root).appendChild(windSquall);
  const wave = trackBlock(stack, "Significant wave height", `${lenWord()} · 7-day`, "fc.wave", true);
  // Wave period rides directly under wave height — the two are read together
  // (a 2 m sea at 5 s is nastier than at 12 s), so keep them adjacent.
  const period = trackBlock(stack, "Wave period", "seconds", "fc.period");

  /**
   * DAW-style channel stripe: the track whose quantity the MAP is painting gets
   * an accent edge on its left, the way a mixer marks the armed channel. The map
   * and the stack sit side by side showing the same weather, and without this
   * the only way to know which colours you were looking at was to find the
   * checkbox in the layer list. Wind feeds the wind track, Waves the wave track,
   * and Limits feeds the workability ribbon — the combined layer is the map-side
   * face of the same go/no-go question. Exhaustive switch: an unmapped layer is
   * a compile error, not an unmarked stack (that silent-default pattern has been
   * the bug on this branch four times).
   */
  function assertNeverLayer(x: never): never {
    throw new Error(`unhandled field layer: ${String(x)}`);
  }
  const syncLayerStripe = (layer: FieldLayer | null): void => {
    for (const t of [workTrack.root, wind.root, wave.root]) t.classList.remove("mapfed");
    if (layer === null) return;
    switch (layer) {
      case "wind":
        wind.root.classList.add("mapfed");
        return;
      case "wave":
        wave.root.classList.add("mapfed");
        return;
      case "limits":
        workTrack.root.classList.add("mapfed");
        return;
      default:
        assertNeverLayer(layer);
    }
  };
  const brief = buildBriefPanel(wsSite);
  briefVessel = brief.setVessel;
  brief.setVessel(selectedVessel); // catch up on the selection made before this panel existed

  // The seasonal normal — what this month is usually like here.
  //
  // Promoted out of the Location primer's prose (Les, 2026-08-15: "where did the
  // readout go"). It was the last sentence of a paragraph about canyons, ports
  // and platforms, which is the wrong company: everything else in that block is
  // geography that will be true next year, and this is the one line that answers
  // an operational question — is the window I am looking at normal for the
  // season, or unusual? That question deserves its own heading next to the
  // briefing, and the same numbers are drawn behind the wind and wave tracks.
  const normal = el("section", "block");
  const normalBh = el("div", "bh");
  normalBh.append(
    el("span", "bt", "Seasonal normal"),
    el("span", "bsub", "what this month usually brings here"),
  );
  normal.appendChild(normalBh);
  const normalBody = el("div", "normalbody");
  normal.appendChild(normalBody);
  const normalSrc = el("div", "bsrc source", "ERA5 reanalysis (Open-Meteo archive)");
  normal.appendChild(normalSrc);
  makeCollapsible(normal, normalBh, "Seasonal normal");
  wsSite.appendChild(normal);

  /**
   * Paint the seasonal-normal block. Null while the archive fetch is in flight
   * or when it failed — said plainly rather than left blank, because an empty
   * block reads as "nothing unusual here" rather than "not known".
   */
  const paintNormalBlock = (clim: ClimFact | null): void => {
    if (clim === null) {
      normalBody.replaceChildren(el("p", "note", "Seasonal normal not available for this point."));
      return;
    }
    const rows = el("dl", "normalrows");
    const row = (k: string, v: string): void => {
      rows.append(el("dt", undefined, k), el("dd", undefined, v));
    };
    row("Wind", `${clim.windP25Kn}–${clim.windP75Kn} kn`);
    if (clim.waveP25M !== null && clim.waveP75M !== null) {
      const d = getUnitSystem() === "imperial" ? 0 : 1;
      row(
        "Sea",
        `${convLen(clim.waveP25M).toFixed(d)}–${convLen(clim.waveP75M).toFixed(d)} ${lenUnit()}`,
      );
    }
    normalBody.replaceChildren(rows);
    // The window and the sample, both stated. "Normal" carries more authority
    // than any sample can pay for silently, and the two rows can have different
    // samples — wind and wave come from different archives.
    const sample =
      clim.waveYears > 0 && clim.waveYears !== clim.years
        ? `${clim.years} years of wind, ${clim.waveYears} of sea`
        : `${clim.years} ${clim.years === 1 ? "year" : "years"}`;
    // --- has it moved? ---------------------------------------------------
    // The same thirty years, split in half, asked about the ARMED limit rather
    // than about a percentile: "the share of hours this vessel could not have
    // worked". That is the operator's own question, and it is the one thing
    // here the published global studies cannot answer — they map the ocean, and
    // nobody's limit is in their grid.
    //
    // Descriptive, and labelled as such. It reports what the record at this cell
    // did; it is NOT a return period, NOT an attribution, and NOT a forecast.
    // The caveat is load-bearing: measured across four longitudes at 50S the
    // sign of this shift FLIPPED (-0.38 m to +0.90 m at p99), so two 15-year
    // halves at one point cannot separate a trend from ordinary decadal swing.
    const limitM = work.waveLimitM();
    if (limitM !== null && clim.early !== null && clim.late !== null) {
      const a = exceedancePct(clim.early.wavePct, limitM);
      const b = exceedancePct(clim.late.wavePct, limitM);
      if (a !== null && b !== null) {
        const row = el("p", "normalshift");
        const d = b - a;
        const word = Math.abs(d) < 0.5 ? "unchanged" : d > 0 ? "more often" : "less often";
        row.append(
          el("span", "normalshift-k", `Over ${fmtLimitLen(limitM)}`),
          el(
            "span",
            "normalshift-v",
            `${a.toFixed(0)}% → ${b.toFixed(0)}% of hours  (${clim.early.firstYear}–` +
              `${clim.early.lastYear} vs ${clim.late.firstYear}–${clim.late.lastYear}, ${word})`,
          ),
        );
        normalBody.appendChild(row);
      }
    }

    normalBody.appendChild(
      el(
        "p",
        "note",
        `The middle half of hourly conditions in ${clim.monthName}, ` +
          `${clim.firstYear}–${clim.lastYear} (${sample}). Half of all hours fall inside ` +
          `this range; a quarter sit below it and a quarter above. It describes TYPICAL ` +
          `conditions, not extremes — the worst hours of a month sit well outside it. ` +
          `Any shift shown above is what this cell's record did between the two halves ` +
          `of the window: decadal swing and a trend look identical over thirty years, so ` +
          `read it as history, not as a direction of travel.`,
      ),
    );
  };
  paintNormalBlock(null);

  // location primer — the "where am I" prose: seafloor feature, jurisdiction,
  // nearby leases/platforms, whale rules, nearest port
  const primer = el("section", "block");
  const primerBh = el("div", "bh");
  primerBh.append(el("span", "bt", "Location primer"), el("span", "bsub", "computed for this point"));
  primer.appendChild(primerBh);
  const primerBody = el("div", "brief");
  primer.appendChild(primerBody);
  const primerSrc = el("div", "bsrc source",
    "Sources: Marine Regions & GEBCO gazetteers · BOEM · NOAA · World Port Index · ERA5 (Open-Meteo)");
  primer.appendChild(primerSrc);
  makeCollapsible(primer, primerBh, "Location primer");
  wsSite.appendChild(primer);

  // forecast-vs-buoy validation: two stacked charts in one block, hidden
  // until a buoy with observations is picked
  const val = el("section", "block");
  const valBh = el("div", "bh");
  const valSub = el("span", "bsub", "");
  // Link out to the station's own NDBC page (full obs incl. visibility, spectral
  // waves, history). stopPropagation so the click opens the page, not collapse.
  const valLink = document.createElement("a");
  valLink.className = "ndbc-link";
  valLink.target = "_blank";
  valLink.rel = "noopener noreferrer";
  valLink.textContent = "NDBC page ↗";
  valLink.addEventListener("click", (e) => e.stopPropagation());
  valBh.append(el("span", "bt", "Forecast vs buoy"), valSub, valLink);
  val.appendChild(valBh);
  const valWindWrap = el("div", "chart-wrap");
  const valWaveWrap = el("div", "chart-wrap");
  valWaveWrap.style.marginTop = "10px";
  val.append(valWindWrap, valWaveWrap);
  const valSrc = el("div", "bsrc source");
  val.appendChild(valSrc);
  makeCollapsible(val, valBh, "Forecast vs buoy");
  val.style.display = "none";
  // Validation belongs with the observations, not with the site: it is the
  // question "is the model tracking reality here", asked of a station.
  wsObs.appendChild(val);
  let valWindChart: Chart | null = null;
  let valWaveChart: Chart | null = null;

  const light = trackBlock(stack, "Daylight & visibility", "", "fc.daylight");
  if (light.meter !== null) light.meter.style.display = "none";
  const sea = trackBlock(stack, "Sea character", `swell vs wind sea · ${lenWord()}`, "fc.seaChar");
  if (sea.meter !== null) sea.meter.style.display = "none";
  // No on-screen legend. There used to be two full sentences of shading
  // explanation here, and as a loose child of the track's GRID they auto-placed
  // into the 150 px header column — prose wrapped into a tall skinny block and
  // broke the stack's vertical rhythm. They were also saying nothing new: the
  // source line under the chart states what the shading is, the ⓘ popover
  // teaches it, and the tooltip flags "⚠ crossed seas" on the very hours it
  // happens. In a stacked track, words live in hover and help; the row itself
  // is for the graphic.
  const cur = trackBlock(stack, "Surface current", "knots · set direction", "fc.current");
  // Sound-speed structure: its own rail workspace, populated by the SVP fetch (and
  // the Argo observed overlay) that rides along with the location primer below.
  const svpPanel = buildSvpPanel(wsSvp);
  // The two operation-context panels. They read what the console already holds —
  // the same profile the SVP workspace draws, the charted depth and relief, the
  // surface current — and re-say it in the vocabulary of one job. Nothing is
  // fetched for them; see web/src/panel/{seep,ebs}.ts for why that is the point.
  const seepPanel: FactPanel | null = wsSeep === null ? null : createFactPanel(wsSeep, {
    title: "Seep hunting — station context",
    sub: "water-column imaging · coring · heat flow",
    help: "ops.seep",
    empty: "Pick a point at sea to read its station context.",
    footer:
      "Modelled, planning-grade. It does not pick targets, plan lines or interpret cores.",
  });
  const ebsPanel: FactPanel | null = wsEbs === null ? null : createFactPanel(wsEbs, {
    title: "Environmental Baseline Survey — cast context",
    sub: "water-column structure for planning a cast",
    help: "ops.ebs",
    empty: "Pick a point at sea to read its water-column structure.",
    footer: EBS_NOTE,
  });

  // Both panels straddle two independent timelines — the SVP fetch and the
  // forecast — so whichever lands second has to redraw with what the first left
  // behind. Held here rather than threaded through, exactly as lastFcHeld is.
  let heldSvp: SvpResult | null = null;
  let heldSvpCovered = false;
  const renderOpsPanels = (): void => {
    if (seepPanel === null || ebsPanel === null) return; // panels are off
    const fc = lastFcHeld;
    if (fc === null) {
      seepPanel.reset();
      ebsPanel.reset();
      return;
    }
    // The playhead when there is one, the current hour otherwise — the same rule
    // the point socks use, and for the same reason: these are read on arrival,
    // before anyone has touched the timeline.
    const i = indexAtTime(fc.times, getCursor()) ?? fc.nowIndex;
    const at = i >= 0 && i < fc.times.length ? i : fc.nowIndex;
    seepPanel.render(
      seepFacts({ fc, svp: heldSvp, svpCovered: heldSvpCovered, i: at }),
      seepNote(fc, at),
    );
    ebsPanel.render(ebsFacts({ fc, svp: heldSvp, svpCovered: heldSvpCovered }));
  };
  // Only the SEEP panel's current line moves with the playhead, but re-rendering
  // both is two DOM rebuilds of a dozen rows — far cheaper than keeping a second
  // code path in step with the first.
  subscribeCursor(renderOpsPanels);
  sea.root.style.display = "none";
  cur.root.style.display = "none";

  // --- the remaining workspaces ---------------------------------------------
  // Each says plainly what it holds and what it does not hold yet. A workspace
  // that opens onto nothing reads as a broken app; one that states its scope
  // reads as a plan.
  const wsPara = (parent: HTMLElement, html: string): void => {
    const p = el("p", "note");
    p.innerHTML = html;
    parent.appendChild(p);
  };
  const wsWrap = (parent: HTMLElement): HTMLElement => {
    const b = el("section", "block");
    parent.appendChild(b);
    return b;
  };

  // --- the framed ----------------------------------------------------------
  // The operating area, quantised. Clicking inside the current framed moves the
  // site and leaves the frame alone; clicking outside it opens a new one and
  // refits the map. That rule is what makes the map stop jumping under you — the
  // old behaviour recentred on every click, so you could never study one area.
  const theaterKv = el("dl", "kv");
  /** Re-labels the cells toggle with the spacing actually drawn. Assigned when the
   *  Theater workspace is built, below. */
  let setCellsLabel: () => void = () => {};
  /** Repaints the saved-waypoints list (and the save/rename state, which depends on
   *  whether the CURRENT framed is one of them). Assigned with the workspace. */
  let refreshSavedList: () => void = () => {};
  let refreshFleetRoster: () => void = () => {};
  /** Re-marks the active view-width button. Assigned with the Waypoints workspace. */
  let syncRungButtons: () => void = () => {};

  // --- the field meshes ------------------------------------------------------
  //
  // The WINDOW mesh: what the map paints, refetched as the view moves, coarsened
  // to the cell budget — which means blockMax cells at wide zoom, the maximum a
  // hazard display must use so nothing vanishes on zoom-out.
  //
  // The TRACE mesh: a tiny native-resolution box around the site, feeding the
  // Map·GFS-Wave line on the charts. It exists because the trace briefly
  // borrowed the window mesh and inherited its statistic: at wide zoom the
  // chart showed the PEAK of a half-degree block beside point-estimate model
  // lines — 2.2 m against the pack's 1.7 — and read as the map running high
  // when it was only a maximum drawn among medians. A chart line whose value
  // changed with the MAP'S ZOOM was wrong in kind, not just in degree. ~35
  // cells, ~40 KB, one fetch per site click; always stride 1, so the trace is
  // always the model's value at the cell containing the point.
  //
  // WINDOW — what the map paints. The viewport plus a margin, at the default cell
  // budget, so the grid sharpens as you zoom in. Followed by ensureWindowMesh.
  let windowMesh: FieldMesh | null = null;
  let windowReq = 0;
  /** Consecutive transport failures, and the pending re-ask. Reset by any success. */
  let windowFails = 0;
  let windowRetryTimer: number | null = null;
  /** First retry after this long, then 2x, 3x. Long enough not to hammer a struggling
   *  server, short enough that a blip heals before anyone reaches for the mouse. */
  const WINDOW_RETRY_MS = 4000;
  const WINDOW_RETRY_MAX = 3;
  /**
   * The newest window request — the box it ASKED for and the cell size it came back
   * at (predicted, until it lands and the mesh says). Null means nothing has been
   * asked for, or the last answer was "no coverage" and is worth retrying.
   *
   * The refetch test runs against this rather than against the mesh's own bounds,
   * because the mesh can legitimately be SMALLER than what was asked: the server crops
   * at the edge of the pre-fetched grid, and clamps a span wider than 40 x 80 degrees.
   * Both are permanent conditions of that box — a working area on the western edge of
   * the grid off Texas hits the first one — so a test against the mesh's bounds would
   * find it short, refetch, get the identical short mesh, and do it again on every
   * moveend for as long as you worked there.
   */
  let windowAsked: { box: FieldBounds; cellDeg: number } | null = null;
  /** The map this console owns, once it exists. Compared against the module-level
   *  activeMap so a debounced refetch scheduled before a route change cannot land on
   *  the next console's map. */
  let fieldMap: SeaMap | null = null;

  // No overview mesh, no locator, no picker. The basin inset was built when the
  // map was caged to a small theater and the system that would close tomorrow's
  // window was literally off-frame; every clause of that rationale has since been
  // deleted — the map roams freely, the field follows the viewport globally, and
  // the reach veil frames "what is yours" on the map itself. What remained was a
  // second picture that had to be kept in agreement with the first, and three of
  // one day's bug fixes were exactly that disagreement. Zooming out IS the
  // locator now. If "glance wide without leaving working zoom" is ever missed,
  // build peek (a momentary wide view of the MAIN map) — one surface, nothing to
  // keep consistent — rather than resurrecting this.
  let traceMesh: FieldMesh | null = null;
  let traceReq = 0;
  /** Fetch the trace's native-resolution neighbourhood for a site. Stale and
   *  failed responses leave the previous mesh in place — meshSeriesAt's bounds
   *  check makes an old site's mesh yield no trace rather than a wrong one. */
  const ensureTraceMesh = (lat: number, lon: number): void => {
    const id = ++traceReq;
    void fetchFieldMesh(boundsFor(lat, lon, 60)).then((res) => {
      if (id !== traceReq) return;
      if (res.ok) traceMesh = res.mesh;
      else if (res.reason === "none") traceMesh = null;
      else return; // transport blip: keep what we have, bounds-guarded
      redrawWind?.();
      redrawWave?.();
    });
  };

  /** How far past the viewport the window mesh reaches, as a fraction of the span on
   *  each side. A quarter is about one screen-width of slack in each direction: enough
   *  that reading around the edges of the area costs nothing, small enough that the
   *  extra ground does not eat the cell budget the working area wants. */
  const WINDOW_MARGIN = 0.25;
  /** How long the map must sit still before the window mesh is refetched. A response
   *  is a couple of MB, and a drag emits moveend once but a zoom-and-pan hunt emits
   *  several a second; this collapses the hunt into one request without making a
   *  deliberate move feel like it stalled. */
  const WINDOW_SETTLE_MS = 300;
  /**
   * How much coarser than the affordable grid the mesh in hand may be before it is
   * worth refetching for resolution alone.
   *
   * Some slack is essential — without it, any zoom that nudged the affordable stride
   * by one would refetch — but too much and zooming in never sharpens, which is the
   * whole reason the window mesh exists. Strides are integers, so the ratios actually
   * reachable are 1, 1.5, 2, 2.5, 3…; setting the bar at 1.5 means "a held stride
   * DOUBLE the wanted stride is stale, a stride and a half is not". Concretely: come
   * back from a 1200 nm look-around (half-degree cells) to a 250 nm working view
   * (quarter-degree affordable) and the field sharpens; drift between two zooms that
   * both want half-degree cells and nothing is fetched.
   *
   * Only coarseness triggers this. A mesh FINER than affordable is never refetched for
   * resolution — it draws correctly, and zooming out breaks its coverage soon enough.
   */
  const WINDOW_COARSE_TOLERANCE = 1.5;

  /** The box to ask the field for: the viewport, grown by WINDOW_MARGIN on each side.
   *  Null when the map cannot supply a usable rectangle — degenerate or inverted
   *  bounds would be a 400, and a world-wide box at zoom 0 is not a request worth
   *  making of a field that spans 150 degrees of longitude. */
  const windowBox = (mm: SeaMap): FieldBounds | null => {
    const b = mm.raw().getBounds();
    let latMin = b.getSouth();
    let latMax = b.getNorth();
    let lonMin = b.getWest();
    let lonMax = b.getEast();
    if (![latMin, latMax, lonMin, lonMax].every((v) => Number.isFinite(v))) return null;
    // MapLibre reports unwrapped longitudes, so a view that has been panned around the
    // globe reads as e.g. 260..380 and a view crossing the antimeridian can read west >
    // east. Un-invert, then CLAMP a wider-than-the-world span to one world about its
    // centre. This used to refuse near-global requests outright — right when the grid
    // was one Atlantic box and a whole-world request told you nothing, wrong now that
    // the grid IS the world: refusing left the previous small mesh painted as a
    // floating rectangle at exactly the zoom where the globe should fill in.
    if (lonMax <= lonMin) lonMax += 360;
    if (lonMax - lonMin > 360) {
      const c = (lonMin + lonMax) / 2;
      lonMin = c - 180;
      lonMax = c + 180;
    }
    if (latMax <= latMin) return null;
    const latPad = (latMax - latMin) * WINDOW_MARGIN;
    const lonPad = (lonMax - lonMin) * WINDOW_MARGIN;
    latMin = Math.max(-89, latMin - latPad);
    latMax = Math.min(89, latMax + latPad);
    lonMin -= lonPad;
    lonMax += lonPad;
    return latMax > latMin && lonMax > lonMin ? { latMin, latMax, lonMin, lonMax } : null;
  };

  /**
   * Refetch the map's field mesh if the one in hand no longer suits the view.
   *
   * Two things can be wrong with the field in hand and BOTH have to be tested. The
   * obvious one is coverage — pan far enough and the image runs out. The subtle one is
   * resolution: after a zoom in, a field fetched for a wide view still covers the
   * viewport perfectly while being far coarser than the same 8000 cells would now buy,
   * and testing coverage alone would leave the map painting 1.5-degree blocks over a
   * 250 nm area forever. So we also compare the cell size in hand against the cell size
   * the CURRENT box would come back at, which the client can work out for itself
   * (strideForBounds) because the grid spacing and the server's budget are both known.
   *
   * The reverse case needs no test. Zooming out always breaks coverage first, so a
   * field that is finer than necessary is refetched as a side effect of no longer
   * reaching far enough — and until it does, finer than necessary draws correctly.
   *
   * Both tests read windowAsked, which is set the moment a request goes out and not
   * only when it lands. That is what stops the moveend at the end of a fitBounds
   * animation paying a second time for the box already in flight.
   */
  const ensureWindowMesh = (): void => {
    const mm = activeMap;
    // A pending debounce outlives the console it was scheduled in — a route change
    // rebuilds everything and leaves this closure holding a map that is gone. Painting
    // through it would drive the LIVE map from a dead console's state.
    if (mm === null || mm !== fieldMap || !mapReady) return;
    const box = windowBox(mm);
    if (box === null) return;
    // The cell size this box would come back at — the bar the mesh in hand has to
    // clear, and the prediction the request about to be made is judged by until it
    // lands. Predicting it is possible only because the grid spacing and the server's
    // default budget are both known here; see strideForBounds.
    const affordableDeg = FIELD_CELL_DEG * strideForBounds(box, FIELD_DEFAULT_MAX_CELLS);
    const have = windowAsked;
    if (
      have !== null &&
      boundsCover(have.box, box) &&
      have.cellDeg <= affordableDeg * WINDOW_COARSE_TOLERANCE
    ) {
      return;
    }

    const id = ++windowReq;
    windowAsked = { box, cellDeg: affordableDeg };
    void fetchFieldMesh(box).then((res) => {
      if (id !== windowReq) return; // superseded by a later move
      if (res.ok) {
        windowFails = 0;
        windowMesh = res.mesh;
        // Replace the prediction with what actually arrived. It can only be finer — the
        // server coarsens a smaller cell set less — and believing the pessimistic guess
        // would refetch a mesh that is already sharp enough.
        windowAsked = { box, cellDeg: meshCellDeg(res.mesh) };
      } else if (res.reason === "none") {
        // The server says there is genuinely no grid here. Clearing is the honest
        // answer; continuing to paint the last basin over uncovered water would be a
        // lie about where we have data.
        windowFails = 0;
        windowMesh = null;
        windowAsked = null;
      } else {
        // We failed to ASK. Nothing was learned, so nothing on screen should change —
        // keep the field that is up rather than blanking the hazard layer over a
        // dropped request. Clear only the record of what we hold for THIS box, so the
        // retry below (and any later move) will ask again.
        windowAsked = null;
        retryWindowMesh();
        return; // no repaint: the mesh is unchanged
      }
      syncWindField();
      // Deliberately NO chart repaint here: the charts' trace reads its own
      // native mesh (ensureTraceMesh), so the map's zoom no longer changes what
      // the charts say.
    });
  };

  /**
   * Re-ask after a failed request, backing off.
   *
   * Without this a user who pans, hits one dropped request and then stops moving would
   * wait forever: the only other trigger is moveend. The backoff caps at WINDOW_RETRY_MAX
   * so a server that is properly down costs a handful of requests rather than a poll
   * loop, and any successful fetch resets it.
   */
  const retryWindowMesh = (): void => {
    if (windowFails >= WINDOW_RETRY_MAX) return;
    windowFails++;
    if (windowRetryTimer !== null) window.clearTimeout(windowRetryTimer);
    windowRetryTimer = window.setTimeout(() => {
      windowRetryTimer = null;
      ensureWindowMesh();
    }, WINDOW_RETRY_MS * windowFails);
  };

  // Cycle revalidation: a parked console never fires moveend, so without this it
  // would hold one model run forever — the bridge-display failure mode, where the
  // screen nobody touches is exactly the one that must stay current. Every
  // half-hour, forget what we asked for and re-ask; the server answers from its
  // in-memory grid, so the cost is one ~2 MB fetch against a Starlink link, and
  // the mesh only actually changes when a new cycle has landed. The trace mesh
  // revalidates on the same tick so the charts and the map can never sit one
  // model run apart — the "fragmented" look this replaced.
  window.setInterval(() => {
    windowAsked = null;
    ensureWindowMesh();
    if (curLat !== null && curLon !== null) ensureTraceMesh(curLat, curLon);
  }, 30 * 60 * 1000);

  let windowTimer: number | null = null;
  /** Coalesce a burst of map movement into one fetch. Bound to moveend, which fires
   *  once per gesture but several times during a zoom-and-pan hunt for an area. */
  const scheduleWindowMesh = (): void => {
    if (windowTimer !== null) window.clearTimeout(windowTimer);
    windowTimer = window.setTimeout(() => {
      windowTimer = null;
      ensureWindowMesh();
    }, WINDOW_SETTLE_MS);
  };
  const paintTheaterKv = (): void => {
    theaterKv.replaceChildren();
    if (framed === null) return;
    const cells = Math.round(framed.spanNm / 60 / 0.25);
    const rows: [string, string][] = [
      ["area", framed.name],
      ["centre", fmtCoord(framed.lat, framed.lon)],
      ["span", `${framed.spanNm} nm`],
      ["model cells", `~${cells} across`],
    ];
    for (const [k, v] of rows) {
      theaterKv.append(el("dt", undefined, k), el("dd", undefined, v));
    }
  };
  /** Draw (or clear) the frame and the model-cell graticule for the current framed. */
  const paintTheater = (): void => {
    const mm = activeMap;
    if (mm === null || !mapReady) return;
    const b = framed === null ? null : boundsFor(framed.lat, framed.lon, framed.spanNm, THEATER_ASPECT);
    setTheaterFrame(mm.raw(), b);
    setTheaterGrid(mm.raw(), showCells ? b : null);
    setCellsLabel();
  };
  // There is deliberately no per-site fetch left. The map's mesh follows the
  // VIEWPORT on moveend, so clicking a point never disturbs what the map shows —
  // the historical siteMoved() hook only ever fed the deleted locator overview.

  /** Frame a waypoint: move the map and the site there. The one path that moves
   *  the view, used by the built-in list, the saved list and the search.
   *
   *  A waypoint with no span is a POSITION, and it opens at whatever width the
   *  view is already set to — the rung buttons are a live choice by the operator
   *  and a bookmark does not get to overrule one. Only a built-in region, which
   *  declares its own width, moves the rung. */
  const goToWaypoint = (a: Waypoint): void => {
    const spanNm = a.spanNm ?? rung(rungId).spanNm;
    framed = { ...a, spanNm };
    if (a.spanNm !== undefined) {
      rungId = rungForSpan(a.spanNm);
      syncRungButtons();
    }
    if (activeMap !== null && mapReady) {
      fitTheater(activeMap.raw(), boundsFor(a.lat, a.lon, spanNm, THEATER_ASPECT));
      activeMap.setPoint(a.lat, a.lon);
    }
    paintTheaterKv();
    paintTheater();
    refreshSavedList();
    void load(a.lat, a.lon);
  };

  {
    // The regions this company works, built in. Nobody should have to define the
    // Gulf of Mexico. These move the map and the site; they do not fence it.
    const ab = wsWrap(wsTheater);
    ab.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Where to work"));
    const areaList = el("div", "thlist");
    for (const a of WAYPOINTS) {
      const row = el("div", "throw");
      const open = document.createElement("button");
      open.type = "button";
      open.className = "thopen";
      open.title = `${a.name} — ${fmtCoord(a.lat, a.lon)}`;
      open.append(el("span", "thnm", a.name), el("span", "thsub", a.note));
      open.addEventListener("click", () => goToWaypoint(a));
      row.appendChild(open);
      areaList.appendChild(row);
    }
    ab.appendChild(areaList);
    const areaSrc = el("div", "bsrc source",
      "Standing operating areas from the TDI-Brooks fleet brief. Jumping to one moves the view — the map stays free, and the field is painted wherever you go.");
    ab.appendChild(areaSrc);

    const b = wsWrap(wsTheater);
    b.appendChild(el("div", "bh")).appendChild(el("span", "bt", "This view"));

    // The ladder floors at 250 nm because that is where GFS-Wave stops
    // resolving — see ops/view.ts. Offering a closer rung would be selling
    // interpolation as detail.
    const seg = el("div", "rungseg");
    const rungBtns = new Map<RungId, HTMLButtonElement>();
    for (const r of RUNGS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "rungseg-btn";
      btn.textContent = r.label;
      btn.title = `${r.spanNm} nm across`;
      btn.addEventListener("click", () => {
        rungId = r.id;
        for (const [id, el2] of rungBtns) el2.classList.toggle("on", id === rungId);
        // A view preset: reframe around the site at this width. It does not
        // define an operating area and it does not stop you zooming afterwards.
        if (curLat !== null && curLon !== null && activeMap !== null && mapReady) {
          fitTheater(activeMap.raw(), boundsFor(curLat, curLon, r.spanNm, THEATER_ASPECT));
        }
      });
      if (r.id === rungId) btn.classList.add("on");
      rungBtns.set(r.id, btn);
      seg.appendChild(btn);
    }
    syncRungButtons = (): void => {
      for (const [id, el2] of rungBtns) el2.classList.toggle("on", id === rungId);
    };
    b.appendChild(seg);
    b.appendChild(theaterKv);

    const cellsLbl = el("label", "wscheck");
    const cellsBox = document.createElement("input");
    cellsBox.type = "checkbox";
    const cellsText = document.createTextNode("Show model cells");
    cellsBox.addEventListener("change", () => {
      showCells = cellsBox.checked;
      paintTheater();
    });
    cellsLbl.append(cellsBox, cellsText);
    b.appendChild(cellsLbl);
    // The graticule thins out at the coarse rungs so a basin doesn't become a grey
    // wash. Every line drawn is still a real cell edge — but a control that says
    // "model cells" while drawing every second one is overstating, so it says which.
    setCellsLabel = (): void => {
      if (framed === null) {
        cellsText.textContent = "Show model cells";
        return;
      }
      const drawn = drawnCellDeg(boundsFor(framed.lat, framed.lon, framed.spanNm, THEATER_ASPECT));
      cellsText.textContent =
        drawn > 0.25
          ? `Show model cells (${drawn}° — 1 line per ${Math.round(drawn / 0.25)} cells)`
          : "Show model cells (0.25°)";
    };
    setCellsLabel();

    // --- save / open ---------------------------------------------------------
    // Saving is what turns the framed from "wherever the map happens to be" into
    // a job you open. The key is already canonical, so a save is just a name for
    // a key — and re-opening one is guaranteed to land on the identical extent
    // and therefore the identical cached field.
    const saveRow = el("div", "thsave");
    const nameIn = document.createElement("input");
    nameIn.type = "text";
    nameIn.className = "thname";
    nameIn.placeholder = "Name this position…";
    nameIn.maxLength = 60;
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "tp-btn";
    saveBtn.textContent = "Save";
    saveRow.append(nameIn, saveBtn);
    b.appendChild(saveRow);

    // No picker canvas. Its own comment conceded the redundancy — clicking bare
    // water "is the same thing clicking the main map does" — and the saved rows
    // below open with a click. The list IS the picker.
    const savedList = el("div", "thlist");
    b.appendChild(savedList);

    /** Open a saved waypoint — the same jump the built-in list makes. */
    const openSaved = (row: SavedWaypoint): void => {
      touchWaypoint(row.id, Date.now());
      goToWaypoint(row);
      renderSaved();
    };

    function renderSaved(): void {
      savedList.replaceChildren();
      const rows = listSaved();
      const here = framed === null ? null : framed.id;
      const mine = rows.find((r) => r.id === here) ?? null;
      nameIn.value = "";
      nameIn.placeholder = mine !== null ? `Saved as \u201c${mine.name}\u201d` : "Name this position\u2026";
      saveBtn.textContent = mine !== null ? "Rename" : "Save";
      saveBtn.disabled = curLat === null || curLon === null;

      if (rows.length === 0) {
        savedList.appendChild(
          el("p", "note", "Nothing saved. Optional \u2014 name the position you are on to come back to it."),
        );
        return;
      }
      for (const row of rows) {
        const item = el("div", "throw" + (row.id === here ? " on" : ""));
        const open = document.createElement("button");
        open.type = "button";
        open.className = "thopen";
        open.title = `${row.name} \u2014 ${fmtCoord(row.lat, row.lon)}`;
        open.append(
          el("span", "thnm", row.name),
          // The position, and the width only if this row still carries one. A row
          // saved before waypoints became positions keeps its span and says so;
          // a new one has none, and printing "undefined nm" is how a bookmark
          // list starts looking broken.
          el(
            "span",
            "thsub",
            row.spanNm === undefined
              ? fmtCoord(row.lat, row.lon)
              : `${row.spanNm} nm \u00b7 ${fmtCoord(row.lat, row.lon)}`,
          ),
        );
        open.addEventListener("click", () => openSaved(row));
        const del = document.createElement("button");
        del.type = "button";
        del.className = "thdel";
        del.textContent = "\u00d7";
        del.title = `Forget \u201c${row.name}\u201d`;
        del.addEventListener("click", () => {
          removeWaypoint(row.id);
          if (framed !== null && framed.id === row.id) framed = null;
          renderSaved();
          paintTheaterKv();
          paintTheater();
        });
        item.append(open, del);
        savedList.appendChild(item);
      }
    }

    // Saving is optional and saves THE POSITION you are on — not the width you
    // happen to be viewing it at. Renaming when this waypoint is already saved,
    // rather than making a duplicate a foot away from the original.
    const doSave = (): void => {
      if (curLat === null || curLon === null) return;
      const existing = framed === null ? null : listSaved().find((r) => r.id === framed?.id) ?? null;
      if (existing !== null) {
        if (nameIn.value.trim() !== "") renameWaypoint(existing.id, nameIn.value);
      } else {
        const saved = saveWaypoint(nameIn.value, curLat, curLon, Date.now());
        framed = { ...saved, spanNm: rung(rungId).spanNm };
      }
      renderSaved();
      paintTheaterKv();
      paintTheater();
    };
    saveBtn.addEventListener("click", doSave);
    nameIn.addEventListener("keydown", (e) => {
      if (e.key === "Enter") doSave();
    });
    renderSaved();
    refreshSavedList = renderSaved;

    // The paragraph that stood here described quantising — nearby clicks snapping
    // to a shared operating area — and that stopped being true when the theater
    // key was removed (see savedwaypoints.ts). A saved waypoint keeps the exact
    // coordinates it was saved at. Copy that describes a mechanism the product no
    // longer has is worse than no copy: it teaches an operator to expect their
    // click to move, and they will read the resulting position as a fault.
    wsPara(
      b,
      "A waypoint is the <em>exact position</em> it was saved at. Opening one moves the site " +
        "there and takes the view with it; the frame is only how wide you are looking, and " +
        "you can zoom freely afterwards.",
    );
    wsPara(
      b,
      "<strong>Saved waypoints live in this browser only</strong> — they do not follow you to " +
        "another machine or out to the vessel.",
    );
    const src = el("div", "bsrc source",
      "Rungs floor at 250 nm because GFS-Wave resolves at 0.25° (~15 nm). Closer zoom would show interpolation, not data.");
    b.appendChild(src);
  }

  {
    const b = wsWrap(wsFleet);
    b.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Fleet"));
    // The roster: every hull in the registry, click to arm it. This workspace
    // used to hold only the limits-provenance note and read as a stub — a rail
    // item called Fleet with no vessels in it is a broken promise. Clicking a
    // row runs the exact flow the Workability dropdown runs (selectVessel), so
    // the whole console re-arms: ramp, rings, veil, limit lines, briefing.
    const roster = el("div", "fleetlist");
    b.appendChild(roster);
    const renderFleet = (): void => {
      roster.replaceChildren();
      const reg = state.registry;
      for (const v of reg.vessels) {
        const row = el("button", "fleetrow");
        row.setAttribute("type", "button");
        if (selectedVessel?.id === v.id) row.classList.add("armed");
        const specs: string[] = [];
        if (v.loa_m != null) specs.push(fmtLen(v.loa_m));
        if (v.cruise_kn != null) specs.push(`${v.cruise_kn} kn cruise`);
        if (v.dp) specs.push("DP");
        // Limits provenance per hull: how many of its operations have YOUR
        // numbers vs the seeded heuristic. This is the roster's second job —
        // seeing at a glance which boats the colour ramp can be trusted for.
        const ops = reg.operations.filter((o) => v.operation_ids.includes(o.id));
        const own = ops.filter((o) => effectiveLimit(v, o, reg)?.isDefault === false).length;
        const chip = el(
          "span",
          own === 0 ? "fleetchip warn" : "fleetchip ok",
          own === 0 ? "preset limits" : `limits ${own}/${ops.length} yours`,
        );
        row.append(
          el("span", "fleetname", v.name),
          el("span", "fleetspecs", specs.join(" · ")),
          chip,
        );
        row.addEventListener("click", () => work.selectVessel(v.id));
        roster.appendChild(row);
      }
    };
    const limNote = el("p", "note");
    b.appendChild(limNote);
    const link = el("a", undefined, "Set limits in Vessels →");
    link.setAttribute("href", "/vessels");
    link.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("/vessels");
    });
    b.appendChild(link);
    // The badge is the point of this workspace. Limits anchor the colour ramp on
    // the map and the limit line on every track, so "these are a heuristic, not
    // your operations team's judgment" has to be visible without opening a panel.
    const syncLimitBadge = (): void => {
      renderFleet(); // the armed row and per-hull chips follow every selection
      const prov = work.limitsProvisional();
      limNote.textContent = prov
        ? "The limits in force are a preset heuristic from hull length and DP class — not your operations team's numbers. The map ramp and every limit line are anchored to them."
        : "Limits for this vessel and operation come from your own limits table.";
      rail.setBadge(
        "fleet",
        prov ? { level: "warn", title: "Operating limits are provisional" } : null,
      );
    };
    syncLimitBadge();
    limitBadgeSync = syncLimitBadge;
    refreshFleetRoster = renderFleet; // a units flip re-prints the LOA specs
  }

  {
    const b = wsWrap(wsObs);
    b.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Stations"));
    wsPara(
      b,
      "Click a buoy on the map to compare it against the forecast below. Observed values " +
        "are never blended into the forecast — they sit beside it, so you can see whether " +
        "the model is tracking reality here.",
    );
    // Keep the validation block under the note that explains it.
    b.after(val);
  }

  {
    const b = wsWrap(wsTropical);
    b.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Tropical systems"));
    const tropList = el("div", "tropcards");
    b.appendChild(tropList);
    b.appendChild(
      el(
        "p",
        "note",
        "Cones and tracks on the map are NHC's (Atlantic, East and Central Pacific). " +
          "Elsewhere the map draws JTWC's warning: the forecast track and the 34-knot danger " +
          "swath, where gales are forecast if the track verifies — not a cone, and with no " +
          "track-error margin. The wind and wave fields still carry the storm itself.",
      ),
    );
    const basinName = (code: string): string =>
      ({
        AL: "Atlantic",
        EP: "East Pacific",
        CP: "Central Pacific",
        WP: "West Pacific",
        IO: "Indian Ocean",
        SH: "Southern Hemisphere",
      })[code] ?? code;
    const fly = (lat: number | null, lon: number | null): (() => void) | undefined =>
      lat === null || lon === null
        ? undefined
        : () => activeMap?.raw().flyTo({ center: [lon, lat], zoom: 4.2 });
    // The cards name what the map is already drawing, from the SAME module state,
    // so the two cannot disagree about what is active. The badge is keyed to the
    // SITE, not to the world: a hurricane exists somewhere most weeks, and a rail
    // that glows for all of them teaches everyone to stop looking. It lights only
    // when the current point sits inside a forecast cone (crit) or a development
    // outlook area (warn) — the two facts an operator here must not miss.
    /**
     * How old the two halves of a storm card are.
     *
     * A storm card is assembled from two NHC products that can be a day apart.
     * The cone and track come from the advisory package; the classification and
     * intensity from the current-storm summary, which revises far more often.
     * On 2026-09-02 they were 21 hours apart on Edouard — the map still calling
     * him a tropical storm at 40 kt while the summary had him a depression at
     * 20 — and an operator reported the console as wrong. It was not wrong. It
     * was drawing a stale upstream and saying nothing about it, which is the
     * same failure that let the SVP depth cap run for weeks.
     *
     * So the card states its own age. Nothing here interprets it: an advisory
     * number and a clock time, and the reader can see for themselves.
     */
    const stormAge = (advNum: string, liveAt: string | null): string => {
      const bits: string[] = [];
      if (advNum !== "") bits.push(`cone from adv ${advNum}`);
      if (liveAt !== null) {
        const t = new Date(liveAt);
        if (!Number.isNaN(t.getTime())) {
          const hrs = Math.floor((Date.now() - t.getTime()) / 3_600_000);
          const when =
            hrs < 1 ? "just now" : hrs === 1 ? "1 h ago" : `${hrs} h ago`;
          bits.push(`strength ${when}`);
        }
      }
      return bits.length === 0 ? "" : ` · ${bits.join(" · ")}`;
    };

    // Re-read the storm feeds on a timer. Without this the panel was fetched
    // once when the map was created and never again, so a console left open all
    // day — which is how one is used — froze at whenever it was last reloaded.
    //
    // Ten minutes against a current-storm summary that revises a few times an
    // hour. It would NOT have prevented the Edouard report: re-fetching a stale
    // endpoint returns the same stale answer, which is why the age stamp above
    // matters more than the interval.
    window.setInterval(() => {
      void refreshTropical().then(() => syncTropical());
    }, 600_000);

    syncTropical = (): void => {
      const s = tropicalSummary();
      tropList.replaceChildren();
      if (s.storms.length === 0 && s.outlooks.length === 0) {
        tropList.appendChild(el("p", "note", "No active systems or outlook areas — any basin."));
      } else {
        for (const st of s.storms) {
          tropList.appendChild(
            tropCardEl({
              name: st.name,
              chip: stormChip(st.type, st.ss),
              fact: stormFact(st.maxwind, st.tcdir, st.tcspd),
              note:
                basinName(st.basin) +
                (st.jtwc !== null
                  ? (st.onMap ? " · JTWC gale swath and track on the map" : " · JTWC — no swath in this warning") +
                    ` · warning ${st.jtwc.warning} · ${st.jtwc.issued}`
                  : (st.onMap ? " · cone and track on the map" : " · JTWC — no warning graphic yet") +
                    stormAge(st.advNum, st.liveAt)),
              lat: st.lat,
              lon: st.lon,
              marker: "storm",
              onFly: fly(st.lat, st.lon),
            }),
          );
        }
        for (const o of s.outlooks) {
          const facts: string[] = [];
          if (o.prob2day !== "") facts.push(`${o.prob2day} in 2 days`);
          if (o.prob7day !== "") facts.push(`${o.prob7day} in 7 days`);
          tropList.appendChild(
            tropCardEl({
              name: `${o.basin} development area`,
              chip: { short: o.prob7day === "" ? "?" : o.prob7day, level: o.risk7day === "High" ? "crit" : "warn" },
              fact: facts.join(" · "),
              note: "outlook area on the map",
              lat: o.lat,
              lon: o.lon,
              marker: "outlook",
              onFly: fly(o.lat, o.lon),
            }),
          );
        }
      }
      const here = curLat !== null && curLon !== null ? tropicalAt(curLat, curLon) : null;
      rail.setBadge(
        "tropical",
        here === null
          ? null
          : here.storm !== null
            ? { level: "stop", title: `Inside ${here.storm.name}'s forecast cone` }
            : { level: "warn", title: "Inside an NHC development outlook area" },
      );
    };
    syncTropical();
  }

  {
    const b = wsWrap(wsSystems);
    b.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Systems"));
    wsPara(
      b,
      "Every upstream this console draws from, with the age of what it last said. " +
        "Health is recorded from the real fetches the console makes — nothing here " +
        "polls, so a struggling link is never made worse by its own monitoring.",
    );
    const sysList = el("div", "syslist");
    b.appendChild(sysList);
    const renderSystems = (): void => {
      sysList.replaceChildren();
      for (const r of systemsSnapshot()) {
        const row = el("div", "sysrow");
        row.append(
          el("i", `sysdot ${r.status}`),
          el("span", "sysname", r.label),
          el("span", "sysdetail", r.detail),
          el("span", "sysfeeds", r.feeds),
        );
        sysList.appendChild(row);
      }
      rail.setBadge("systems", systemsBadge());
    };
    renderSystems();
    subscribeSystems(renderSystems);
    // Ages are text ("23 min ago") that must keep moving with no fetch events —
    // a stale field that never refetches would otherwise show a frozen age. One
    // slow tick; the panel is a dozen DOM nodes.
    window.setInterval(renderSystems, 30_000);
  }

  /** What the console knew when a report was written. Sent verbatim; the server
   *  stores it as JSONB without validating the shape, so adding a field here
   *  needs no migration and an old report with fewer fields still displays. */
  const currentFeedbackContext = (): Record<string, unknown> => {
    const cur = getCursor();
    return {
      route: window.location.pathname + window.location.search,
      lat: curLat,
      lon: curLon,
      vessel: selectedVessel?.name ?? null,
      operation: work.selectedOperation(),
      windLimitKn: work.windLimitKn(),
      waveLimitM: work.waveLimitM(),
      limitsProvisional: work.limitsProvisional(),
      cursorUtc: cur === null ? null : new Date(cur).toISOString(),
      windowHours: getWindowHours(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      appVersion: document.documentElement.dataset.build ?? "",
    };
  };

  /** One line of provenance under a report, from whatever keys it happens to
   *  carry. Tolerant on purpose: old reports predate newer fields. */
  const asContextLine = (raw: unknown): string => {
    if (raw === null || typeof raw !== "object") return "";
    const c = raw as Record<string, unknown>;
    const bits: string[] = [];
    if (typeof c.lat === "number" && typeof c.lon === "number") {
      bits.push(`${c.lat.toFixed(3)}, ${c.lon.toFixed(3)}`);
    }
    if (typeof c.vessel === "string" && c.vessel !== "") bits.push(c.vessel.replace(/ —.*/, ""));
    if (typeof c.operation === "string" && c.operation !== "") bits.push(c.operation);
    if (typeof c.cursorUtc === "string" && c.cursorUtc !== "") bits.push(`${c.cursorUtc.slice(0, 16).replace("T", " ")}Z`);
    if (typeof c.route === "string" && c.route !== "" && c.route !== "/console") bits.push(c.route);
    return bits.join(" · ");
  };

  // --- Feedback workspace ---------------------------------------------------
  //
  // Every report is sent WITH the state it was written in: the route, the point,
  // the vessel and operation selected, and the hour under the playhead. "The wave
  // chart looks wrong" is nearly useless a week later; the same sentence carrying
  // 27.5N 94.0W, R/V Brooks McCall, CPT push and Wed 14:00 is reproducible. The
  // operator should not have to think of any of that, so the console attaches it.
  {
    const b = wsWrap(wsFeedback);
    b.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Feedback"));
    wsPara(
      b,
      "Anything wrong, missing, or awkward — say it here. Where you are, which " +
        "vessel and operation are selected, and the hour you are looking at are " +
        "attached automatically, so there is no need to describe them.",
    );

    const box = document.createElement("textarea");
    box.className = "fbbox";
    box.rows = 5;
    box.placeholder = "What happened, and what did you expect instead?";
    b.appendChild(box);

    const actions = el("div", "fbactions");
    const send = el("button", "btn primary", "Send feedback");
    send.type = "button";
    const status = el("span", "fbstatus");
    actions.append(send, status);
    b.appendChild(actions);

    const listBlock = wsWrap(wsFeedback);
    listBlock.appendChild(el("div", "bh")).appendChild(el("span", "bt", "Sent"));
    const list = el("div", "fblist");
    listBlock.appendChild(list);

    const fmtWhen = (iso: string): string => {
      const t = Date.parse(iso);
      // An event stamp (browser clock / UTC when toggled), not a timeline hour:
      // "when did I send this" belongs to this machine, not to the site's zone.
      return Number.isFinite(t) ? fmtEventStamp(t) : iso;
    };

    let lastFeedbackItems: ApiFeedback[] = [];
    const renderList = (items: ApiFeedback[]): void => {
      lastFeedbackItems = items;
      list.replaceChildren();
      if (items.length === 0) {
        list.appendChild(el("p", "note", "Nothing sent yet."));
        return;
      }
      for (const f of items) {
        const row = el("div", "fbrow");
        const head = el("div", "fbhead");
        head.append(
          el("span", "fbwhen", fmtWhen(f.created_at)),
          el("span", "fbwho", f.author !== "" ? f.author : "unknown"),
        );
        const del = el("button", "btn fbdel", "Remove");
        del.type = "button";
        del.addEventListener("click", () => {
          del.disabled = true;
          void deleteFeedback(f.id).then((res) => {
            if (res.ok) void refreshList();
            else del.disabled = false;
          });
        });
        head.appendChild(del);
        row.append(head, el("p", "fbmsg", f.message));
        const ctx = asContextLine(f.context);
        if (ctx !== "") row.appendChild(el("p", "fbctx", ctx));
        list.appendChild(row);
      }
    };

    const refreshList = async (): Promise<void> => {
      const res = await getFeedback();
      if (res.ok) renderList(res.data);
      else list.replaceChildren(el("p", "note", "Could not load past feedback."));
    };

    send.addEventListener("click", () => {
      const msg = box.value.trim();
      if (msg === "") {
        status.textContent = "Write something first.";
        return;
      }
      send.disabled = true;
      status.textContent = "Sending…";
      void sendFeedback(msg, currentFeedbackContext()).then((res) => {
        send.disabled = false;
        if (res.ok) {
          box.value = "";
          status.textContent = "Sent — thank you.";
          void refreshList();
        } else {
          status.textContent = res.error;
        }
      });
    });

    void refreshList();
    // A clock flip re-stamps the sent reports from what is already in hand.
    repaintFeedback = () => renderList(lastFeedbackItems);
  }

  const marineBlocks = [wave.root, work.root, brief.root, primer, period.root, sea.root, cur.root];

  let reqId = 0;

  // The observed Argo profile in hand, and its own request counter. Held across
  // point loads (containment cache) so a click near the same float reuses it
  // instead of hitting ERDDAP again; the request id guards against a slow reply
  // landing after the site has moved on. A transport failure leaves this untouched.
  let heldArgo: ArgoProfile | null = null;
  let argoReq = 0;

  // The drifter set in hand, its fetch centre, and its own request counter — the
  // same containment-cache + request-id discipline as Argo. Held across point loads
  // so a click inside the same ±6° box reuses the set rather than repeating the slow
  // OSMC scan; a transport failure leaves it untouched (site-distance checks make a
  // held set harmless).
  let heldDrifters: HeldDrifters | null = null;
  let drifterReq = 0;

  let lastBuoy: BuoyPick | undefined;

  async function load(lat: number, lon: number, buoy?: BuoyPick): Promise<void> {
    curLat = lat;
    curLon = lon;
    lastPickedPoint = { lat, lon };
    lastBuoy = buoy;
    syncTropical(); // the tropical badge is keyed to the SITE, so it moves with it
    ensureTraceMesh(lat, lon); // the charts' Map·GFS-Wave line, native cells at the site
    autoAnchor = [lat, lon]; // Auto theme reads the sun at the viewed point
    updateRange(); // re-center the range rings on the new point
    syncWindField(); // repaint the field with this point's limits; it does not move
    header.coord.innerHTML =
      `<span class="k">PT</span> ${fmtCoord(lat, lon)}` +
      (buoy !== undefined
        ? ` <span class="k">·</span> ${buoySource(buoy.source)?.pickLabel(buoy) ?? buoy.id}`
        : "");
    header.juris.textContent = "";
    header.seabed.textContent = "";
    const id = ++reqId;
    const live = (): boolean => id === reqId;

    const jurisPromise = fetchJurisdiction(lat, lon);
    void jurisPromise.then(
      (j) => reportSystem("eez", j !== null),
      () => reportSystem("eez", false),
    );
    void jurisPromise.then((j) => {
      if (!live() || j === null) return;
      const short = j.name.replace("Exclusive Economic Zone", "EEZ");
      header.juris.innerHTML =
        `<span class="k">EEZ</span> ${short}` +
        (j.disputed ? ` <span class="disputed">⚠ ${j.polType}</span>` : "");
    });

    // location primer: gathers its facts alongside everything else, renders
    // with the jurisdiction name once both have answered
    primerBody.replaceChildren(el("p", "note", "Reading the neighborhood…"));
    // A new point must never be read against the previous point's normal.
    curClim = null;
    svpPanel.reset(); // hide until the SVP fetch (below) answers for this point
    heldSvp = null;
    heldSvpCovered = false;
    seepPanel?.reset();
    ebsPanel?.reset();
    const month = new Date().getMonth();
    // The SVP renders on its OWN timeline — it must never wait on the primer's
    // (slower, many-source) fetch. They are independent panels.
    void fetchSvp(lat, lon)
      .catch(() => null)
      .then((svp) => {
        const answered = svp !== null && svp.ok;
        // "No coverage" is a healthy pipe saying a true thing — but say it in the
        // chip too, so a green Systems row and an empty panel can never disagree
        // (Les read exactly that pairing as a broken console, 2026-07-21). The
        // note is site-specific, so a stale answer updates health but not the note.
        const stale = !live();
        reportSystem(
          "svp",
          answered,
          stale ? {} : { note: answered && svp.data === null ? "no model cell at this point" : "" },
        );
        if (stale) return;
        svpPanel.render(answered ? svp.data : null, { lat, lon }, answered);
        heldSvp = answered ? svp.data : null;
        heldSvpCovered = answered;
        renderOpsPanels();
      });

    // Observed Argo overlay — its own timeline again, and its own containment
    // cache. When the float in hand still represents this site (still near, still
    // within the age budget) we reuse it and skip the ERDDAP round-trip; otherwise
    // we ask. A transport failure keeps whatever we hold and shows no overlay here
    // rather than a float from the wrong neighbourhood — the site check IS the
    // bounds guard that makes the held profile harmless. "No float within range" is
    // a healthy answer (a fact about the ocean), not a failure of the pipe.
    if (heldArgo !== null && argoStillRepresents(heldArgo, lat, lon)) {
      reportSystem("argo", true, { dataAt: heldArgo.timeUtcMs, note: `float ${heldArgo.platform}` });
      svpPanel.setObserved(heldArgo);
    } else {
      const aid = ++argoReq;
      void fetchArgo(lat, lon).then((res) => {
        if (aid !== argoReq) return; // a newer click already asked; drop this answer
        if (!res.ok) {
          reportSystem("argo", false); // transport blip — keep the held profile, show nothing new
          return;
        }
        reportSystem("argo", true, {
          dataAt: res.profile !== null ? res.profile.timeUtcMs : null,
          note: res.profile !== null ? `float ${res.profile.platform}` : "no float within range",
        });
        if (res.profile !== null) heldArgo = res.profile;
        if (!live()) return;
        svpPanel.setObserved(res.profile);
      });
    }

    // Observed drifters — the fifth observed-beside-modelled use, and the same shape
    // as Argo above: its own request id, its own containment cache, its own timeline.
    // It feeds TWO surfaces — the map's drifter overlay (glyph + set vector + tail)
    // and the Surface-current chart's observed dots — so both are refreshed here.
    // "No drifter in range" is a healthy answer with no data stamp (a fact about the
    // ocean, drifters are sparse); only a transport failure is a failure, and it keeps
    // whatever set we hold. The fetch is SLOW (a whole-GTS OSMC scan), which is exactly
    // why the chart draws from the model first and redrawCurrent repaints it on arrival.
    const applyDrifters = (drifters: Drifter[]): void => {
      curDrifters = drifters;
      if (activeMap !== null) setDrifterData(activeMap.raw(), drifters);
      redrawCurrent?.();
    };
    if (heldDrifters !== null && driftersStillRepresent(heldDrifters, lat, lon)) {
      reportSystem("drifters", true, {
        dataAt: driftersNewestMs(heldDrifters.drifters),
        note: driftersNote(heldDrifters.drifters),
      });
      applyDrifters(heldDrifters.drifters);
    } else {
      curDrifters = []; // clear the previous site's dots until this fetch answers
      const did = ++drifterReq;
      void fetchDrifters(lat, lon).then((res) => {
        if (did !== drifterReq) return; // a newer click already asked; drop this answer
        if (!res.ok) {
          reportSystem("drifters", false); // transport blip — keep any held set, show nothing new
          return;
        }
        heldDrifters = { lat, lon, drifters: res.drifters };
        reportSystem("drifters", true, {
          dataAt: driftersNewestMs(res.drifters),
          note: driftersNote(res.drifters),
        });
        if (!live()) return;
        applyDrifters(res.drifters);
      });
    }

    // location primer prose — its facts plus the jurisdiction frame
    void Promise.all([fetchPrimer(lat, lon, month), jurisPromise.catch(() => null)]).then(
      ([facts, j]) => {
        if (!live()) return;
        // tropical read the map hover used to hide — lead with it, it's a hazard
        const tropHtml = tropicalHtml(tropicalAt(lat, lon));
        // The seasonal normal reaches the tracks here, after the fact. The two
        // charts drew from the forecast alone; this is what puts the band behind
        // them, and it is why redrawWind/redrawWave are retained.
        curClim = facts.clim ?? null;
        if (curClim !== null) {
          redrawWind?.();
          redrawWave?.();
        }
        paintNormalBlock(curClim);
        const html = buildPrimerHtml(facts, j !== null && j.name !== "High seas" ? j.name : null);
        if (html.trim() === "" && tropHtml === "") {
          primerBody.replaceChildren(el("p", "note", "No location facts available for this point."));
        } else {
          primerBody.innerHTML = tropHtml + html;
          // innerHTML can't carry wired listeners, so attach the ⓘ affordance after.
          const trop = primerBody.querySelector(".trop-note");
          if (trop !== null) trop.appendChild(infoButton("map.tropical"));
        }
      },
    );

    // buoy observations load alongside the forecast, never blocking it —
    // routed to the picked station's source adapter (NDBC / AODN / …)
    const obsPromise: Promise<BuoyObs | null> =
      buoy !== undefined ? fetchObsFor(buoy.source, buoy.id) : Promise.resolve(null);

    resetBlock(wind, "Loading forecast…");
    resetBlock(wave, "Loading forecast…");
    resetBlock(period, "");
    resetBlock(light, "");
    // Blank the ruler/transport/confidence strip along with the charts. They hold
    // the PREVIOUS forecast's label arrays, and setLabelZone below changes what
    // those labels mean — a cursor move during the (buoy-obs) fetch would repaint
    // them with old labels parsed under the new site's zone, displacing the
    // playhead by the offset difference until the new times land.
    setRulerTimes(null);
    conditions.update(null); // same stale-labels hazard as the ruler
    pointSocks?.update(null); // and the socks, which read the same held hours
    work.render(null);
    redrawWorkTrack();
    brief.render(null, null);
    valWindChart?.destroy();
    valWaveChart?.destroy();
    valWindChart = null;
    valWaveChart = null;
    val.style.display = "none";
    landNote.style.display = "none";
    freshness.hidden = false;
    freshness.textContent = "Retrieving forecast…";
    freshAt = null; // set only on success, so a clock flip never revives a stale line

    try {
      const fc = await fetchPointForecast(lat, lon);
      if (!live()) return;
      // The whole timeline — parsing chart labels to instants AND formatting the
      // playhead readouts back to wall clock — runs in the SITE's zone from here
      // on. Set before any consumer touches fc.times, or the first render maps
      // labels in whatever zone the previous site (or the browser) used.
      setLabelZone(fc.utcOffsetSeconds);
      // The SVP panel gets the charted depth the moment the forecast lands —
      // before the buoy-obs await below, which would otherwise delay the seabed
      // line and axis floor by an unrelated fetch. The seabed line, the axis
      // extension, and the CSV/PNG "Water depth" provenance all come from this
      // one NCEI number.
      svpPanel.setSiteDepth(fc.depthM);
      freshAt = Date.now();
      freshness.textContent = `Forecast retrieved ${fmtClockHM(freshAt)} · re-click any point to refresh`;
      const obs = await obsPromise.catch(() => null);
      // Health only when a station was actually asked for; the newest sample's
      // own timestamp is the DATA age — a buoy that stops reporting goes stale
      // here even though every fetch of its (unchanging) file succeeds.
      if (buoy !== undefined) {
        reportSystem("obs", obs !== null, {
          dataAt: obs !== null && obs.timesUtcMs.length > 0 ? obs.timesUtcMs[obs.timesUtcMs.length - 1] : null,
          note: obs !== null ? buoy.id : "",
        });
      }
      if (!live()) return;

      const solar = solarSeries(fc.times, fc.utcOffsetSeconds, lat, lon);

      // site classification: LAND / NEARSHORE / WATER, from NCEI depth with a
      // wave-model fallback when bathymetry is unavailable
      type Site = "land" | "nearshore" | "water" | "unknown";
      const site: Site =
        fc.landElevM !== null
          ? "land"
          : fc.depthM !== null
            ? fc.depthM <= 50
              ? "nearshore"
              : "water"
            : fc.waves !== null
              ? "water"
              : "unknown";
      const chip = (cls: string, label: string): string =>
        `<span class="k">SITE</span> <span class="site-chip ${cls}">${label}</span>`;
      if (site === "land") {
        header.seabed.innerHTML = chip(
          "land",
          `LAND · ${fmtDepth(fc.landElevM ?? 0)} elev`,
        );
      } else if (site === "nearshore") {
        header.seabed.innerHTML = chip(
          "near",
          `NEARSHORE · ${fmtDepth(fc.depthM ?? 0)}`,
        );
      } else if (site === "water") {
        header.seabed.innerHTML = chip(
          "water",
          fc.depthM !== null ? `WATER · ${fmtDepth(fc.depthM)}` : "WATER",
        );
      }

      // on dry land the marine panels make no sense — say so plainly and show
      // only what's still meaningful (wind, daylight)
      const dry = site === "land";
      const noteEl = landNote.querySelector("p");
      if (noteEl !== null) {
        noteEl.textContent =
          `⚠ This point is on land (elevation ≈ ${fmtDepth(fc.landElevM ?? 0)}). ` +
          `Wind and daylight are shown; click at sea for the marine forecast.`;
      }
      landNote.style.display = dry ? "" : "none";
      for (const b of marineBlocks) b.style.display = dry ? "none" : "";

      // buoy observation overlays, bucketed onto the forecast hour axis
      const obsWind = obs !== null ? bucketObs(obs, "wspdKn", fc.times, fc.utcOffsetSeconds) : null;
      const obsWave = obs !== null ? bucketObs(obs, "wvhtM", fc.times, fc.utcOffsetSeconds) : null;
      const obsPeriod = obs !== null ? bucketObs(obs, "dpdS", fc.times, fc.utcOffsetSeconds) : null;
      const obsColor = cssVar("--obs", "#48575f");
      const obsSeries = (data: (number | null)[] | null, label: string): Series[] =>
        data !== null && data.some((v) => v !== null)
          ? [{ label, data, color: obsColor, points: true, order: 50 }]
          : [];

      /**
       * The map's own model, as a line among the models.
       *
       * The colours on the map come from ONE deterministic run (NOAA GFS-Wave),
       * while these charts carry three wind models, four wave models and a GEFS
       * ensemble from Open-Meteo. Different vendors, different physics, and no way
       * for an operator to tell whether the field agrees with the consensus they are
       * reading — the map was an unaudited second opinion.
       *
       * Drawn here in the map's own accent, at double width and with a dot on every
       * real model step, it becomes auditable: hugging the pack means trust the
       * colours, running high on Thursday means do not plan Thursday off the map.
       * The gap between this line and the ensemble band is a live, flow-dependent
       * statement of the field's uncertainty, measured rather than assumed.
       *
       * The label carries the cell size and the offset because part of any visible
       * gap is spatial sampling, not model disagreement: at stride 2 the cell is half
       * a degree and its centre can sit 15-30 nm from where you clicked, while the
       * Open-Meteo lines are interpolated to the exact coordinates.
       */
      const fieldTrace = (kind: FieldKind): Series[] => {
        const mesh = traceMesh;
        if (mesh === null || curLat === null || curLon === null) return [];
        const s = meshSeriesAt(mesh, curLat, curLon, kind, fc.times, fc.utcOffsetSeconds);
        if (s === null) return [];
        return [{
          // The trace mesh is a tiny box that always fits the budget at stride 1,
          // so in practice this always prints "0.25° cell". The "peak of" branch
          // stays as a guard: if the mesh ever arrives coarsened, a blockMax is
          // NOT a cell reading, and printing it as one is how 2.2 m gets read
          // against a 1.7 m pack as model disagreement instead of sampling.
          // Named like every other line on the chart: by MODEL, then provenance.
          // "Map field" named the surface it drives, not the source it comes
          // from — the one anonymous line on a chart whose whole job is model
          // attribution. (The wave chart also carries Open-Meteo's own
          // "GFS-Waves (WW3)" line: same model family, different plumbing — ours
          // is the raw NOAA grid the map paints, theirs is regridded to the
          // point. The "map" prefix is what keeps the two distinguishable.)
          label:
            s.cellDeg > FIELD_CELL_DEG * 1.5
              ? `Map · GFS-Wave · peak of ${s.cellDeg.toFixed(2)}° block, ${Math.round(s.offsetNm)} nm off`
              : `Map · GFS-Wave · ${s.cellDeg.toFixed(2)}° cell, ${Math.round(s.offsetNm)} nm off`,
          data: s.data,
          color: cssVar("--accent", "#0f6b74"),
          // Same weight as every other model line. This one is already told apart
          // three ways — the "Map ·" prefix, the accent hue and its point markers —
          // and at width 3 it simply sat on top of the lines it is meant to be
          // compared against, which is the one thing a comparison chart cannot do.
          width: 2,
          points: true,
          line: true,
          order: 60,
        }];
      };

      // wind — retained as redrawWind() so an operation change (which moves the
      // limit line and rescales the axis) can repaint just this chart, no refetch.
      redrawWind = (): void => {
        if (fc.wind === null) {
          resetBlock(wind, "Wind forecast unavailable at this point.");
          return;
        }
        const w = fc.wind;
        const limitKn = work.windLimitKn();
        mountChart(wind, (c) => {
          const m4 = cssVar("--m4", "#7b5ea7");
          const series: Series[] = [];
          if (w.ensemble !== null) {
            series.push({ label: "", data: w.ensemble.p05, color: "transparent", width: 0, noLegend: true, noTooltip: true });
            series.push({ label: "", data: w.ensemble.p95, color: "transparent", width: 0, fill: "-1", fillColor: rgba(m4, 0.14), noLegend: true, noTooltip: true });
            series.push({ label: "GEFS median", data: w.ensemble.p50, color: m4, dashed: true });
          }
          for (const m of w.models) {
            if (hasData(m)) series.push({ label: m.label, data: m.data, color: cssVar(m.slot, "#888") });
          }
          series.push(...obsSeries(obsWind, `NDBC ${buoy?.id ?? ""} observed`));
          series.push(...fieldTrace("wind"));
          // Prefer the ensemble median for the no-go bands, falling back to the
          // leading deterministic model when there is no ensemble at this point.
          const noGoSource = w.ensemble?.p50 ?? w.models.find(hasData)?.data ?? null;
          // Axis: keep the limit line on screen, but only just.
          //
          // This used to anchor the top at 150% of the limit so the line sat two
          // thirds up. On a calm day against a 22 kn limit that pinned the axis at
          // 35 kn while the models peaked near 12, so the entire model spread — the
          // thing you are here to read — was squeezed into the bottom third of a
          // 76 px track and rendered as a single smear. Headroom is worth far less
          // than resolution: 5% above whichever of the data and the limit is higher
          // still shows how much margin you have, without spending half the chart
          // on empty air.
          let windPeak = 0;
          for (const s of series) {
            for (const v of s.data) {
              if (v != null && Number.isFinite(v) && v > windPeak) windPeak = v;
            }
          }
          const anchor = limitKn !== null ? limitKn * 1.05 : 30;
          const windMax = Math.max(Math.ceil(Math.max(windPeak * 1.1, anchor) / 5) * 5, 5);
          return createChart(c, {
            gutterLeft: STACK_GUTTER,
            xAxis: false, // shared ruler heads the stack
            // …and the readout strip above the plot heads each track: it prints
            // the same swatch and name the legend would, with the value as well.
            // Two rows of the same nine names, one above the other, cost this
            // track 20 of its 118 px — about what the readout itself takes, so
            // dropping the canvas row is how the stack comes out even.
            legend: false,
            series,
            xLabels: fc.times,
            yUnit: "kn",
            yMin: 0,
            yMax: windMax,
            tooltipTitle: tooltipTitle(fc.times),
            tooltipExtra: (i) => {
              const why = fc.squall?.why[i];
              return why != null && why !== "" ? [`⚡ ${why}`] : [];
            },
            marks: [
              // Seasonal normal first: it is context, and it belongs under
              // everything that is a measurement or a threshold.
              ...(curClim !== null
                ? [normalBand(curClim.windP25Kn, curClim.windP75Kn, {
                    label: `${curClim.monthName} normal`,
                  })]
                : []),
              squallWash(fc),
              // No-go bands come off the ensemble median where there is one — a
              // single deterministic run crossing the limit is a weaker signal
              // than the middle of 31 members doing so.
              ...(limitKn !== null && noGoSource !== null
                ? [noGoSpans(noGoSource, limitKn, { unknownIsNoGo: false })]
                : []),
              ...(limitKn !== null ? [limitLine(limitKn, { label: `limit ${limitKn} kn` })] : []),
              nowLine(fc.nowIndex),
              dirArrows(w.directionDeg),
            ],
          });
        });
        // Meter off the ensemble median where there is one -- the same series the
        // no-go bands use, so header and shading can never disagree.
        const windMeter = w.ensemble?.p50 ?? w.models.find(hasData)?.data ?? null;
        if (windMeter !== null) {
          meterUnsubs.push(bindMeter(wind, fc.times, windMeter, "kn", 0, () => work.windLimitKn()));
        }
        wind.src.textContent = sourceLine(
          w.models,
          w.ensemble !== null
            ? "GEFS ensemble — dashed line is the member median, shaded band is the 5–95% member spread (wider = less agreement, less certain)"
            : undefined,
        ) + (fc.squall !== null ? " · squall wash: GFS convective" : "");
      };
      clearMeters();
      setRulerTimes(fc.times);
      redrawWind();

      // squall flag: the next flagged hour, one line; the reasoning rides in the
      // hover title here and per-hour in the chart tooltip
      windSquall.textContent = "";
      windSquall.title = "";
      if (fc.squall !== null) {
        const sq = fc.squall;
        let idx = -1;
        let lvl = 0;
        for (let i = Math.max(0, fc.nowIndex); i < sq.level.length; i++) {
          if (sq.level[i] >= 2) {
            idx = i;
            lvl = sq.level[i];
            break;
          }
        }
        if (idx === -1) {
          for (let i = Math.max(0, fc.nowIndex); i < sq.level.length; i++) {
            if (sq.level[i] >= 1) {
              idx = i;
              lvl = 1;
              break;
            }
          }
        }
        if (idx >= 0) {
          const when = tooltipTitle(fc.times)(idx);
          const head =
            lvl >= 3 ? "Severe squall signal" : lvl === 2 ? "Squalls likely" : "Squalls possible";
          const why = sq.why[idx];
          windSquall.textContent = `⚡ ${head} · ${when}`;
          windSquall.title =
            `${head} ${when}` + (why != null && why !== "" ? ` — ${why}` : "") +
            ". Hover the chart for any hour's reasoning.";
        }
      }

      // waves — like the wind chart, retained as redrawWave() so an operation
      // change (which moves the wave limit) repaints the limit line and the
      // no-go bands in place.
      if (fc.waves !== null) {
        const wv = fc.waves;
        redrawWave = (): void => {
          mountChart(wave, (c) => {
            const ink = cssVar("--ink", "#16232e");
            const series: Series[] = [];
            const env = envelope(wv.height, fc.times.length);
            series.push({ label: "", data: env.lo, color: "transparent", width: 0, noLegend: true, noTooltip: true });
            series.push({ label: "", data: env.hi, color: "transparent", width: 0, fill: "-1", fillColor: rgba(ink, 0.07), noLegend: true, noTooltip: true });
            for (const m of wv.height) {
              if (hasData(m)) series.push({ label: m.label, data: m.data, color: cssVar(m.slot, "#888") });
            }
            series.push(...obsSeries(obsWave, `NDBC ${buoy?.id ?? ""} observed`));
            series.push(...fieldTrace("wave"));
            // The vessel's own limit, not a fixed 1.5 m. The reach field already
            // colours off waveLimitM(), so a hardcoded line here meant the map went
            // red at one height while the chart drew its limit at another.
            const limitM = work.waveLimitM();
            // Shade against the primary model run — the leading height series.
            // Metric on purpose: no-go spans are x-positions from a metric-vs-
            // metric comparison, untouched by the display conversion below.
            const lead = wv.height.find(hasData)?.data ?? null;
            // The chart draws in the DISPLAY unit — data, axis and limit convert
            // together here, so ticks come out as nice feet, not translated
            // half-metres. Everything upstream stays metric.
            const drawn = series.map((s) => ({ ...s, data: convLenSeries(s.data) }));
            const limitDisp = limitM !== null ? convLen(limitM) : null;
            // Same reasoning as the wind axis. Left to the default "nice ceiling"
            // this drew 0-4 m under a 2 m limit with the models sitting below 1 —
            // three quarters of the track empty and every model on top of every
            // other. Fit the data and the limit, then stop.
            let wavePeak = 0;
            for (const s of drawn) {
              for (const v of s.data) {
                if (v != null && Number.isFinite(v) && v > wavePeak) wavePeak = v;
              }
            }
            const waveAnchor = limitDisp !== null ? limitDisp * 1.05 : convLen(2);
            const waveFit = Math.max(wavePeak * 1.1, waveAnchor);
            // Ceiling on a step native to the display unit: half-metres, whole feet.
            const waveMax =
              getUnitSystem() === "imperial"
                ? Math.max(Math.ceil(waveFit), 2)
                : Math.max(Math.ceil(waveFit * 2) / 2, 0.5);
            return createChart(c, {
            gutterLeft: STACK_GUTTER,
            xAxis: false, // shared ruler heads the stack
            legend: false, // the readout strip above prints the same swatch and name
              series: drawn,
              xLabels: fc.times,
              yUnit: lenUnit(),
              yMin: 0,
              yMax: waveMax,
              tooltipTitle: tooltipTitle(fc.times),
              marks: [
                // convLen because this axis may be plotting feet; the
                // climatology is metres, like every other stored length.
                ...(curClim !== null && curClim.waveP25M !== null && curClim.waveP75M !== null
                  ? [normalBand(convLen(curClim.waveP25M), convLen(curClim.waveP75M), {
                      label: `${curClim.monthName} normal`,
                    })]
                  : []),
                ...(limitM !== null && lead !== null
                  ? [noGoSpans(lead, limitM, { unknownIsNoGo: false })]
                  : []),
                ...(limitM !== null && limitDisp !== null
                  ? [limitLine(limitDisp, { label: `limit ${fmtLimitLen(limitM)}` })]
                  : []),
                nowLine(fc.nowIndex),
                dirArrows(wv.directionDeg),
              ],
            });
          });
          const waveMeter = wv.height.find(hasData)?.data ?? null;
          if (waveMeter !== null) {
            meterUnsubs.push(
              bindMeter(
                wave,
                fc.times,
                convLenSeries(waveMeter),
                lenUnit(),
                getUnitSystem() === "imperial" ? 1 : 2,
                () => {
                  const lim = work.waveLimitM();
                  return lim !== null ? convLen(lim) : null;
                },
              ),
            );
          }
          // "model spread", never "ensemble": these are four deterministic
          // runs, and the band is their per-hour min–max, not member quantiles.
          wave.src.textContent =
            sourceLine(
              wv.height,
              "model spread — shaded band is the per-hour min–max across these models (wider = less agreement, less certain)",
            ) + " · arrows: where the waves are running";
        };
        redrawWave();

        // period
        mountChart(period, (c) => {
          const series: Series[] = [];
          for (const m of wv.period) {
            if (hasData(m)) series.push({ label: m.label, data: m.data, color: cssVar(m.slot, "#888") });
          }
          series.push(...obsSeries(obsPeriod, `NDBC ${buoy?.id ?? ""} observed`));
          return createChart(c, {
            gutterLeft: STACK_GUTTER,
            xAxis: false, // shared ruler heads the stack
            legend: false, // the readout strip above prints the same swatch and name
            series,
            xLabels: fc.times,
            yUnit: "s",
            tooltipTitle: tooltipTitle(fc.times),
            marks: [nowLine(fc.nowIndex)],
          });
        });
        const perMeter = wv.period.find(hasData)?.data ?? null;
        if (perMeter !== null) {
          meterUnsubs.push(bindMeter(period, fc.times, perMeter, "s", 1, () => null));
        }
        period.src.textContent = sourceLine(wv.period);
      } else {
        resetBlock(wave, "No wave data at this point (land or unmodeled cell).");
        period.root.style.display = "none";
      }

      // daylight & visibility
      light.sub.textContent = sunLine(solar, fc.times, fc.nowIndex, fc.utcOffsetSeconds);
      mountChart(light, (c) => {
        const series: Series[] = [];
        if (anyData(fc.visibilityM)) {
          const visNm = (fc.visibilityM ?? []).map((v) => (v === null ? null : v / 1852));
          series.push({ label: "Visibility", data: visNm, color: cssVar("--m2", "#2f9e78") });
        }
        return createChart(c, {
            gutterLeft: STACK_GUTTER,
            xAxis: false, // shared ruler heads the stack
            legend: false, // the readout strip above prints the same swatch and name
          series,
          xLabels: fc.times,
          yUnit: "nm",
          yMax: series.length > 0 ? undefined : 10,
          tooltipTitle: tooltipTitle(fc.times),
          marks: [
            nightBands(solar.level),
            ...(series.length > 0 ? [refLine({ value: 3, label: "3 nm transfer min", below: true })] : []),
            nowLine(fc.nowIndex),
          ],
        });
      });
      light.src.textContent =
        "Source: NOAA solar geometry (computed) " +
        (anyData(fc.visibilityM) ? "· GFS visibility" : "· visibility unavailable");

      // sea character
      if (fc.partition !== null && (anyData(fc.partition.swellH) || anyData(fc.partition.windSeaH))) {
        sea.root.style.display = "";
        const p = fc.partition;
        const seaLevels = seaCharacterLevels(fc);
        mountChart(sea, (c) =>
          createChart(c, {
            gutterLeft: STACK_GUTTER,
            xAxis: false, // shared ruler heads the stack
            legend: false, // the readout strip above prints the same swatch and name
            series: [
              { label: "Swell", data: convLenSeries(p.swellH ?? []), color: cssVar("--m1", "#2f6f9f") },
              { label: "Wind sea", data: convLenSeries(p.windSeaH ?? []), color: cssVar("--m3", "#d98a2b") },
            ],
            xLabels: fc.times,
            yUnit: lenUnit(),
            tooltipTitle: tooltipTitle(fc.times),
            tooltipExtra: (i) => {
              const lines: string[] = [];
              const sp = p.swellPer?.[i];
              if (sp != null && Number.isFinite(sp)) lines.push(`swell period ${Math.round(sp)} s`);
              const wd = p.windSeaDir?.[i];
              if (wd != null && Number.isFinite(wd)) lines.push(`wind sea from ${Math.round(wd)}°`);
              if (seaLevels[i] === 2) {
                lines.push("⚠ crossed seas — expect confused, rolly conditions");
              }
              return lines;
            },
            marks: [
              shadeBands(seaLevels, (lvl) =>
                lvl === 2
                  ? rgba(cssVar("--serious", "#d9772b"), 0.2)
                  : lvl === 1
                    ? rgba(cssVar("--m1", "#2f6f9f"), 0.08)
                    : null,
              ),
              nowLine(fc.nowIndex),
              dirArrows(p.swellDir ?? [], { toward: false }),
            ],
          }),
        );
        sea.src.textContent =
          "Source: Open-Meteo best-match partition · arrows: where the swell is running · " +
          "amber wash: crossed seas (swell vs wind sea > 60° apart) · blue wash: wind sea dominates";
      } else {
        sea.chart?.destroy();
        sea.chart = null;
        sea.root.style.display = "none";
      }

      // current — hoisted so the slow drifter fetch can repaint it with observed dots
      // once it lands (see the drifter block in load()). The chart draws from the SMOC
      // model on the first pass and gains the drifter dots on the second, without a
      // refetch of anything. The model + arrows are unchanged; only the observed layer
      // and the source line move between passes.
      redrawCurrent = (): void => {
        if (fc.current === null || !anyData(fc.current.speedKn)) {
          cur.chart?.destroy();
          cur.chart = null;
          cur.root.style.display = "none";
          return;
        }
        cur.root.style.display = "";
        const cu = fc.current;
        // The observed layer: the drifter whose track passed closest to the site (if
        // any came within 30 nm), its DERIVED speed bucketed onto the site-local hour
        // axis exactly as the NDBC obs are. Absence is normal — most sites have none.
        const near = nearestDrifter(curDrifters);
        const drifterObs = near !== null ? bucketDrifterSpeed(near, fc.times, fc.utcOffsetSeconds) : null;
        const drifterSeries =
          near !== null ? obsSeries(drifterObs, `GDP drifter ${near.id} observed`) : [];
        mountChart(cur, (c) =>
          createChart(c, {
            gutterLeft: STACK_GUTTER,
            xAxis: false, // shared ruler heads the stack
            legend: false, // the readout strip above prints the same swatch and name
            series: [
              { label: "Current", data: cu.speedKn, color: cssVar("--m1", "#2f6f9f") },
              ...drifterSeries,
            ],
            xLabels: fc.times,
            yUnit: "kn",
            tooltipTitle: tooltipTitle(fc.times),
            marks: [nowLine(fc.nowIndex), dirArrows(cu.dirDeg, { toward: true })],
          }),
        );
        cur.src.textContent =
          "Source: Météo-France SMOC · arrows: set (toward)" +
          (drifterSeries.length > 0
            ? ` · dots: NOAA Global Drifter Program ${near?.id} observed ` +
              "(drogue 15 m; current derived from position track)"
            : "");
      };
      redrawCurrent();
      // The header meter is bound ONCE (not inside redrawCurrent, which the drifter
      // fetch re-invokes) so a late drifter arrival cannot stack duplicate cursor
      // subscriptions on the track.
      const curMeter = fc.current !== null ? fc.current.speedKn : null;
      if (curMeter !== null) {
        meterUnsubs.push(bindMeter(cur, fc.times, curMeter, "kn", 2, () => null));
      }

      // forecast vs buoy — the last ~48 h where observations exist
      if (buoy !== undefined && obs !== null && !dry && fc.wind !== null && fc.waves !== null) {
        const from = Math.max(0, fc.nowIndex - 48);
        const to = fc.nowIndex;
        const cut = (a: (number | null)[]): (number | null)[] => a.slice(from, to + 1);
        const pastTimes = fc.times.slice(from, to + 1);
        const wModels = fc.wind.models;
        const vModels = fc.waves.height;

        const buildVal = (
          wrap: HTMLDivElement,
          models: typeof wModels,
          obsData: (number | null)[] | null,
          unit: string,
          conv: (a: (number | null)[]) => (number | null)[] = (a) => a,
        ): Chart | null => {
          const series: Series[] = [];
          for (const mm of models) {
            if (hasData(mm)) series.push({ label: mm.label, data: conv(cut(mm.data)), color: cssVar(mm.slot, "#888") });
          }
          if (obsData !== null) {
            const sliced = conv(cut(obsData));
            if (sliced.some((vv) => vv !== null)) {
              series.push({ label: "observed", data: sliced, color: obsColor, points: true, order: 50 });
            }
          }
          const c = document.createElement("canvas");
          wrap.replaceChildren(c);
          return createChart(c, {
            gutterLeft: STACK_GUTTER,
            // Legend ON, unlike the tracks above. This pair is mounted straight
            // into a wrap rather than through mountChart, so it has no readout
            // strip to name its lines — and naming four models by colour alone
            // is the thing docs/CONVENTIONS.md rules out.
            series,
            xLabels: pastTimes,
            yUnit: unit,
            tooltipTitle: tooltipTitle(pastTimes),
          });
        };

        valWindChart = buildVal(valWindWrap, wModels, obsWind, "kn");
        valWaveChart = buildVal(valWaveWrap, vModels, obsWave, lenUnit(), convLenSeries);

        const windBias = obsWind !== null ? meanBias(wModels.map((mm) => mm.data), obsWind, from, to) : null;
        const waveBias = obsWave !== null ? meanBias(vModels.map((mm) => mm.data), obsWave, from, to) : null;
        const fmtBias = (v: number | null, unit: string): string =>
          v === null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)} ${unit}`;
        const src = buoySource(buoy.source);
        const label = src?.pickLabel(buoy) ?? buoy.id;
        const stationUrl = src?.stationUrl(buoy.id) ?? null;
        if (stationUrl !== null) {
          valLink.href = stationUrl;
          valLink.style.display = "";
        } else {
          valLink.style.display = "none";
        }
        valSub.textContent = `${label} · last ${to - from} h · dots: observed`;
        valSrc.textContent =
          `Mean model − buoy over shown window: wind ${fmtBias(windBias, "kn")} · ` +
          `waves ${fmtBias(waveBias !== null ? convLen(waveBias) : null, lenUnit())} · ` +
          `Source: ${src?.attribution ?? "buoy"} · ${label}`;
        val.style.display = "";
      }

      work.render(fc);
      redrawWorkTrack();
      // In meterUnsubs with the other track meters: what it reads depends on this
      // forecast, so it must be torn down and rebound when the point changes. (The
      // ribbon canvas keeps its own permanent cursor subscription — it only ever
      // draws whatever it was last handed, like the ruler.)
      meterUnsubs.push(subscribeCursor(paintWorkMeter));
      // The verdict now spans the work window, so a change to the window's LENGTH
      // moves it just as a scrub does — without this, dragging Length re-drew the
      // ruler's span while the header kept answering for the old one.
      meterUnsubs.push(subscribeWindow(paintWorkMeter));
      lastFcHeld = fc;
      lastSolarHeld = solar;
      lastTropHeld = tropicalHtml(tropicalAt(lat, lon));
      brief.render(fc, solar, lastTropHeld);
      // After work.render(fc), so the table's Workability tab starts with this
      // point's hours rather than waiting for the next vessel change.
      conditions.update({
        fc,
        solar,
        vessel: selectedVessel,
        limit: work.effective(),
        opLabel: work.selectedOperation(),
        workP: work.hourly()?.p ?? null,
        place: buoy !== undefined ? buoy.name : fmtCoord(lat, lon),
      });
      pointSocks?.update({ fc, limit: work.effective() });
      renderOpsPanels();
    } catch {
      if (!live()) return;
      resetBlock(wind, "Could not load the forecast.");
      resetBlock(wave, "");
      freshness.textContent = "Forecast could not be retrieved — re-click to retry.";
    }
  }

  // base map click debounces briefly so a buoy click (which fires right after)
  // can supersede it with the buoy's own coordinates, and so a port/fleet
  // click can cancel it entirely (info, not a new forecast point).
  let pendingBase: number | null = null;
  const pick = (lat: number, lon: number): void => {
    if (pendingBase !== null) window.clearTimeout(pendingBase);
    pendingBase = window.setTimeout(() => {
      pendingBase = null;
      void load(lat, lon);
    }, 90);
  };
  setMapClickInterceptor(() => {
    if (pendingBase !== null) {
      window.clearTimeout(pendingBase);
      pendingBase = null;
    }
  });

  // Opening view, by precedence: a deep link beats the user's home beats the
  // default. The deep link (/console?lat=..&lon=..[&buoy=..[&name=..]]) is a
  // shared pointer to a specific place and must win, or a shared link would
  // open somewhere else for anyone with a home set. The HOME comes from the
  // user's server-side prefs (prefs.home = {lat, lon, buoy?}), so an account
  // provisioned for one operating area — a client whose whole world is one
  // installation — opens on their water, on any machine, with their buoy
  // already selected. DEFAULT_POINT is the Gulf, and is now only the view for
  // an account with no home and no link.
  const params = new URLSearchParams(window.location.search);
  const qLat = Number(params.get("lat"));
  const qLon = Number(params.get("lon"));
  const hasQ = params.get("lat") !== null && Number.isFinite(qLat) && Number.isFinite(qLon);
  const home = homeFromPrefs(state.user?.prefs);
  const startLat = hasQ ? qLat : home?.lat ?? DEFAULT_POINT[0];
  const startLon = hasQ ? qLon : home?.lon ?? DEFAULT_POINT[1];
  const qBuoyId = params.get("buoy");
  const startBuoy: BuoyPick | undefined =
    hasQ && qBuoyId !== null
      ? {
          source: params.get("src") ?? "ndbc",
          id: qBuoyId,
          name: params.get("name") ?? qBuoyId,
          lat: qLat,
          lon: qLon,
        }
      : !hasQ && home?.buoy !== undefined
        ? { source: "ndbc", id: home.buoy, name: home.buoy, lat: home.lat, lon: home.lon }
        : undefined;

  activeMap?.destroy();
  activeMap = createMap(mapContainer, {
    center: [startLon, startLat],
    zoom: 5,
    onPick: pick,
    // Dragging moves the site INSIDE the framed. The drop is clamped to the
    // frame rather than being allowed to wander out of it: leaving the operating
    // area is a deliberate act (click outside), not something you do by
    // overshooting a drag.
    onDrag: (lat, lon, settled) => {
      if (!settled) {
        header.coord.innerHTML = `<span class="k">PT</span> ${fmtCoord(lat, lon)}`;
        return;
      }
      let at = { lat, lon };
      if (framed !== null) {
        at = clampToBounds(boundsFor(framed.lat, framed.lon, framed.spanNm, THEATER_ASPECT), lat, lon);
        if (at.lat !== lat || at.lon !== lon) activeMap?.setPoint(at.lat, at.lon);
      }
      void load(at.lat, at.lon);
    },
  });

  // The brightness dimmer, in the map's own corner rather than only behind the
  // gear. Someone whose eyes are adapting on a dark bridge should be able to
  // take the light down in one gesture, not by opening a panel and reading a
  // list of labels. It mounts INSIDE the map container but outside the two
  // MapLibre containers the filter lands on, which is what keeps it lit — and
  // therefore usable — at the very bottom of its own range.
  mapDimmer = createMapDimmer(mapContainer);
  mapContainer.appendChild(mapDimmer.root);
  mapDimmer.setTheme(resolvedTheme());

  // Search, top-left, where a map's search field always is. It goes through the
  // SAME path as the waypoint lists — a search result is a waypoint you had not
  // saved — so there is one function in this file that moves the view and one
  // definition of what "go there" means.
  //
  // A built-in region carries its own width and reframes to it; anything else is
  // a position and keeps the width you were using. That is goToWaypoint's rule
  // already, and it is why a port does not throw the view out to 600 nm.
  activeSearch?.destroy();
  activeSearch = createSearchBox({
    here: () => (curLat === null || curLon === null ? null : { lat: curLat, lon: curLon }),
    onPick: (p) => {
      goToWaypoint({
        id: `search:${p.kind}:${p.name}`,
        name: p.name,
        note: p.sub,
        lat: p.lat,
        lon: p.lon,
        ...(p.spanNm === undefined ? {} : { spanNm: p.spanNm }),
      });
    },
  });
  mapContainer.appendChild(activeSearch.root);

  // The flags ride the site marker itself, so they need no projection here and
  // survive pan, zoom and a marker drag. Centred on it, because the dot they fly
  // from IS the point — the canvas is pointer-transparent, so the water under it
  // is still the water you click to move the site.
  pointSocks?.destroy();
  pointSocks = createPointSocks();
  activeMap.attachAtPoint(pointSocks.root, [0, 0]);

  const m = activeMap;
  fieldMap = m;
  m.onReady(() => {
    const raw = m.raw();
    void setupBuoys(raw, (b) => {
      if (pendingBase !== null) window.clearTimeout(pendingBase);
      pendingBase = null;
      m.setPoint(b.lat, b.lon);
      void load(b.lat, b.lon, b);
    });
    void setupTropical(raw).then((r) => {
      // setupTropical catches internally: a null badge means the NHC layers
      // failed; jtwcOk covers the aggregate feed behind the non-NHC cards.
      reportSystem("tropical", r.badge !== null);
      reportSystem("jtwc", r.jtwcOk);
      reportSystem("jtwcwarn", r.jtwcWarnOk);
      if (r.badge !== null) {
        // The corner badge is also the door to the storm list.
        r.badge.title = "Open the storm list";
        r.badge.addEventListener("click", () => rail.select("tropical"));
      }
      syncTropical();
    });
    // ONE layer surface. The overlay list moves off the map into the Observations
    // workspace, and the reach field's Wind/Waves toggles join it at the top —
    // they were the second, competing layer control. What stays on the map is the
    // field's scrubber and its limit-anchored legend: time and colour key, both
    // of which are read against the field itself.
    const layerCtl = createLayerControl(raw, {
      mount: layersHost,
      onRange: (on) => {
        rangeOn = on;
        updateRange();
      },
    });
    const lcBody = layerCtl.querySelector<HTMLElement>(".lc-body");
    windControl = createWindControl(mapContainer, {
      toggleMount: lcBody ?? undefined,
      // Toggling the field off must take the reach veil with it — the veil dims
      // the field, and a dimmed bare basemap reads as a rendering bug.
      onLayerChange: (l) => {
        updateReachVeil();
        syncLayerStripe(l);
      },
    });
    syncLayerStripe(windControl.kind());
    // The field is the layer this console is about — it leads the list.
    if (lcBody !== null) lcBody.prepend(windControl.toggles);
    mapReady = true;
    // The field follows the VIEW, so the view is what asks for it. Every path that
    // moves the map — a preset, the picker, a wheel, a drag — ends in moveend, so this
    // one subscription covers them all and none of them has to remember to refetch.
    raw.on("moveend", scheduleWindowMesh);
    updateRange(); // draw rings for the initial vessel + point now the map is ready
    ensureWindowMesh(); // the opening view, undebounced — nothing has moved yet
    syncWindField(); // hand the field control its first context
    applyMapTheme(resolvedTheme()); // dim the basemap if a dark table is active
  });

  // Let a Day/Dusk/Night change repaint this console's canvases (charts read the
  // palette vars at draw time). Reloading the current point is the simplest repaint.
  activeThemeRepaint = () => {
    if (curLat !== null && curLon !== null) void load(curLat, curLon, lastBuoy);
  };

  // A Local/UTC flip re-renders the textual time surfaces from held state — no
  // refetch: the data didn't change, only the clock it is read in.
  activeClockRepaint = () => {
    if (freshAt !== null) {
      freshness.textContent = `Forecast retrieved ${fmtClockHM(freshAt)} · re-click any point to refresh`;
    }
    if (lastFcHeld !== null) {
      work.render(lastFcHeld); // window chips
      redrawWorkTrack(); // the meter's "worst Thu 14:00"
      brief.render(lastFcHeld, lastSolarHeld, lastTropHeld); // Start readout + prose
    }
    repaintFeedback();
  };

  // A Metric/Imperial flip converts a number on nearly every surface — chart
  // axes and tooltips, meters, the briefing, the seabed chip — so reloading the
  // current point is the honest repaint, exactly as a theme change does. The
  // subtitles and roster are text the load path never rewrites, so they are
  // brought along by hand.
  activeUnitsRepaint = () => {
    wave.sub.textContent = `${lenWord()} · 7-day`;
    sea.sub.textContent = `swell vs wind sea · ${lenWord()}`;
    refreshFleetRoster();
    if (curLat !== null && curLon !== null) void load(curLat, curLon, lastBuoy);
  };

  // Typed coordinates land on the SAME path a map click takes — load() — plus
  // the camera move a click never needs (the click was already in view).
  coordEntry = (lat, lon) => {
    m.setPoint(lat, lon);
    m.raw().flyTo({ center: [lon, lat] });
    void load(lat, lon);
  };

  void load(startLat, startLon, startBuoy);
}

// --- render -----------------------------------------------------------------------------------

function render(): void {
  const root = document.getElementById("app");
  if (root === null) return;
  applyClientBrand(resolvedTheme()); // login/logout changes who the brand is
  activeMap?.destroy();
  activeMap = null;
  unmountReport(); // report charts hold cursor/theme subscriptions; drop them
  activeThemeRepaint = null; // rebuilt by renderConsole when the console mounts
  activeClockRepaint = null;
  activeUnitsRepaint = null;
  coordEntry = null;
  activeSearch?.destroy();
  activeSearch = null;
  root.replaceChildren();
  // The phone tab bar belongs to the console alone, and its class gives #app a
  // definite height. Leaving it set would clip the report and the site pages,
  // which scroll — replaceChildren() clears the children but not the class.
  root.classList.remove("mtabbed");

  const route = currentRoute();

  // public site pages — their own quiet chrome, not the app header
  if (route === "landing" || route === "about" || route === "case-study") {
    const opts = { authed: state.user !== null, navigate };
    if (route === "landing") mountLanding(root, opts);
    else if (route === "about") mountAbout(root, opts);
    else mountCaseStudy(root, opts);
    return;
  }

  // The console is client-only: it renders a specific operator's fleet, vessels
  // and workability, so it lives entirely behind the login. A logged-out visit
  // is sent to sign-in rather than shown a public/fallback view.
  if (route === "console" && state.user === null) {
    navigate("/login");
    return;
  }

  // /admin IS NOT A PAGE unless you are one of ours. Not "forbidden" — not a
  // page: the address behaves exactly as any unrecognised one does, and lands on
  // the site's front door.
  //
  // The first cut mounted the frame for ANY signed-in account and let the data
  // 404 behind it, reasoning that the server is the real gate. The server is;
  // that was never the problem. A client account typing the URL got our heading,
  // our lede — "SeaState's own instruments" — and the names of all six sections,
  // which is a description of how we run the business, handed to the business we
  // run it for. Empty panels do not make that private.
  //
  // Checked before the header is built so nothing of the area is ever in the
  // document, and it cannot flash: boot renders only after /api/me has answered.
  if (route === "admin" && state.user?.staff !== true) {
    window.history.replaceState(null, "", "/");
    render(); // terminates: the route is now "landing"
    return;
  }

  const header = buildHeader(root, route);

  // Help pages are public (no login gate): an operator — or a prospective one —
  // can read how the data is made and what it can't do before ever signing in.
  // SeaState's own instruments. Unreachable above unless staff; `staff` is
  // passed anyway so the frame carries its own condition rather than trusting
  // that every future caller remembers the one above.
  if (route === "admin") {
    const doc = el("main", "page helppage");
    root.appendChild(doc);
    mountAdmin(doc, { authed: state.user !== null, staff: state.user?.staff === true, navigate });
    return;
  }

  // The fleet's surfaces are not the thermal console's. A thermal account that
  // reaches one by URL — a bookmark, a guess, a link in an old message — gets
  // its own console back, not another product's pages. The Docs menu already
  // offers none of these to it; this closes the address bar.
  if (state.user?.console === "thermal" && FLEET_ONLY_ROUTES.includes(route)) {
    navigate("/console");
    return;
  }

  if (route === "updates") {
    const doc = el("main", "page helppage");
    root.appendChild(doc);
    document.title = "Updates — SeaState";
    mountUpdates(doc, { authed: state.user !== null, navigate });
    return;
  }

  if (route === "guide" || route === "methods") {
    // `helppage` scopes the Guide/Methods `.doc` styles so they don't collide with
    // the site pages' own `.doc` grid layout (About/case-study use aside+article).
    const doc = el("main", "page helppage");
    root.appendChild(doc);
    const opts = { authed: state.user !== null, navigate };
    if (route === "guide") {
      document.title = "Guide — SeaState";
      mountGuide(doc, opts);
    } else {
      document.title = "Methods & Limitations — SeaState";
      mountMethods(doc, opts);
    }
    return;
  }

  if (route === "console") {
    document.title = "Console — SeaState";
    // Which console this client gets, decided server-side and carried on the
    // identity (clients.console). The fleet console below is the one this
    // product was built for; a client with no vessels gets a different one
    // rather than the same one with its centre cut out.
    if (state.user?.console === "thermal") {
      mountThermalConsole(root);
      return;
    }
    renderConsole(root, header);
    // Paint from what we have, then confirm against the server. A limit edited on
    // the Vessels page a moment ago must not need a page reload to take effect,
    // and re-rendering only on an actual change keeps ordinary navigation free.
    if (state.user !== null) {
      void refreshRegistry().then((changed) => {
        if (changed && currentRoute() === "console") render();
      });
    }
    return;
  }

  const page = el("main", "page");
  root.appendChild(page);

  if (route === "login") {
    document.title = "Client login — SeaState";
    if (state.user !== null) {
      navigate("/console");
      return;
    }
    mountLogin(page, () => {
      void refreshAuth().then(() => navigate("/console"));
    });
    return;
  }

  // Claiming an account. Deliberately NOT behind the login gate and not
  // redirected away when a session already exists: someone may be setting up a
  // second account on a machine already signed in as the first, and bouncing
  // them to the console would strand a single-use link they cannot re-open.
  if (route === "set-password") {
    document.title = "Set your password — SeaState";
    mountSetPassword(page, (user) => {
      // The server already opened the session; adopt it rather than re-fetching.
      state.user = user;
      navigate(user.staff === true ? "/admin" : "/console");
    });
    return;
  }

  if (route === "report") {
    // Client-only, like the console: the report is built from a specific
    // client's registry and carries their name, so it lives behind the login.
    document.title = "Report — SeaState";
    if (state.user === null) {
      navigate("/login");
      return;
    }
    page.classList.add("reportpage");
    // The "From current view" snapshot: what the console is showing right now.
    // Position is the last point load()ed (null until the console has mounted
    // this session — the page disables the button and says why); vessel and
    // operation mirror the Workability panel's selection; the window is the
    // timeline's playhead + work-window length when a window is set.
    const cursor = getCursor();
    mountReport(page, {
      registry: state.registry,
      clientName: state.user.client,
      prefill: {
        position: lastPickedPoint,
        vesselId: lastWorkSelection?.vesselId ?? null,
        opId: lastWorkSelection?.opId ?? null,
        windowStartMs: cursor,
        windowHours: cursor !== null ? getWindowHours() : null,
      },
    });
    return;
  }

  // vessels
  document.title = "Vessels — SeaState";
  if (state.user === null) {
    navigate("/login");
    return;
  }
  mountVessels(page, {
    onUnauthorized: () => {
      state.user = null;
      navigate("/login");
    },
  });
}

window.addEventListener("popstate", render);

initTheme();
initHelp(navigate);
// One app-lifetime subscription drives whichever console is mounted; the slot
// is nulled off-console, so nothing fires into a torn-down page.
subscribeTimeMode(() => activeClockRepaint?.());
subscribeUnits(() => activeUnitsRepaint?.());
// A position spelling changes text and nothing else — no threshold, no fetch,
// no geometry — so it rides the units repaint rather than earning a third slot.
subscribePosition(() => activeUnitsRepaint?.());
void refreshAuth().then(render);
